import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Title, Tabs, useToast, Button, Selectbox, Message, Spinner, NumberInput, ColorPicker, Box, Tooltip, InfoIcon, Table } from './bpm';
import { useAuth } from '../contexts/AuthContext';
import { useOliverName } from '../contexts/OliverContext';
import { useCanEdit } from '../hooks/useCanEdit';
import { usePortfolio } from '../contexts/PortfolioContextIndex';
import { twsAPI, organizationsAPI, subscriptionsAPI, categoriesAPI, settingsAPI, ibGatewayConfigAPI, healthAPI, portfolioAPI, adminAPI, xFeedAPI, authAPI, getPerformanceLog, clearPerformanceLog } from '../services/api';
import { getPageIcon } from '../utils/pageIcons';
import appConfig from '../config/app.config';
import './Parametres.css';

const Parametres = ({ defaultTab = 0, onLogoClick = null }) => {

  const [activeTab, setActiveTab] = useState(defaultTab);
  
  // Mettre à jour activeTab si defaultTab change (par exemple, lors d'un clic sur l'avatar)
  useEffect(() => {
    if (defaultTab !== undefined && defaultTab !== null) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);
  const { profile, logout, sessionTimeoutMinutes, setSessionTimeoutMinutes, refreshAvatar, setAvatarToNull } = useAuth();
  const { canEdit } = useCanEdit();
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionTimeoutInput, setSessionTimeoutInput] = useState(String(sessionTimeoutMinutes || 30));
  const { showToast } = useToast();
  const loadSettingsLastErrorTime = useRef(0); // Pour limiter les logs répétés
  
  // États pour les nouveaux paramètres de configuration
  // Utiliser des strings pour permettre la saisie libre, convertir en nombre au blur
  const [fiscalYearEndDay, setFiscalYearEndDay] = useState('31');
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState('12');
  const [taxRate, setTaxRate] = useState('25.0');
  const [referenceCurrency, setReferenceCurrency] = useState('EUR');
  const [loadingSettings, setLoadingSettings] = useState(false);
  
  // États pour les clés API IA
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [grokApiKey, setGrokApiKey] = useState('');
  const [chatgptApiKey, setChatgptApiKey] = useState('');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  /** IA par défaut pour Oliver (claude | openai | gemini | grok) */
  const [oliverDefaultProvider, setOliverDefaultProvider] = useState('claude');
  
  // État pour l'avatar de l'utilisateur connecté
  const [userAvatar, setUserAvatar] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [myPassword, setMyPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  
  // États pour les préférences d'apparence
  const [sidebarDefaultOpen, setSidebarDefaultOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarDefaultOpen');
      return saved !== null ? saved === 'true' : window.innerWidth >= 640;
    } catch (e) {
      return window.innerWidth >= 640;
    }
  });
  const [darkTheme, setDarkTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('darkTheme');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });
  const [darkThemeAuto, setDarkThemeAuto] = useState(() => {
    try {
      const saved = localStorage.getItem('darkThemeAuto');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });
  const [accentColor, setAccentColor] = useState(() => {
    try {
      const saved = localStorage.getItem('accentColor');
      return (saved && /^#[0-9A-Fa-f]{6}$/.test(saved)) ? saved : '#1A4B8F';
    } catch (e) {
      return '#1A4B8F';
    }
  });
  const [sidebarLogo, setSidebarLogo] = useState(() => {
    try {
      return localStorage.getItem('sidebarLogo') || null;
    } catch (e) {
      return null;
    }
  });
  const [notificationMinLevel, setNotificationMinLevel] = useState(() => {
    try {
      const saved = localStorage.getItem('notification_min_level');
      const val = saved ? parseInt(saved, 10) : 3;
      return [1, 2, 3].includes(val) ? val : 3;
    } catch (e) {
      return 3;
    }
  });
  /** Ids des icônes à afficher dans la barre mobile (null = toutes). Stocké en JSON. */
  const [sidebarMobileItemIds, setSidebarMobileItemIds] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_mobile_items');
      if (!saved) return null;
      const arr = JSON.parse(saved);
      return Array.isArray(arr) ? arr : null;
    } catch (e) {
      return null;
    }
  });
  /** Ids des icônes à afficher dans la sidebar desktop ouverte/fermée (null = toutes). Stocké en JSON. */
  const [sidebarDesktopItemIds, setSidebarDesktopItemIds] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_desktop_items');
      if (!saved) return null;
      const arr = JSON.parse(saved);
      return Array.isArray(arr) ? arr : null;
    } catch (e) {
      return null;
    }
  });

  // États pour les clés API financières
  const [yahooFinanceApiKey, setYahooFinanceApiKey] = useState('');
  const [financialModelingPrepApiKey, setFinancialModelingPrepApiKey] = useState('');
  const [alphaVantageApiKey, setAlphaVantageApiKey] = useState('');
  const [ibkrApiKey, setIbkrApiKey] = useState('');
  const [xApiKey, setXApiKey] = useState('');
  
  // Oliver : prompt de base pour pré-conditionner l'assistant
  const [oliverBasePrompt, setOliverBasePrompt] = useState('');
  const [savingOliverPrompt, setSavingOliverPrompt] = useState(false);
  const DEFAULT_OLIVER_PROMPT = `Tu es un analyste financier. Tu ne dois utiliser que les données issues de l'application (portefeuille, actifs, compte) pour répondre. Réponds de façon concise et structurée.`;
  // Oliver : nom d'affichage (partout dans l'app)
  const { oliverName, setOliverName } = useOliverName();
  const [oliverDisplayName, setOliverDisplayName] = useState(oliverName);
  const [savingOliverDisplayName, setSavingOliverDisplayName] = useState(false);
  useEffect(() => { setOliverDisplayName(oliverName); }, [oliverName]);
  
  // États pour la configuration IB Gateway
  const [ibGatewayConfig, setIbGatewayConfig] = useState({
    host: '127.0.0.1',
    port: 4002,
    username: '',
    password: '',
    client_id: 1,
    is_active: true,
    has_password: false  // Indique si un mot de passe est sauvegardé
  });
  const [loadingIbGatewayConfig, setLoadingIbGatewayConfig] = useState(false);
  const [savingIbGatewayConfig, setSavingIbGatewayConfig] = useState(false);
  const ibGatewayConfigLoadedRef = useRef(false);
  
  // État pour le statut du backend
  const [backendStatus, setBackendStatus] = useState({
    connected: false,
    loading: false,
    lastCheck: null,
    error: null,
    health: null
  });

  // État pour le statut IB Gateway
  const [ibGatewayStatus, setIbGatewayStatus] = useState({
    connected: false,
    loading: false,
    lastCheck: null,
    error: null,
    status: null,
    lastError: null  // Dernière erreur de connexion (backend) pour affichage dans la carte
  });

  // État pour le statut du service de sauvegarde automatique
  const [dailyValuationStatus, setDailyValuationStatus] = useState({
    connected: false,
    loading: false,
    lastCheck: null,
    error: null,
    lastValuation: null
  });

  // État pour le statut du service keep-alive IBKR
  const [keepAliveStatus, setKeepAliveStatus] = useState({
    running: false,
    loading: false,
    lastCheck: null,
    error: null,
    stats: null
  });

  // État pour la modal diagnostic TWS (qui écoute sur le port, recommandations)
  const [twsDiagnosticModal, setTwsDiagnosticModal] = useState({
    show: false,
    loading: false,
    error: null,
    data: null
  });

  // État pour l'onglet Log (super admin)
  const [logsState, setLogsState] = useState({
    service: 'backend',
    lines: 200,
    content: '',
    loading: false,
    error: null
  });
  const [perfLogRefresh, setPerfLogRefresh] = useState(0); // pour forcer le rafraîchissement

  const loadLogs = useCallback(async () => {
    if (logsState.service === 'performance-api') {
      const entries = getPerformanceLog();
      const apiEntries = entries.filter(e => e.type === 'api');
      const appEntries = entries.filter(e => e.type && e.type !== 'api');
      const total = entries.length;
      const avg = total ? Math.round(entries.reduce((s, e) => s + (e.durationMs || 0), 0) / total) : 0;
      const slow = entries.filter(e => (e.durationMs || 0) >= 500).length;
      const formatTsParis = (isoStr) => {
        if (isoStr == null || isoStr === '') return '--:--:--';
        try {
          const d = new Date(isoStr);
          if (Number.isNaN(d.getTime())) return '--:--:--';
          const s = d.toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          // Éviter d'afficher "Invalid Date" (ou variante traduite) si le navigateur le renvoie
          if (typeof s !== 'string' || !/^\d{1,2}:\d{2}:\d{2}/.test(s.trim())) return '--:--:--';
          return s;
        } catch {
          return '--:--:--';
        }
      };
      const lines = entries.map((e) => {
        const ts = formatTsParis(e.ts);
        const dur = e.durationMs != null ? e.durationMs : 0;
        if (e.type === 'api') {
          return `[${ts}] API ${e.method || '?'} ${e.url || '?'} — ${dur} ms${e.serverTimeMs != null ? ` (serveur: ${e.serverTimeMs} ms)` : ''} — ${e.status || ''}`;
        }
        const kind = e.type === 'page' ? 'PAGE' : e.type === 'load' ? 'LOAD' : e.type === 'calc' ? 'CALC' : 'APP';
        const label = e.label || '?';
        const detail = e.detail ? ` — ${e.detail}` : '';
        return `[${ts}] ${kind} ${label} — ${dur} ms${detail}`;
      });
      const summary = `API: ${apiEntries.length} | App: ${appEntries.length} | Total: ${total} | Moyenne: ${avg} ms | Lentes (>500 ms): ${slow}`;
      const content = lines.length ? `${summary}\n\n${lines.join('\n')}` : 'Aucune donnée enregistrée. Naviguez dans l\'application pour générer du trafic.';
      setLogsState(prev => ({ ...prev, content, loading: false, error: null }));
      setPerfLogRefresh(n => n + 1);
      return;
    }
    setLogsState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await adminAPI.getLogs(logsState.service, logsState.lines);
      const data = response?.data;
      setLogsState(prev => ({
        ...prev,
        content: data?.logs || '(Aucun log)',
        loading: false,
        error: data?.error || null
      }));
    } catch (err) {
      setLogsState(prev => ({
        ...prev,
        content: '',
        loading: false,
        error: err?.response?.data?.detail || err?.message || 'Erreur lors du chargement des logs'
      }));
    }
  }, [logsState.service, logsState.lines]);

  // Auto-chargement des logs Performance quand on sélectionne ce service
  useEffect(() => {
    if (logsState.service === 'performance-api') {
      loadLogs();
    }
  }, [logsState.service]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyLogsToClipboard = useCallback(async () => {
    const text = logsState.content || '';
    if (!text || logsState.loading) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast('Logs copiés dans le presse-papiers', 'success', 2000, 'Copier', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      showToast('Impossible de copier les logs', 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    }
  }, [logsState.content, logsState.loading, showToast]);

  // Liste des devises disponibles
  const currencies = [
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'USD', label: 'USD - Dollar américain' },
    { value: 'GBP', label: 'GBP - Livre sterling' },
    { value: 'CHF', label: 'CHF - Franc suisse' },
    { value: 'JPY', label: 'JPY - Yen japonais' },
    { value: 'CAD', label: 'CAD - Dollar canadien' },
    { value: 'AUD', label: 'AUD - Dollar australien' },
    { value: 'CNY', label: 'CNY - Yuan chinois' },
  ];
  
  // Synchroniser l'état local avec la valeur externe quand elle change
  // MAIS seulement si la valeur a vraiment changé (pour éviter de réinitialiser pendant la saisie)
  useEffect(() => {
    if (sessionTimeoutMinutes !== undefined && sessionTimeoutMinutes !== null) {
      const newValue = String(sessionTimeoutMinutes);
      // Ne mettre à jour que si la valeur est différente de celle actuellement affichée
      // Cela évite de réinitialiser pendant que l'utilisateur tape
      setSessionTimeoutInput(prev => {
        const prevNum = parseFloat(prev) || 0;
        const newNum = sessionTimeoutMinutes;
        // Si les valeurs numériques sont différentes, mettre à jour
        if (Math.abs(prevNum - newNum) > 0.01) {
          return newValue;
        }
        return prev;
      });
    }
  }, [sessionTimeoutMinutes]);
  
  // REFS SIMPLES - uniquement pour éviter les doubles appels
  const orgsLoadedRef = useRef(false);
  const subsLoadedRef = useRef(false);
  const catsLoadedRef = useRef(false);
  const isLoadingOrgsRef = useRef(false);
  const isLoadingSubsRef = useRef(false);
  
  // REFS pour accéder aux valeurs actuelles (pour le diagnostic)
  const organizationsRef = useRef([]);
  const subscriptionsRef = useRef([]);
  const membersRef = useRef([]);
  const categoriesRef = useRef([]);
  
  // États pour les sociétés - SIMPLIFIÉ
  // Initialiser depuis sessionStorage si disponible (pour éviter la perte lors des remontages)
  const [organizations, setOrganizations] = useState(() => {
    try {
      const savedOrgs = sessionStorage.getItem('parametres_organizations');
      const savedOrgsTimestamp = sessionStorage.getItem('parametres_organizations_timestamp');
      if (savedOrgs && savedOrgsTimestamp) {
        const age = Date.now() - parseInt(savedOrgsTimestamp, 10);
        if (age < 5 * 60 * 1000) { // Moins de 5 minutes
          const orgs = JSON.parse(savedOrgs);
          organizationsRef.current = orgs;
          orgsLoadedRef.current = true;
          return orgs;
        }
      }
    } catch (e) {
      console.warn('⚠️ Erreur lors de l\'initialisation depuis sessionStorage:', e);
    }
    return [];
  });
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(null);
  const [togglingOrg, setTogglingOrg] = useState(null);
  
  // États pour les abonnements - SIMPLIFIÉ
  // Initialiser depuis sessionStorage si disponible (pour éviter la perte lors des remontages)
  const [subscriptions, setSubscriptions] = useState(() => {
    try {
      const savedSubs = sessionStorage.getItem('parametres_subscriptions');
      const savedSubsTimestamp = sessionStorage.getItem('parametres_subscriptions_timestamp');
      if (savedSubs && savedSubsTimestamp) {
        const age = Date.now() - parseInt(savedSubsTimestamp, 10);
        if (age < 5 * 60 * 1000) { // Moins de 5 minutes
          const subs = JSON.parse(savedSubs);
          subscriptionsRef.current = subs;
          subsLoadedRef.current = true;
          return subs;
        }
      }
    } catch (e) {
      console.warn('⚠️ Erreur lors de l\'initialisation depuis sessionStorage:', e);
    }
    return [];
  });
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [deletingSubscription, setDeletingSubscription] = useState(null);
  const [togglingSubscription, setTogglingSubscription] = useState(null);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    code: '',
    name: '',
    description: '',
    price: 0,
    currency: 'EUR',
    is_active: true
  });
  
  // État pour les erreurs de validation des formulaires
  const [formErrors, setFormErrors] = useState({});
  const [memberFormErrors, setMemberFormErrors] = useState({});
  
  // États pour les catégories - Initialiser depuis sessionStorage si disponible
  const [categories, setCategories] = useState(() => {
    try {
      const savedCats = sessionStorage.getItem('parametres_categories');
      const savedCatsTimestamp = sessionStorage.getItem('parametres_categories_timestamp');
      if (savedCats && savedCatsTimestamp) {
        const age = Date.now() - parseInt(savedCatsTimestamp, 10);
        if (age < 5 * 60 * 1000) { // Moins de 5 minutes
          const cats = JSON.parse(savedCats);
          categoriesRef.current = cats;
          catsLoadedRef.current = true;
          return cats;
        }
      }
    } catch (e) {
      console.warn('⚠️ Erreur lors de l\'initialisation des catégories depuis sessionStorage:', e);
    }
    return [];
  });
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [togglingCategory, setTogglingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    is_fragile: false,
    target_percentage: null,
    color: '#000000'
  });
  const [categoryFormErrors, setCategoryFormErrors] = useState({});
  
  // État pour les utilisateurs (onglet Utilisateurs)
  // IMPORTANT : currentOrgId est une CHAÎNE (ou null) pour correspondre au Selectbox
  // Restaurer currentOrgId depuis sessionStorage
  const [currentOrgId, setCurrentOrgId] = useState(() => {
    try {
      const savedOrgId = sessionStorage.getItem('parametres_currentOrgId');
      return savedOrgId || null;
    } catch (e) {
      return null;
    }
  });
  // Initialiser depuis sessionStorage si disponible (pour éviter la perte lors des remontages)
  const [members, setMembers] = useState(() => {
    try {
      const savedMembers = sessionStorage.getItem('parametres_members');
      const savedMembersTimestamp = sessionStorage.getItem('parametres_members_timestamp');
      const savedOrgId = sessionStorage.getItem('parametres_currentOrgId');
      if (savedMembers && savedMembersTimestamp && savedOrgId) {
        const age = Date.now() - parseInt(savedMembersTimestamp, 10);
        if (age < 5 * 60 * 1000) { // Moins de 5 minutes
          const membersData = JSON.parse(savedMembers);
          membersRef.current = membersData;
          return membersData;
        }
      }
    } catch (e) {
      console.warn('⚠️ Erreur lors de l\'initialisation des membres depuis sessionStorage:', e);
    }
    return [];
  });
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editingMemberRow, setEditingMemberRow] = useState(null);
  const [togglingMember, setTogglingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  
  
  // États pour le tri des tableaux
  const [sortConfig, setSortConfig] = useState({
    members: { column: null, direction: 'asc' },
    organizations: { column: null, direction: 'asc' },
    subscriptions: { column: null, direction: 'asc' }
  });
  const [memberFormData, setMemberFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    function: '', // Fonction
    access_type: 'user', // 'user' ou 'admin'
    role: 'viewer',
    is_owner: false,
    allowed_tabs: [], // Liste des onglets autorisés
    avatar: null // Avatar en base64 ou URL
  });

  // État unique pour tracker le lock du scroll (remplace modalOpenRef)
  const [modalScrollLocked, setModalScrollLocked] = useState(false);


  // Synchroniser les refs avec les états (pour le diagnostic)
  useEffect(() => {
    organizationsRef.current = organizations;
  }, [organizations]);

  useEffect(() => {
    subscriptionsRef.current = subscriptions;
  }, [subscriptions]);

  useEffect(() => {
    membersRef.current = members;
  }, [members]);

  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);


  // Effet de gestion du scroll - VERSION CORRIGÉE
  useEffect(() => {
    const isAnyModalOpen = showCreateForm || showSubscriptionForm || showMemberForm;
    
    if (isAnyModalOpen && !modalScrollLocked) {
      // OUVERTURE : Bloquer le scroll une seule fois
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const scrollY = window.scrollY;
      
      // Stocker la position dans un data attribute (plus fiable qu'une variable)
      document.body.dataset.scrollY = scrollY.toString();
      
      // Appliquer IMMÉDIATEMENT (pas de setTimeout)
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      setModalScrollLocked(true);
      
    } else if (!isAnyModalOpen && modalScrollLocked) {
      // FERMETURE : Restaurer le scroll
      const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
      
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      delete document.body.dataset.scrollY;
      
      window.scrollTo(0, scrollY);
      
      setModalScrollLocked(false);
    }
  }, [showCreateForm, showSubscriptionForm, showMemberForm, modalScrollLocked]);

  // Cleanup de sécurité au démontage du composant
  useEffect(() => {
      return () => {
      // Garantir la restauration si le composant se démonte avec une modal ouverte
      if (document.body.style.position === 'fixed') {
        const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        delete document.body.dataset.scrollY;
        window.scrollTo(0, scrollY);
    }
    };
  }, []);
  
  // État simple pour le formulaire de création / édition de société
  const [formData, setFormData] = useState({
    organization: {
      name: '',
      slug: '',
      siren: '',
      address_line1: '',
      address_line2: '',
      city: '',
      postal_code: '',
      country: '',
      subscription_tier: 'free',
      is_active: true
    },
    super_admin: {
      email: '',
      password: '',
      first_name: '',
      last_name: ''
    }
  });

  // Utiliser le statut du contexte PortfolioContext pour la synchronisation
  const portfolioContext = usePortfolio();
  const portfolioIsConnected = portfolioContext?.isConnected ?? false;
  const checkPortfolioStatus = portfolioContext?.checkStatus;
  const connectPortfolio = portfolioContext?.connect;
  const disconnectPortfolio = portfolioContext?.disconnect;
  
  const isConnectedRef = useRef(portfolioIsConnected);
  
  // Synchroniser l'état local avec le contexte
  useEffect(() => {
    setIsConnected(portfolioIsConnected);
    isConnectedRef.current = portfolioIsConnected;
  }, [portfolioIsConnected]);
  
  const checkStatus = useCallback(async () => {
    try {
      // Utiliser la fonction du contexte pour garantir la synchronisation
      const connected = await checkPortfolioStatus();
      setIsConnected(connected);
      isConnectedRef.current = connected;
      return connected;
    } catch (err) {
      console.warn('⚠️ [Parametres checkStatus] Erreur:', err.message);
      // Conserver le statut actuel en cas d'erreur
      return isConnectedRef.current;
    }
  }, [checkPortfolioStatus]);

  // FONCTION STABLE - Chargement des abonnements
  const loadSubscriptions = useCallback(async (silent = false) => {
    // Ne pas bloquer si déjà en cours de chargement (utiliser ref pour éviter les boucles)
    if (isLoadingSubsRef.current) return;
    
    isLoadingSubsRef.current = true;
    setLoadingSubscriptions(true);
    try {
      const response = await subscriptionsAPI.listAll();
      const subs = Array.isArray(response.data) ? response.data : [];
      setSubscriptions(subs);
      subscriptionsRef.current = subs;
      subsLoadedRef.current = true;
      // Persister dans sessionStorage pour éviter la perte lors des remontages
      try {
        sessionStorage.setItem('parametres_subscriptions', JSON.stringify(subs));
        sessionStorage.setItem('parametres_subscriptions_timestamp', Date.now().toString());
      } catch (e) {
        // Ignorer les erreurs de sessionStorage
      }
    } catch (err) {
      // Ne pas vider les données existantes en cas d'erreur (garder les données en cache)
      // Utiliser setSubscriptions avec une fonction pour préserver les données existantes
      setSubscriptions(prev => {
        // Si on a déjà des données, les garder
        if (prev && prev.length > 0) {
          return prev;
        }
        // Sinon, utiliser les valeurs par défaut seulement si on n'a pas encore de données
        return [
        { id: 1, code: 'free', name: 'Gratuit', price: 0, currency: 'EUR', is_active: true },
        { id: 2, code: 'pro', name: 'Professionnel', price: 29.99, currency: 'EUR', is_active: true },
        { id: 3, code: 'enterprise', name: 'Entreprise', price: 99.99, currency: 'EUR', is_active: true }
      ];
      });
      // Ne pas afficher d'erreur si c'est un rechargement silencieux en arrière-plan
      if (!silent) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur lors du chargement des abonnements';
        showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      }
      // Ne pas mettre subsLoadedRef.current à true en cas d'erreur pour permettre un nouvel essai
      subsLoadedRef.current = false; // Réinitialiser pour permettre un nouvel essai
    } finally {
      isLoadingSubsRef.current = false;
      setLoadingSubscriptions(false);
    }
  }, [showToast]);

  // FONCTION STABLE - Chargement des organisations
  const loadOrganizations = useCallback(async (silent = false) => {
    // Ne pas bloquer si déjà en cours de chargement (utiliser ref pour éviter les boucles)
    if (isLoadingOrgsRef.current) return;
    
    isLoadingOrgsRef.current = true;
    setLoadingOrgs(true);
    try {
      const response = await organizationsAPI.list();
      const orgs = Array.isArray(response.data) ? response.data : [];
      setOrganizations(orgs);
      organizationsRef.current = orgs;
      orgsLoadedRef.current = true;
      // Persister dans sessionStorage pour éviter la perte lors des remontages
      try {
        sessionStorage.setItem('parametres_organizations', JSON.stringify(orgs));
        sessionStorage.setItem('parametres_organizations_timestamp', Date.now().toString());
      } catch (e) {
        // Ignorer les erreurs de sessionStorage
      }
    } catch (err) {
      // Ne pas vider les données existantes en cas d'erreur (garder les données en cache)
      // Utiliser setOrganizations avec une fonction pour préserver les données existantes
      setOrganizations(prev => {
        // Si on a déjà des données, les garder
        if (prev && prev.length > 0) {
          return prev;
        }
        // Sinon, retourner un tableau vide seulement si on n'avait rien
        return [];
      });
      // Ne pas afficher d'erreur si c'est un rechargement silencieux en arrière-plan
      if (!silent) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur lors du chargement des sociétés';
        showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      }
      // Ne pas mettre orgsLoadedRef.current à true en cas d'erreur pour permettre un nouvel essai
      orgsLoadedRef.current = false; // Réinitialiser pour permettre un nouvel essai
    } finally {
      isLoadingOrgsRef.current = false;
      setLoadingOrgs(false);
    }
  }, [showToast]);

  
  // Vérifier le statut seulement au montage, pas à chaque re-render
  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Seulement au montage

  // Vérifier le statut de connexion quand on ouvre l'onglet API (index 3)
  useEffect(() => {
    if (activeTab === 3) { // Onglet API
      checkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // checkStatus est stable
  
  // Vérifier le statut UNIQUEMENT quand on ouvre l'onglet API (pas de vérification périodique globale)


  // Charger les données UNIQUEMENT au changement d'onglet (PAS de rechargement automatique)
  // Logique : chargement séquentiel dans l'ordre : abonnements → sociétés → utilisateurs
  // Note: Les données sont maintenant initialisées directement dans useState depuis sessionStorage
  // pour éviter la perte lors des remontages (pas besoin de useEffect de restauration)
  useEffect(() => {
    // Vérifier si l'utilisateur est admin dans son organisation
    const isAdmin = profile?.role === 'admin' || profile?.role === 'owner' || profile?.is_owner || profile?.is_superadmin;
    
    if (!isAdmin) {
      return;
    }
    
    // Index fixes des onglets (basés sur l'ordre dans allTabs)
    // 0: Configuration, 1: Apparence, 2: Valeurs par défaut, 3: API, 4: Import/Export, 5: Compte, 6: Utilisateurs, 7: Administration
    const adminTabIndex = 7;
    const usersTabIndex = 6;

    // Chargement séquentiel : abonnements → sociétés → utilisateurs
    if (activeTab === adminTabIndex || activeTab === usersTabIndex) {
      // Étape 1 : Charger les abonnements d'abord (si pas encore chargés)
      if (!subsLoadedRef.current && subscriptions.length === 0 && !loadingSubscriptions) {
        setTimeout(async () => {
          await loadSubscriptions();
          // Étape 2 : Charger les sociétés après les abonnements (si pas encore chargées)
          if (!orgsLoadedRef.current && organizations.length === 0 && !loadingOrgs) {
            setTimeout(async () => {
              await loadOrganizations();
              // Étape 3 : Charger les utilisateurs après les sociétés (si une société est sélectionnée)
              const orgIdToLoad = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
              if (activeTab === usersTabIndex && orgIdToLoad && members.length === 0 && !loadingMembers) {
                setTimeout(() => {
                  loadMembers(orgIdToLoad);
                }, 0);
              }
            }, 0);
          }
        }, 0);
      } else if (!orgsLoadedRef.current && organizations.length === 0 && !loadingOrgs) {
        // Si les abonnements sont déjà chargés, charger directement les sociétés
        setTimeout(async () => {
          await loadOrganizations();
          // Étape 3 : Charger les utilisateurs après les sociétés
          const orgIdToLoad = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
          if (activeTab === usersTabIndex && orgIdToLoad && members.length === 0 && !loadingMembers) {
            setTimeout(() => {
              loadMembers(orgIdToLoad);
            }, 0);
          }
        }, 0);
      } else if (activeTab === usersTabIndex) {
        // Si les abonnements et sociétés sont déjà chargés, charger directement les utilisateurs
        const orgIdToLoad = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
        if (orgIdToLoad && members.length === 0 && !loadingMembers) {
          setTimeout(() => {
            loadMembers(orgIdToLoad);
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, profile?.email, currentOrgId]); // Chargement uniquement au changement d'onglet

  // État pour le relancement du backend
  const [restartingBackend, setRestartingBackend] = useState(false);

  // Fonction pour vérifier le statut du backend
  const checkBackendStatus = useCallback(async () => {
    setBackendStatus(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await healthAPI.getHealth();
      // Axios structure: response.data contient les données JSON
      // S'assurer qu'on récupère bien les données
      const healthData = response?.data || response;
      setBackendStatus({
        connected: true,
        loading: false,
        lastCheck: new Date(),
        error: null,
        health: healthData
      });
    } catch (error) {
      // Afficher l'erreur complète pour diagnostic
      let errorMessage = 'Impossible de se connecter au backend';
      
      // Extraire le message d'erreur de manière robuste
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail);
        } else if (error.response.data.message) {
          errorMessage = typeof error.response.data.message === 'string'
            ? error.response.data.message
            : JSON.stringify(error.response.data.message);
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // S'assurer que errorMessage est toujours une chaîne
      if (typeof errorMessage !== 'string') {
        errorMessage = String(errorMessage);
      }
      
      console.error('❌ [Health Check] Erreur complète:', {
        message: errorMessage,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        code: error.code,
        config: error.config ? {
          url: error.config.url,
          method: error.config.method,
          timeout: error.config.timeout
        } : null,
        error: error
      });
      setBackendStatus({
        connected: false,
        loading: false,
        lastCheck: new Date(),
        error: errorMessage,
        health: null
      });
    }
  }, []);

  // Fonction pour vérifier le statut IB Gateway (et keep-alive - même endpoint)
  const checkIbGatewayStatus = useCallback(async () => {
    setIbGatewayStatus(prev => ({ ...prev, loading: true, error: null }));
    setKeepAliveStatus(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await twsAPI.getStatus(true);
      const isConnected = response.data?.is_connected || false;
      setIbGatewayStatus({
        connected: isConnected,
        loading: false,
        lastCheck: new Date(),
        error: null,
        status: response.data,
        lastError: response.data?.last_error ?? null
      });
      // Mettre à jour le statut keep-alive (données du même endpoint)
      const stats = response.data?.keep_alive_stats;
      const running = response.data?.keepalive_running ?? stats?.is_running ?? false;
      setKeepAliveStatus({
        running,
        loading: false,
        lastCheck: new Date(),
        error: null,
        stats: stats || null
      });
    } catch (error) {
      setIbGatewayStatus({
        connected: false,
        loading: false,
        lastCheck: new Date(),
        error: error.message || 'Impossible de vérifier le statut IB Gateway',
        status: null
      });
      setKeepAliveStatus({
        running: false,
        loading: false,
        lastCheck: new Date(),
        error: error.response?.status === 503 ? 'Backend non disponible' : (error.message || 'Impossible de vérifier le keep-alive'),
        stats: null
      });
    }
  }, []);

  const fetchTwsDiagnostic = useCallback(async () => {
    setTwsDiagnosticModal(prev => ({ ...prev, show: true, loading: true, error: null, data: null }));
    try {
      const res = await twsAPI.getDiagnostic();
      const data = res.data;
      setTwsDiagnosticModal(prev => ({ ...prev, loading: false, data, error: null }));
    } catch (err) {
      setTwsDiagnosticModal(prev => ({
        ...prev,
        loading: false,
        data: null,
        error: err.response?.data?.detail || err.message || 'Impossible de récupérer le diagnostic'
      }));
    }
  }, []);

  const closeTwsDiagnosticModal = useCallback(() => {
    setTwsDiagnosticModal(prev => ({ ...prev, show: false, data: null, error: null }));
  }, []);

  // Fonction pour relancer le backend
  const restartBackend = useCallback(async () => {
    setRestartingBackend(true);
    try {
      const response = await healthAPI.restartBackend();
      showToast('Le backend est en cours de redémarrage...', 'info', 5000, 'Redémarrage', 'Paramètres', getPageIcon('Paramètres'));
      // Attendre quelques secondes puis vérifier le statut
      setTimeout(() => {
        checkBackendStatus();
        setRestartingBackend(false);
      }, 5000);
    } catch (error) {
      showToast('Erreur lors du redémarrage: ' + (error.message || 'Erreur inconnue'), 'error', 5000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      setRestartingBackend(false);
    }
  }, [checkBackendStatus, showToast]);

  // Fonction pour vérifier le statut du service de sauvegarde automatique
  const checkDailyValuationStatus = useCallback(async () => {
    setDailyValuationStatus(prev => ({ ...prev, loading: true, error: null }));
    try {
      // Récupérer les valorisations récentes (30 derniers jours)
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const response = await portfolioAPI.getValuations(startDate, endDate);
      
      if (response.data && Array.isArray(response.data)) {
        // Chercher la dernière valorisation automatique (avec le commentaire spécifique)
        const automaticValuations = response.data.filter(v => 
          v.commentaire && 
          v.commentaire.includes('Valorisation SPOT sauvegardée automatiquement à 22h00')
        );
        
        if (automaticValuations.length > 0) {
          // Trier par date décroissante et prendre la plus récente
          const latest = automaticValuations.sort((a, b) => 
            new Date(b.date_valorisation) - new Date(a.date_valorisation)
          )[0];
          
          const valuationDate = new Date(latest.date_valorisation);
          const now = new Date();
          const daysSinceLastSave = Math.floor((now - valuationDate) / (1000 * 60 * 60 * 24));
          
          // Le service est considéré comme fonctionnel si une sauvegarde automatique existe
          // et qu'elle date de moins de 2 jours (pour tolérer les week-ends)
          const isWorking = daysSinceLastSave <= 2;
          
          setDailyValuationStatus({
            connected: isWorking,
            loading: false,
            lastCheck: new Date(),
            error: null,
            lastValuation: {
              date: latest.date_valorisation,
              value: latest.valeur_totale,
              daysAgo: daysSinceLastSave
            }
          });
        } else {
          // Aucune valorisation automatique trouvée
          setDailyValuationStatus({
            connected: false,
            loading: false,
            lastCheck: new Date(),
            error: 'Aucune sauvegarde automatique trouvée dans les 30 derniers jours',
            lastValuation: null
          });
        }
      } else {
        setDailyValuationStatus({
          connected: false,
          loading: false,
          lastCheck: new Date(),
          error: 'Aucune donnée de valorisation disponible',
          lastValuation: null
        });
      }
    } catch (error) {
      setDailyValuationStatus({
        connected: false,
        loading: false,
        lastCheck: new Date(),
        error: error.message || 'Impossible de vérifier le statut du service',
        lastValuation: null
      });
    }
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    try {
      // Utiliser la fonction du contexte pour garantir la synchronisation
      if (connectPortfolio) {
        try {
          const connected = await connectPortfolio();
          if (connected) {
            showToast('La connexion à IB Gateway a été établie avec succès.', 'success', 3000, 'Connexion IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
            // Le statut sera mis à jour automatiquement via le useEffect qui écoute portfolioIsConnected
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.warn('⚠️ [handleConnect] connectPortfolio a retourné false');
            }
            showToast('Vérifiez que IB Gateway est lancé et que les connexions API sont activées dans les paramètres.', 'error', 3000, 'Échec de connexion IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
          }
        } catch (connectErr) {
          // Gérer l'erreur propagée depuis PortfolioContext
          console.error('❌ [handleConnect] Erreur depuis connectPortfolio:', connectErr);
          if (connectErr.response?.status === 500) {
            const detail = connectErr.response?.data?.detail || connectErr.message || '';
            if (detail) {
              // Afficher le message complet dans la console (avec sauts de ligne)
              console.error('❌ [TWS Connect] Erreur détaillée du backend:', detail);
              // Message simplifié pour le toast (sans sauts de ligne)
              const errorMsg = detail.split('\n')[0] || detail.replace(/\n/g, ' ').trim();
              showToast(errorMsg, 'error', 5000, 'Erreur de connexion', 'Paramètres', getPageIcon('Paramètres'));
            } else {
              showToast('Vérifiez les logs du backend pour plus de détails sur l\'erreur serveur.', 'error', 3000, 'Erreur serveur', 'Paramètres', getPageIcon('Paramètres'));
            }
          } else {
            const errorMsg = connectErr.response?.data?.detail || connectErr.message || 'Échec de connexion à IB Gateway';
            showToast(errorMsg, 'error', 3000, 'Échec de connexion IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
          }
        }
      } else {
        // Fallback si le contexte n'est pas disponible
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ [handleConnect] connectPortfolio non disponible, utilisation du fallback');
        }
        const response = await twsAPI.connect();
        const statusResponse = await twsAPI.getStatus();
        const actuallyConnected = statusResponse.data.is_connected;
        isConnectedRef.current = actuallyConnected;
        setIsConnected(actuallyConnected);
        
        if (actuallyConnected) {
          showToast('La connexion à IB Gateway a été établie avec succès.', 'success', 3000, 'Connexion IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
        } else {
          showToast('Vérifiez que IB Gateway est lancé et que les connexions API sont activées dans les paramètres.', 'error', 3000, 'Échec de connexion IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
        }
      }
    } catch (err) {
      isConnectedRef.current = false;
      setIsConnected(false);
      
      console.error('🔴 [handleConnect] Erreur détaillée:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url
      });
      
      // Gérer spécifiquement les erreurs
      if (err.response?.status === 404) {
        const url = err.config?.url || 'route inconnue';
        showToast(`Route API non trouvée: ${url}. Vérifiez que le backend est démarré et que la route existe.`, 'error', 3000, 'Route API introuvable', 'Paramètres', getPageIcon('Paramètres'));
      } else if (err.response?.status === 500) {
        const detail = err.response?.data?.detail || err.message || '';
        // Afficher le message détaillé du backend qui contient les instructions
        if (detail) {
          // Afficher le message complet dans la console (avec sauts de ligne)
          console.error('❌ [TWS Connect] Erreur détaillée du backend:', detail);
          // Message simplifié pour le toast (première ligne ou sans sauts de ligne)
          const errorMsg = detail.split('\n')[0] || detail.replace(/\n/g, ' ').trim();
          showToast(errorMsg, 'error', 5000, 'Erreur de connexion', 'Paramètres', getPageIcon('Paramètres'));
        } else {
          showToast('Vérifiez les logs du backend pour plus de détails sur l\'erreur serveur.', 'error', 3000, 'Erreur serveur', 'Paramètres', getPageIcon('Paramètres'));
        }
      } else if (err.response?.status === 504) {
        showToast('Le serveur ne répond pas. Vérifiez que le backend est démarré et accessible.', 'error', 3000, 'Serveur inaccessible', 'Paramètres', getPageIcon('Paramètres'));
      } else if (err.response?.status === 503) {
        showToast('Vérifiez qu\'IB Gateway est lancé et que les connexions API sont activées dans les paramètres.', 'error', 3000, 'IB Gateway non connecté', 'Paramètres', getPageIcon('Paramètres'));
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        showToast('Vérifiez que le backend est démarré sur http://localhost:8000 et accessible.', 'error', 3000, 'Erreur réseau', 'Paramètres', getPageIcon('Paramètres'));
      } else {
        const errorMsg = err.response?.data?.detail || err.message || 'Échec de connexion à IB Gateway';
        showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      if (disconnectPortfolio) {
        await disconnectPortfolio();
      } else {
      await twsAPI.disconnect();
      }
      isConnectedRef.current = false;
      setIsConnected(false);
      showToast('Vous avez été déconnecté d\'IB Gateway.', 'info', 3000, 'Déconnexion', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      // Erreur silencieuse lors de la déconnexion
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    
    // Réinitialiser les erreurs
    const errors = {};
    
    // Validation côté client
    if (!formData.organization.name || !formData.organization.name.trim()) {
      errors['organization.name'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.slug || !formData.organization.slug.trim()) {
      errors['organization.slug'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.siren || !formData.organization.siren.trim()) {
      errors['organization.siren'] = 'Veuillez renseigner ce champ.';
    } else if (!/^[0-9]{9}$/.test(formData.organization.siren)) {
      errors['organization.siren'] = 'Le SIREN doit contenir exactement 9 chiffres.';
    }
    if (!formData.organization.address_line1 || !formData.organization.address_line1.trim()) {
      errors['organization.address_line1'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.postal_code || !formData.organization.postal_code.trim()) {
      errors['organization.postal_code'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.city || !formData.organization.city.trim()) {
      errors['organization.city'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.country || !formData.organization.country.trim()) {
      errors['organization.country'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.subscription_tier || !formData.organization.subscription_tier.trim()) {
      errors['organization.subscription_tier'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.super_admin.email || !formData.super_admin.email.trim()) {
      errors['super_admin.email'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.super_admin.password || formData.super_admin.password.length < 8) {
      errors['super_admin.password'] = 'Le mot de passe doit contenir au moins 8 caractères.';
    } else {
      // Vérifier la longueur en bytes (bcrypt limite à 72 bytes, pas 72 caractères)
      const passwordBytes = new TextEncoder().encode(formData.super_admin.password).length;
      if (passwordBytes > 72) {
        errors['super_admin.password'] = 'Le mot de passe ne peut pas dépasser 72 bytes (environ 72 caractères ASCII ou moins pour les caractères accentués).';
      }
    }
    if (!formData.super_admin.first_name || !formData.super_admin.first_name.trim()) {
      errors['super_admin.first_name'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.super_admin.last_name || !formData.super_admin.last_name.trim()) {
      errors['super_admin.last_name'] = 'Veuillez renseigner ce champ.';
    }
    
    // Afficher les erreurs
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showToast('Veuillez corriger les erreurs dans le formulaire avant de continuer.', 'error', 3000, 'Erreur de formulaire', 'Paramètres', getPageIcon('Paramètres'));
      return;
    }
    
    // Réinitialiser les erreurs si tout est valide
    setFormErrors({});
    
    setLoading(true);
    try {
      const response = await organizationsAPI.createWithAdmin(formData);
      const newOrg = response.data?.organization;
      
      // Fermer la modal IMMÉDIATEMENT
      setShowCreateForm(false);
      setFormErrors({});
      setFormData({
        organization: { 
          name: '', 
          slug: '', 
          siren: '',
          address_line1: '',
          address_line2: '',
          city: '',
          postal_code: '',
          country: '',
          subscription_tier: 'free',
          is_active: true
        },
        super_admin: { email: '', password: '', first_name: '', last_name: '' }
      });
      
      // Mise à jour optimiste : ajouter la nouvelle organisation immédiatement
      if (newOrg) {
        setOrganizations(prev => [...prev, newOrg]);
      }
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      orgsLoadedRef.current = false;
        loadOrganizations(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('La société a été créée avec succès.', 'success', 3000, 'Création de société', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur lors de la création de la société';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = useCallback((section, field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      };
      return newData;
    });
  }, []);

  // Générer le slug automatiquement depuis le nom
  const handleNameChange = useCallback((value) => {
    handleFormChange('organization', 'name', value);
    // Générer le slug automatiquement
    const slug = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    handleFormChange('organization', 'slug', slug);
  }, [handleFormChange]);

  // Handler pour fermer la modal avec protection contre les fermetures accidentelles
  const handleCloseModal = useCallback((e) => {
    // Si un événement est fourni, vérifier qu'il n'est pas un événement de soumission de formulaire
    if (e && e.type === 'submit') {
      return;
    }
    
    setShowCreateForm(false);
    setEditingOrg(null);
    setFormData({
      organization: { 
        name: '', 
        slug: '', 
        siren: '',
        address_line1: '',
        address_line2: '',
        city: '',
        postal_code: '',
        country: '',
        subscription_tier: 'free',
        is_active: true
      },
      super_admin: { email: '', password: '', first_name: '', last_name: '' }
    });
    setFormErrors({});
  }, []); // Pas de dépendances

  // Handler pour le bouton Annuler
  const handleCancel = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    handleCloseModal();
  }, [handleCloseModal]);

  // Handler pour fermer la modal abonnements
  const handleCloseSubscriptionModal = useCallback((e) => {
    if (e && e.type === 'submit') {
      return;
    }
    setShowSubscriptionForm(false);
    setEditingSubscription(null);
    setSubscriptionFormData({
      code: '',
      name: '',
      description: '',
      price: 0,
      currency: 'EUR',
      is_active: true
    });
  }, []);

  // Handler pour annuler la modal abonnements
  const handleCancelSubscription = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    handleCloseSubscriptionModal();
  }, [handleCloseSubscriptionModal]);

  // Handler pour fermer la modal utilisateurs
  const handleCloseMemberModal = useCallback((e) => {
    if (e && e.type === 'submit') {
      return;
    }
    setShowMemberForm(false);
    setEditingMember(null);
    setEditingMemberRow(null);
    setMemberFormErrors({});
    setMemberFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      function: '',
      access_type: 'user',
      role: 'viewer',
      is_owner: false,
      allowed_tabs: []
    });
  }, []);

  // Handler pour annuler la modal utilisateurs
  const handleCancelMember = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    handleCloseMemberModal();
  }, [handleCloseMemberModal]);

  // Handlers pour l'édition/suppression d'organisations
  const handleEditOrganization = useCallback((org) => {
    setEditingOrg(org);
    setFormErrors({});
    setFormData({
      organization: {
        name: org.name || '',
        slug: org.slug || '',
        siren: org.siren || '',
        address_line1: org.address_line1 || '',
        address_line2: org.address_line2 || '',
        city: org.city || '',
        postal_code: org.postal_code || '',
        country: org.country || '',
        subscription_tier: org.subscription_tier || 'free',
        is_active: org.is_active !== false
      },
      // Pas de super_admin lors de l'édition d'une société existante
      super_admin: {
        email: '',
        password: '',
        first_name: '',
        last_name: ''
      }
    });
    setShowCreateForm(true);
  }, []);

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    if (!editingOrg) return;
    
    // Réinitialiser les erreurs
    const errors = {};
    
    // Validation côté client
    if (!formData.organization.name || !formData.organization.name.trim()) {
      errors['organization.name'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.slug || !formData.organization.slug.trim()) {
      errors['organization.slug'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.siren || !formData.organization.siren.trim()) {
      errors['organization.siren'] = 'Veuillez renseigner ce champ.';
    } else if (!/^[0-9]{9}$/.test(formData.organization.siren)) {
      errors['organization.siren'] = 'Le SIREN doit contenir exactement 9 chiffres.';
    }
    if (!formData.organization.address_line1 || !formData.organization.address_line1.trim()) {
      errors['organization.address_line1'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.postal_code || !formData.organization.postal_code.trim()) {
      errors['organization.postal_code'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.city || !formData.organization.city.trim()) {
      errors['organization.city'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.country || !formData.organization.country.trim()) {
      errors['organization.country'] = 'Veuillez renseigner ce champ.';
    }
    if (!formData.organization.subscription_tier || !formData.organization.subscription_tier.trim()) {
      errors['organization.subscription_tier'] = 'Veuillez renseigner ce champ.';
    }
    
    // Afficher les erreurs
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showToast('Veuillez corriger les erreurs dans le formulaire avant de continuer.', 'error', 3000, 'Erreur de formulaire', 'Paramètres', getPageIcon('Paramètres'));
      return;
    }
    
    // Réinitialiser les erreurs si tout est valide
    setFormErrors({});
    
    setLoading(true);
    try {
      const response = await organizationsAPI.update(editingOrg.id, formData.organization);
      const updatedOrg = response.data;
      
      // Fermer la modal IMMÉDIATEMENT
      setShowCreateForm(false);
      setEditingOrg(null);
      setFormErrors({});
      setFormData({
        organization: { 
          name: '', 
          slug: '', 
          siren: '',
          address_line1: '',
          address_line2: '',
          city: '',
          postal_code: '',
          country: '',
          subscription_tier: 'free',
          is_active: true
        },
        super_admin: { email: '', password: '', first_name: '', last_name: '' }
      });
      
      // Mise à jour optimiste : mettre à jour l'organisation immédiatement
      if (updatedOrg) {
        setOrganizations(prev => prev.map(org => org.id === updatedOrg.id ? updatedOrg : org));
      }
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      orgsLoadedRef.current = false;
        loadOrganizations(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('La société a été mise à jour avec succès.', 'success', 3000, 'Mise à jour de société', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur lors de la mise à jour';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = useCallback((org) => {
    setDeletingOrg(org);
  }, []);

  const confirmDeleteOrganization = async () => {
    if (!deletingOrg) return;
    
    const orgIdToDelete = deletingOrg.id;
    
    setLoading(true);
    try {
      await organizationsAPI.delete(orgIdToDelete);
      
      // Fermer la modal IMMÉDIATEMENT
      setDeletingOrg(null);
      
      // Mise à jour optimiste : retirer l'organisation immédiatement
      setOrganizations(prev => prev.filter(org => org.id !== orgIdToDelete));
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      orgsLoadedRef.current = false;
        loadOrganizations(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('La société a été supprimée avec succès.', 'success', 3000, 'Suppression de société', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la suppression';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrganizationStatus = useCallback((org) => {
    setTogglingOrg(org);
  }, []);

  const confirmToggleOrganizationStatus = async () => {
    if (!togglingOrg) return;
    
    const orgId = togglingOrg.id;
    const newStatus = !togglingOrg.is_active;
    
    setLoading(true);
    try {
      await organizationsAPI.updateStatus(orgId, newStatus);
      
      // Fermer la modal IMMÉDIATEMENT
      setTogglingOrg(null);
      
      // Mise à jour optimiste : mettre à jour le statut immédiatement
      setOrganizations(prev => prev.map(org => 
        org.id === orgId ? { ...org, is_active: newStatus } : org
      ));
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      orgsLoadedRef.current = false;
        loadOrganizations(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast(`La société a été ${newStatus ? 'activée' : 'désactivée'} avec succès.`, 'success', 3000, 'Modification de société', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la mise à jour';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  // FONCTION STABLE - Chargement des membres
  const loadMembers = useCallback(async (orgIdStr) => {
    if (!orgIdStr) {
      setMembers([]);
      return;
    }
    
    const orgId = parseInt(orgIdStr);
    if (isNaN(orgId)) {
      setMembers([]);
      return;
    }

    const t0 = performance.now();
    setLoadingMembers(true);
    try {
      const response = await organizationsAPI.listMembers(orgId);
      const membersData = response.data || [];
      
      // Charger les avatars depuis SystemParameter pour chaque utilisateur
      const membersWithAvatars = await Promise.all(
        membersData.map(async (member) => {
          const userId = member.user?.id || member.user?.user_id;
          if (userId) {
            try {
              const avatarResponse = await settingsAPI.get('user_avatar', null, userId);
              if (avatarResponse.data?.param_value) {
                // Mettre à jour l'avatar dans member.user.picture
                return {
                  ...member,
                  user: {
                    ...member.user,
                    picture: avatarResponse.data.param_value
                  }
                };
              }
            } catch (avatarError) {
              // Ignorer silencieusement les erreurs 404 (pas d'avatar personnalisé - c'est normal)
              // Ne logger que les autres erreurs
              if (avatarError.response?.status !== 404 && avatarError.code !== 'ECONNABORTED') {
                console.warn(`⚠️ [loadMembers] Erreur lors du chargement de l'avatar pour user_id ${userId}:`, avatarError.message);
              }
            }
          }
          return member;
        })
      );
      
      setMembers(membersWithAvatars);
      membersRef.current = membersWithAvatars;  // Mettre à jour le ref pour le diagnostic
      // Persister dans sessionStorage pour éviter la perte lors des remontages
      try {
        sessionStorage.setItem('parametres_members', JSON.stringify(membersWithAvatars));
        sessionStorage.setItem('parametres_members_timestamp', Date.now().toString());
        sessionStorage.setItem('parametres_currentOrgId', orgIdStr);
      } catch (e) {
        console.warn('⚠️ Impossible de sauvegarder dans sessionStorage:', e);
      }
    } catch (err) {
      showToast('Impossible de charger la liste des utilisateurs. Veuillez réessayer.', 'error', 3000, 'Erreur de chargement', 'Paramètres', getPageIcon('Paramètres'));
      setMembers([]);
      membersRef.current = [];  // Mettre à jour le ref même en cas d'erreur
    } finally {
      setLoadingMembers(false);
      if (process.env.NODE_ENV === 'development') {
      }
    }
  }, [showToast]);

  // Options pour le Selectbox des organisations
  const organizationOptions = useMemo(() => {
    return organizations.map(org => ({
      value: org.id.toString(),
      label: org.name
    }));
  }, [organizations]);
  
  // Handler pour la sélection d'organisation dans l'onglet Utilisateurs
  const handleOrganizationChange = useCallback((value) => {
    const orgId = value || null;
    setCurrentOrgId(orgId);
    // Sauvegarder currentOrgId dans sessionStorage
    try {
      if (orgId) {
        sessionStorage.setItem('parametres_currentOrgId', orgId);
      } else {
        sessionStorage.removeItem('parametres_currentOrgId');
        sessionStorage.removeItem('parametres_members');
        sessionStorage.removeItem('parametres_members_timestamp');
      }
    } catch (e) {
      // Ignorer les erreurs de sessionStorage
    }
  }, []);

  // Initialiser currentOrgId avec l'organisation de l'utilisateur si ce n'est pas un superadmin
  useEffect(() => {
    if (profile && !profile?.is_superadmin && profile?.organization_id) {
      const userOrgId = profile?.organization_id?.toString();
      // Si currentOrgId n'est pas défini ou est différent de l'organisation de l'utilisateur, le mettre à jour
      if (!currentOrgId || currentOrgId !== userOrgId) {
        setCurrentOrgId(userOrgId);
        // Sauvegarder dans sessionStorage
        try {
          sessionStorage.setItem('parametres_currentOrgId', userOrgId);
        } catch (e) {
          // Ignorer les erreurs de sessionStorage
        }
      }
    }
  }, [profile?.is_superadmin, profile?.organization_id]);

  // Charger les membres UNIQUEMENT quand currentOrgId change (PAS de rechargement périodique)
  // Utiliser setTimeout pour différer le chargement et permettre au bouton de s'afficher instantanément
  useEffect(() => {
    if (!profile) {
      setMembers([]);
      return;
    }
    
    const orgIdToLoad = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
    if (orgIdToLoad) {
      // Différer davantage pour laisser le bouton s'afficher
      const timeoutId = setTimeout(() => {
        loadMembers(orgIdToLoad.toString());
      }, 100); // Augmenter à 100ms
      return () => clearTimeout(timeoutId);
    } else {
      setMembers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, profile?.is_superadmin, profile?.organization_id, profile, loadMembers]);

  const handleCreateMember = async (e) => {
    e.preventDefault();
    
    if (!currentOrgId) {
      console.warn('🔴 [handleCreateMember] Pas de currentOrgId');
      return;
    }
    
    // Réinitialiser les erreurs
    const errors = {};
    
    // Convertir en nombre seulement pour l'API
    const orgId = parseInt(currentOrgId);
    if (isNaN(orgId)) {
      console.error('🔴 [handleCreateMember] orgId invalide:', currentOrgId);
      errors.general = 'Société invalide';
      setMemberFormErrors(errors);
      return;
    }
    
    // Validation
    if (!memberFormData.email || !memberFormData.email.trim()) {
      console.warn('🔴 [handleCreateMember] Email manquant');
      errors.email = 'Veuillez renseigner l\'email.';
    }
    if (!memberFormData.password || memberFormData.password.length < 8) {
      console.warn('🔴 [handleCreateMember] Mot de passe invalide:', {
        exists: !!memberFormData.password,
        length: memberFormData.password?.length || 0,
        value: memberFormData.password ? '[MASQUÉ]' : 'VIDE'
      });
      errors.password = 'Le mot de passe doit contenir au moins 8 caractères.';
    } else {
      // Vérifier la longueur en bytes (bcrypt limite à 72 bytes, pas 72 caractères)
      const passwordBytes = new TextEncoder().encode(memberFormData.password).length;
      if (passwordBytes > 72) {
        console.error('🔴 [handleCreateMember] Mot de passe trop long en bytes:', passwordBytes);
        errors.password = 'Le mot de passe ne peut pas dépasser 72 bytes (environ 72 caractères ASCII ou moins pour les caractères accentués).';
      }
    }
    if (!memberFormData.first_name || !memberFormData.first_name.trim()) {
      errors.first_name = 'Veuillez renseigner le prénom.';
    }
    if (!memberFormData.last_name || !memberFormData.last_name.trim()) {
      errors.last_name = 'Veuillez renseigner le nom.';
    }
    const tabsToSend = getSelectedTabs();
    if (!tabsToSend || tabsToSend.length === 0) {
      errors.allowed_tabs = 'Veuillez sélectionner au moins un onglet autorisé.';
    }
    
    // Afficher les erreurs
    if (Object.keys(errors).length > 0) {
      console.error('🔴 [handleCreateMember] Erreurs de validation:', errors);
      setMemberFormErrors(errors);
      return;
    }
    
    // Réinitialiser les erreurs si tout est valide
    setMemberFormErrors({});
    
    // Fonction pour tronquer le mot de passe à 72 bytes maximum
    const truncatePasswordTo72Bytes = (password) => {
      if (!password) return password;
      
      const passwordBytes = new TextEncoder().encode(password);
      if (passwordBytes.length <= 72) {
        return password;
      }
      
      // Tronquer à 72 bytes
      const truncatedBytes = passwordBytes.slice(0, 72);
      // Supprimer les bytes incomplets UTF-8 (bytes de continuation)
      let i = truncatedBytes.length - 1;
      while (i >= 0 && (truncatedBytes[i] & 0xC0) === 0x80) {
        i--;
      }
      const finalBytes = truncatedBytes.slice(0, i + 1);
      
      // Décoder en string
      try {
        return new TextDecoder('utf-8', { fatal: false }).decode(finalBytes);
      } catch (e) {
        // Fallback: tronquer par caractères
        let result = '';
        for (let i = 0; i < password.length; i++) {
          const test = result + password[i];
          if (new TextEncoder().encode(test).length > 72) {
            break;
          }
          result = test;
        }
        return result;
      }
    };
    
    // Tronquer le mot de passe à 72 bytes AVANT l'envoi
    const originalPassword = memberFormData.password;
    const truncatedPassword = truncatePasswordTo72Bytes(originalPassword);
    const originalBytes = new TextEncoder().encode(originalPassword).length;
    const finalBytes = new TextEncoder().encode(truncatedPassword).length;
    
    if (originalBytes > 72) {
      console.warn(`🔴 [handleCreateMember] Mot de passe tronqué: ${originalBytes} bytes -> ${finalBytes} bytes`);
    }
    
    // Préparer les données à envoyer
    const memberData = {
      email: memberFormData.email.trim(),
      password: truncatedPassword, // Utiliser le mot de passe tronqué
      first_name: memberFormData.first_name.trim(),
      last_name: memberFormData.last_name.trim(),
      function: memberFormData.function?.trim() || null,
        access_type: memberFormData.access_type,
      allowed_tabs: getSelectedTabs() // Utiliser la sélection réelle (défaut = tous si aucun toggle)
    };
    
    setLoading(true);
    try {
      const response = await organizationsAPI.createMember(orgId, memberData, memberFormData.role);
      
      // Sauvegarder l'avatar si il a été fourni
      const createdUserId = response.data?.user?.id || response.data?.user?.user_id;
      const avatarToSave = memberFormData.avatar || null;
      
      if (createdUserId && avatarToSave) {
        try {
          await settingsAPI.update('user_avatar', avatarToSave, null, createdUserId);
        } catch (avatarError) {
          console.error('❌ [handleCreateMember] Erreur lors de la sauvegarde de l\'avatar:', avatarError);
          // Ne pas bloquer la création si l'avatar échoue, mais logger l'erreur
        }
      }
      
      showToast('L\'utilisateur a été créé avec succès.', 'success', 3000, 'Création d\'utilisateur', 'Paramètres', getPageIcon('Paramètres'));
      setShowMemberForm(false);
      setMemberFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        function: '',
        access_type: 'user',
        role: 'viewer',
        is_owner: false,
        allowed_tabs: [],
        avatar: null
      });
      // Recharger les membres avec la bonne organisation pour afficher l'avatar dans la liste
      const orgIdToReload = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
      if (orgIdToReload) {
        loadMembers(orgIdToReload.toString());
      }
    } catch (err) {
      console.error('🔴 [handleCreateMember] Erreur lors de la création:', {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          data: err.config?.data ? {
            ...JSON.parse(err.config.data),
            password: '[MASQUÉ]'
          } : null
        }
      });
      const errorMsg = err.response?.data?.detail || err.message || 'Erreur lors de la création';
      console.error('🔴 [handleCreateMember] Message d\'erreur à afficher:', errorMsg);
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    // Utiliser l'organisation de l'utilisateur connecté si ce n'est pas un superadmin
    const orgIdToUse = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
    if (!orgIdToUse || !editingMember) return;
    
    // Réinitialiser les erreurs
    setMemberFormErrors({});
    
    // Convertir en nombre seulement pour l'API
    const orgId = parseInt(orgIdToUse);
    if (isNaN(orgId)) {
      setMemberFormErrors({ general: 'Société invalide' });
      return;
    }
    
    setLoading(true);
    try {
      const updateData = {
        role: editingMember.role || 'viewer',
        is_active: editingMember.user?.is_active !== undefined ? editingMember.user.is_active : true,
        is_owner: editingMember.is_owner,
        allowed_tabs: getSelectedTabs()
      };
      
      const response = await organizationsAPI.updateMember(orgId, editingMember.id, updateData);
      // La modification d'avatar n'est pas proposée dans la popup d'édition
      
      showToast('L\'utilisateur a été mis à jour avec succès.', 'success', 3000, 'Mise à jour d\'utilisateur', 'Paramètres', getPageIcon('Paramètres'));
      setShowMemberForm(false);
      setEditingMember(null);
      setMemberFormErrors({});
      setMemberFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        function: '',
        access_type: 'user',
        role: 'viewer',
        is_owner: false,
        allowed_tabs: [],
        avatar: null
      });
      // Recharger les membres avec la bonne organisation pour mettre à jour l'avatar dans la liste
      const orgIdToReload = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
      if (orgIdToReload) {
        loadMembers(orgIdToReload.toString());
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la mise à jour';
      setMemberFormErrors({ general: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (member) => {
    // Utiliser l'organisation de l'utilisateur connecté si ce n'est pas un superadmin
    const orgIdToUse = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
    if (!orgIdToUse) return;
    
    const orgId = parseInt(orgIdToUse);
    if (isNaN(orgId)) {
      showToast('La société sélectionnée est invalide. Veuillez sélectionner une société valide.', 'error', 3000, 'Erreur de validation', 'Paramètres', getPageIcon('Paramètres'));
      return;
    }
    
    if (member.is_owner) {
      showToast('Impossible de supprimer le propriétaire de la société. Le propriétaire doit être transféré avant suppression.', 'error', 3000, 'Suppression impossible', 'Paramètres', getPageIcon('Paramètres'));
      return;
    }
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${member.user.email} ?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await organizationsAPI.deleteMember(orgId, member.id);
      showToast('L\'utilisateur a été supprimé avec succès.', 'success', 3000, 'Suppression d\'utilisateur', 'Paramètres', getPageIcon('Paramètres'));
      const orgIdToReload = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
      if (orgIdToReload) {
        loadMembers(orgIdToReload);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la suppression';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMemberStatus = useCallback((member) => {
    setShowMemberForm(false);
    setEditingMember(null);
    setEditingMemberRow(null);
    setTogglingMember(member);
  }, []);

  const handleDeleteMemberFromModal = useCallback((member) => {
    setShowMemberForm(false);
    setEditingMember(null);
    setEditingMemberRow(null);
    setDeletingMember(member);
  }, []);

  const confirmToggleMemberStatus = async () => {
    if (!togglingMember) return;
    const orgIdToUse = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
    if (!orgIdToUse) return;
    const orgId = parseInt(orgIdToUse);
    if (isNaN(orgId)) return;
    
    const newStatus = !(togglingMember.user?.is_active ?? true);
    setLoading(true);
    try {
      await organizationsAPI.updateMember(orgId, togglingMember.id, { is_active: newStatus });
      setTogglingMember(null);
      setMembers(prev => prev.map(m => 
        m.id === togglingMember.id 
          ? { ...m, user: { ...m.user, is_active: newStatus } } 
          : m
      ));
      showToast(`L'utilisateur a été ${newStatus ? 'activé' : 'désactivé'} avec succès.`, 'success', 3000, 'Modification d\'utilisateur', 'Paramètres', getPageIcon('Paramètres'));
      const orgIdToReload = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
      if (orgIdToReload) loadMembers(orgIdToReload);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la modification';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      setTogglingMember(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteMember = async () => {
    if (!deletingMember) return;
    const orgIdToUse = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
    if (!orgIdToUse) return;
    const orgId = parseInt(orgIdToUse);
    if (isNaN(orgId)) return;
    
    if (deletingMember.is_owner) {
      showToast('Impossible de supprimer le propriétaire de la société.', 'error', 3000, 'Suppression impossible', 'Paramètres', getPageIcon('Paramètres'));
      setDeletingMember(null);
      return;
    }
    
    setLoading(true);
    try {
      await organizationsAPI.deleteMember(orgId, deletingMember.id);
      showToast('L\'utilisateur a été supprimé avec succès.', 'success', 3000, 'Suppression d\'utilisateur', 'Paramètres', getPageIcon('Paramètres'));
      setDeletingMember(null);
      const orgIdToReload = profile?.is_superadmin ? currentOrgId : profile?.organization_id;
      if (orgIdToReload) loadMembers(orgIdToReload);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la suppression';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      setDeletingMember(null);
    } finally {
      setLoading(false);
    }
  };

  // Handler mémorisé pour ouvrir la modal utilisateur (évite les re-renders)
  // Par défaut : accès à tout (tous les onglets cochés)
  const handleOpenMemberForm = useCallback((e, defaultTabIds) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingMember(null);
    setMemberFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      function: '',
      access_type: 'user',
      role: 'viewer',
      is_owner: false,
      allowed_tabs: Array.isArray(defaultTabIds) && defaultTabIds.length > 0 ? [...defaultTabIds] : [],
      avatar: null
    });
    setShowMemberForm(true);
  }, []);

  // Handlers mémorisés pour optimiser les changements de formulaire utilisateur
  const handleMemberFormChange = useCallback((field, value) => {
    setMemberFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleEditingMemberChange = useCallback((field, value, isUserField = false) => {
    setEditingMember(prev => {
      if (!prev) return prev;
      if (isUserField) {
        return {
          ...prev,
          user: {
            ...prev.user,
            [field]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  // Structure hiérarchique des onglets pour le formulaire Accès autorisés
  // Utilise le registre dynamique, avec fallback sur une liste statique pour compatibilité
  // Ordre d'affichage dans la sidebar (selon Dashboard.jsx)
  const sidebarOrder = useMemo(() => ({
    // Onglets niveau 1 (ordre de la sidebar)
    level1: ['accueil', 'portefeuille', 'compte', 'ai', 'analyse', 'stock-screener', 'macro', 'parametres'],
    // Sous-onglets d'Accueil (ordre d'affichage)
    accueil: ['accueil-vue-ensemble', 'accueil-performance', 'accueil-repartition'],
    // Sous-onglets de Portefeuille (ordre d'affichage)
    portefeuille: ['portefeuille-synthese', 'portefeuille-detail', 'portefeuille-performance', 'portefeuille-volatilite', 'portefeuille-sentiment', 'portefeuille-ordres', 'portefeuille-valorisation'],
    compte: ['compte-performance', 'compte-synthese-tri', 'compte-pl', 'compte-apports-retraits', 'compte-investisseurs', 'compte-valorisations', 'compte-commentaires'],
    analyse: ['analyse-analyse', 'analyse-rapports', 'analyse-feed-x', 'analyse-import', 'analyse-export', 'analyse-parametres'],
    macro: ['macro-indices', 'macro-matieres-premieres', 'macro-macro', 'macro-historique-120-ans'],
    parametres: ['compte-settings', 'configuration', 'apparence', 'valeurs-par-defaut', 'api', 'oliver', 'import-export', 'utilisateurs', 'configuration-admin', 'administration', 'statuts', 'logs'],
    // Sous-onglets de Stock screener (ordre d'affichage)
    'stock-screener': ['stock-screener-recherche', 'stock-screener-filtres', 'stock-screener-resultats']
  }), []);

  // Liste statique complète - source fiable pour le formulaire Accès autorisés
  const STATIC_TABS = useMemo(() => [
    { id: 'accueil', label: 'Accueil', parent: null, level: 1, children: ['accueil-vue-ensemble', 'accueil-performance', 'accueil-repartition'] },
    { id: 'accueil-vue-ensemble', label: "Vue d'ensemble", parent: 'accueil', level: 2 },
    { id: 'accueil-performance', label: 'Performance', parent: 'accueil', level: 2 },
    { id: 'accueil-repartition', label: 'Répartition', parent: 'accueil', level: 2 },
    { id: 'portefeuille', label: 'Portefeuille', parent: null, level: 1 },
    { id: 'portefeuille-synthese', label: 'Synthèse', parent: 'portefeuille', level: 2 },
    { id: 'portefeuille-detail', label: 'Portefeuille', parent: 'portefeuille', level: 2 },
    { id: 'portefeuille-performance', label: 'Performance', parent: 'portefeuille', level: 2 },
    { id: 'portefeuille-volatilite', label: 'Volatilité', parent: 'portefeuille', level: 2 },
    { id: 'portefeuille-sentiment', label: 'Sentiment', parent: 'portefeuille', level: 2 },
    { id: 'portefeuille-ordres', label: 'Ordres', parent: 'portefeuille', level: 2 },
    { id: 'portefeuille-valorisation', label: 'Valorisation', parent: 'portefeuille', level: 2 },
    { id: 'compte', label: 'Compte', parent: null, level: 1 },
    { id: 'compte-performance', label: 'Performance', parent: 'compte', level: 2 },
    { id: 'compte-synthese-tri', label: 'TRI', parent: 'compte', level: 2 },
    { id: 'compte-pl', label: 'P&L', parent: 'compte', level: 2 },
    { id: 'compte-apports-retraits', label: 'Apports/Retraits', parent: 'compte', level: 2 },
    { id: 'compte-investisseurs', label: 'Investisseurs', parent: 'compte', level: 2 },
    { id: 'compte-valorisations', label: 'Valorisations', parent: 'compte', level: 2 },
    { id: 'compte-commentaires', label: 'Commentaires', parent: 'compte', level: 2 },
    { id: 'ai', label: 'Oliver', parent: null, level: 1 },
    { id: 'analyse', label: 'Analyse', parent: null, level: 1 },
    { id: 'stock-screener', label: 'Stock screener', parent: null, level: 1, children: ['stock-screener-recherche', 'stock-screener-filtres', 'stock-screener-resultats'] },
    { id: 'stock-screener-recherche', label: 'Recherche', parent: 'stock-screener', level: 2 },
    { id: 'stock-screener-filtres', label: 'Filtres', parent: 'stock-screener', level: 2 },
    { id: 'stock-screener-resultats', label: 'Résultats', parent: 'stock-screener', level: 2 },
    { id: 'analyse-analyse', label: 'Analyse', parent: 'analyse', level: 2 },
    { id: 'analyse-rapports', label: 'Rapports', parent: 'analyse', level: 2 },
    { id: 'analyse-feed-x', label: 'Feed', parent: 'analyse', level: 2 },
    { id: 'analyse-import', label: 'Import', parent: 'analyse', level: 2 },
    { id: 'analyse-export', label: 'Export', parent: 'analyse', level: 2 },
    { id: 'analyse-parametres', label: 'Paramètres', parent: 'analyse', level: 2 },
    { id: 'macro', label: 'Macro', parent: null, level: 1 },
    { id: 'macro-indices', label: 'Indices', parent: 'macro', level: 2 },
    { id: 'macro-matieres-premieres', label: 'Matières Premières', parent: 'macro', level: 2 },
    { id: 'macro-macro', label: 'Macro', parent: 'macro', level: 2 },
    { id: 'macro-historique-120-ans', label: 'Historique 120 ans', parent: 'macro', level: 2 },
    { id: 'parametres', label: 'Paramètres', parent: null, level: 1 },
    { id: 'compte-settings', label: 'Profil', parent: 'parametres', level: 2 },
    { id: 'configuration', label: 'Configuration', parent: 'parametres', level: 2 },
    { id: 'apparence', label: 'Apparence', parent: 'parametres', level: 2 },
    { id: 'valeurs-par-defaut', label: 'Valeurs par défaut', parent: 'parametres', level: 2 },
    { id: 'api', label: 'API', parent: 'parametres', level: 2 },
    { id: 'oliver', label: 'Oliver', parent: 'parametres', level: 2 },
    { id: 'import-export', label: 'Import/Export', parent: 'parametres', level: 2 },
    { id: 'utilisateurs', label: 'Utilisateurs', parent: 'parametres', level: 2 },
    { id: 'configuration-admin', label: 'Paramètres généraux', parent: 'parametres', level: 2 },
    { id: 'administration', label: 'Administration', parent: 'parametres', level: 2 },
    { id: 'statuts', label: 'Statuts', parent: 'parametres', level: 2 },
    { id: 'logs', label: 'Logs', parent: 'parametres', level: 2 }
  ], []);

  const tabsHierarchy = useMemo(() => {
    const tabs = STATIC_TABS;
    
    // Trier les onglets selon l'ordre de la sidebar
    const sortedTabs = [];
    
    // Séparer les onglets par niveau
    const level1Tabs = tabs.filter(tab => tab.level === 1);
    const level2Tabs = tabs.filter(tab => tab.level === 2);
    
    // Ordre niveau 1 : d'abord ceux dans sidebarOrder, puis tout onglet niveau 1 non listé
    const knownIds = new Set(sidebarOrder.level1);
    const sortedLevel1 = [
      ...sidebarOrder.level1.map(id => level1Tabs.find(tab => tab.id === id)).filter(Boolean),
      ...level1Tabs.filter(tab => !knownIds.has(tab.id))
    ];
    
    // Ajouter les onglets niveau 1 triés
    sortedLevel1.forEach(parent => {
      sortedTabs.push(parent);
      
      // Trier et ajouter les enfants de ce parent
      if (parent.children && parent.children.length > 0) {
        // Utiliser l'ordre défini pour ce parent, ou l'ordre alphabétique par défaut
        const childOrder = sidebarOrder[parent.id] || parent.children;
        const sortedChildren = childOrder
          .map(id => level2Tabs.find(tab => tab.id === id && tab.parent === parent.id))
          .filter(Boolean);
        sortedTabs.push(...sortedChildren);
      } else {
        // Si pas de children array, chercher par parent.id
        const children = level2Tabs
          .filter(tab => tab.parent === parent.id)
          .sort((a, b) => a.label.localeCompare(b.label));
        sortedTabs.push(...children);
      }
    });
    
    return sortedTabs;
  }, [STATIC_TABS, sidebarOrder]);

  // Liste de tous les IDs d'onglets (défaut = accès à tout)
  const allTabIds = useMemo(() => tabsHierarchy.map(t => t.id), [tabsHierarchy]);

  // Fonction pour obtenir les onglets sélectionnés selon le contexte (editingMember ou memberFormData)
  // null/undefined/vide = accès à tout (tous les onglets cochés)
  const getSelectedTabs = useCallback(() => {
    const raw = editingMember 
      ? (editingMember.allowed_tabs ?? null)
      : (memberFormData.allowed_tabs ?? null);
    if (!raw || (Array.isArray(raw) && raw.length === 0)) return allTabIds;
    return raw;
  }, [editingMember, memberFormData.allowed_tabs, allTabIds]);

  // Fonction pour mettre à jour les onglets sélectionnés
  const setSelectedTabs = useCallback((newTabs) => {
    if (editingMember) {
      setEditingMember(prev => ({ ...prev, allowed_tabs: newTabs }));
    } else {
      setMemberFormData(prev => ({ ...prev, allowed_tabs: newTabs }));
    }
  }, [editingMember]);

  // Vérifier si un parent est complètement coché (tous les enfants sont cochés)
  const isParentChecked = useCallback((parentId) => {
    const parent = tabsHierarchy.find(t => t.id === parentId);
    if (!parent || !parent.children) {
      // Onglet sans enfants : vérifier directement
      return getSelectedTabs().includes(parentId);
    }
    // Onglet avec enfants : vérifier que tous les enfants sont cochés
    return parent.children.every(childId => getSelectedTabs().includes(childId));
  }, [tabsHierarchy, getSelectedTabs]);

  // Vérifier si un parent est partiellement coché (état indéterminé)
  const isParentIndeterminate = useCallback((parentId) => {
    const parent = tabsHierarchy.find(t => t.id === parentId);
    if (!parent || !parent.children) return false;
    const selectedTabs = getSelectedTabs();
    const checkedChildren = parent.children.filter(childId => selectedTabs.includes(childId));
    return checkedChildren.length > 0 && checkedChildren.length < parent.children.length;
  }, [tabsHierarchy, getSelectedTabs]);

  // Toggle simple pour les onglets sans enfants
  // DOIT être défini AVANT handleParentChange et handleChildChange qui l'utilisent
  const handleMemberTabToggle = useCallback((tabValue) => {
    const selectedTabs = getSelectedTabs();
    const newTabs = selectedTabs.includes(tabValue)
      ? selectedTabs.filter(t => t !== tabValue)
      : [...selectedTabs, tabValue];
    setSelectedTabs(newTabs);
  }, [getSelectedTabs, setSelectedTabs]);

  // Gérer le changement d'un parent (cascade vers les enfants)
  const handleParentChange = useCallback((parentId) => {
    const parent = tabsHierarchy.find(t => t.id === parentId);
    if (!parent || !parent.children) {
      // Onglet sans enfants : toggle simple
      handleMemberTabToggle(parentId);
      return;
    }

    const selectedTabs = getSelectedTabs();
    const isChecked = isParentChecked(parentId);

    let newTabs;
    if (!isChecked) {
      // Cocher le parent et tous les enfants
      newTabs = [...new Set([...selectedTabs, parentId, ...parent.children])];
    } else {
      // Décocher le parent et tous les enfants
      newTabs = selectedTabs.filter(id => 
        id !== parentId && !parent.children.includes(id)
      );
    }

    setSelectedTabs(newTabs);
  }, [tabsHierarchy, getSelectedTabs, isParentChecked, setSelectedTabs, handleMemberTabToggle]);

  // Gérer le changement d'un enfant (mettre à jour le parent si nécessaire)
  const handleChildChange = useCallback((childId) => {
    const child = tabsHierarchy.find(t => t.id === childId);
    if (!child || !child.parent) {
      // Pas un enfant, toggle simple
      handleMemberTabToggle(childId);
      return;
    }

    const selectedTabs = getSelectedTabs();
    const isChecked = selectedTabs.includes(childId);
    let newTabs;

    if (isChecked) {
      // Décocher l'enfant
      newTabs = selectedTabs.filter(id => id !== childId);
    } else {
      // Cocher l'enfant
      newTabs = [...selectedTabs, childId];
    }

    // Mettre à jour le statut du parent
    const parent = tabsHierarchy.find(t => t.id === child.parent);
    if (parent && parent.children) {
      const checkedChildren = parent.children.filter(c => newTabs.includes(c));
      if (checkedChildren.length > 0 && !newTabs.includes(parent.id)) {
        // Au moins un enfant est coché : cocher le parent
        newTabs.push(parent.id);
      } else if (checkedChildren.length === 0 && newTabs.includes(parent.id)) {
        // Aucun enfant n'est coché : décocher le parent
        newTabs = newTabs.filter(id => id !== parent.id);
      }
    }

    setSelectedTabs(newTabs);
  }, [tabsHierarchy, getSelectedTabs, setSelectedTabs, handleMemberTabToggle]);

  // Composant pour afficher une checkbox d'onglet avec gestion de l'état indéterminé
  const TabCheckbox = React.memo(({ 
    tab, 
    isChecked, 
    isIndeterminate, 
    isParent, 
    isChild, 
    isParentChecked,
    onParentChange, 
    onChildChange, 
    onSimpleToggle 
  }) => {
    const checkboxRef = React.useRef(null);
    
    React.useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = isIndeterminate;
      }
    }, [isIndeterminate]);
    
    const handleChange = (e) => {
      // Empêcher la propagation pour éviter les doubles déclenchements
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      
      if (isParent) {
        if (onParentChange) {
          onParentChange(tab.id);
        }
      } else if (isChild) {
        if (onChildChange) {
          onChildChange(tab.id);
        }
      } else {
        if (onSimpleToggle) {
          onSimpleToggle(tab.id);
        }
      }
    };
    
    return (
      <label 
        className={`onglets-checkbox-label level-${tab.level}`}
        style={{ 
          display: 'flex', 
          flexDirection: 'row',
          alignItems: 'center', 
          justifyContent: 'flex-start',
          cursor: 'pointer',
          gap: '8px',
          width: '100%',
          paddingLeft: tab.level === 1 ? '12px' : '32px',
          paddingRight: '12px',
          boxSizing: 'border-box'
        }}
        onClick={(e) => {
          // Si le clic est sur le label ou le span, déclencher le toggle
          // Ne pas empêcher le comportement par défaut si c'est sur la checkbox
          if (e.target === e.currentTarget || e.target.tagName === 'SPAN') {
            e.preventDefault();
            // Simuler un clic sur la checkbox
            if (checkboxRef.current) {
              checkboxRef.current.click();
            }
          }
        }}
      >
        <input
          ref={checkboxRef}
          type="checkbox"
          name="allowed_tabs[]"
          value={tab.id}
          id={`tab-${tab.id}`}
          checked={isParent ? isParentChecked : isChecked}
          onChange={handleChange}
          style={{ 
            margin: 0,
            width: 'auto',
            flexShrink: 0,
            cursor: 'pointer',
            minWidth: '16px',
            maxWidth: '20px',
            pointerEvents: 'auto'
          }}
        />
        <span style={{ 
          textAlign: 'left',
          flex: '1 1 auto',
          width: 'auto',
          overflow: 'visible',
          whiteSpace: 'nowrap',
          fontSize: tab.level === 2 ? '0.95em' : '1em',
          color: tab.level === 2 ? '#555' : 'inherit'
        }}>
          {tab.label}
        </span>
      </label>
    );
  });

  // Liste des labels pour compatibilité (utilisée ailleurs dans le code)
  // Note: tabsHierarchy est défini plus haut, avant les fonctions qui l'utilisent
  const availableTabLabels = tabsHierarchy.map(tab => tab.label);

  // Map pour un accès rapide aux labels d'abonnements (O(1) au lieu de O(n))
  const subscriptionLabelMap = useMemo(() => {
    const map = new Map();
    subscriptions.forEach(sub => {
      map.set(sub.code, sub.name);
    });
    return map;
  }, [subscriptions]);

  // Fonction pour obtenir le label d'abonnement depuis la valeur
  const getSubscriptionLabel = useCallback((tier) => {
    if (!tier) return '-';
    return subscriptionLabelMap.get(tier) || tier;
  }, [subscriptionLabelMap]);

  // Fonction générique de tri
  const handleSort = useCallback((tableName, columnKey) => {
    setSortConfig(prev => {
      const currentSort = prev[tableName];
      let newDirection = 'asc';
      
      if (currentSort.column === columnKey) {
        // Inverser la direction si on clique sur la même colonne
        newDirection = currentSort.direction === 'asc' ? 'desc' : 'asc';
      }
      
      return {
        ...prev,
        [tableName]: { column: columnKey, direction: newDirection }
      };
    });
  }, []);

  // Données triées pour les membres
  const sortedMembers = useMemo(() => {
    const sort = sortConfig.members;
    if (!sort.column || members.length === 0) return members;

    return [...members].sort((a, b) => {
      let aValue, bValue;
      
      switch (sort.column) {
        case 'email': 
          aValue = a.user?.email || '';
          bValue = b.user?.email || '';
          break;
        case 'name': 
          aValue = `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim();
          bValue = `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.trim();
          break;
        case 'role': 
          aValue = a.role || '';
          bValue = b.role || '';
          break;
        case 'is_owner': 
          aValue = a.is_owner ? 'Oui' : 'Non';
          bValue = b.is_owner ? 'Oui' : 'Non';
          break;
        case 'is_active': 
          aValue = a.user?.is_active ? 'Actif' : 'Inactif';
          bValue = b.user?.is_active ? 'Actif' : 'Inactif';
          break;
        default: 
          aValue = a[sort.column] ?? '';
          bValue = b[sort.column] ?? '';
      }

      // Gérer les valeurs null/undefined
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convertir en nombres si possible
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

      if (isNumeric) {
        return sort.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Tri alphabétique
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      return sort.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [members, sortConfig.members]);

  // Données triées pour les organisations
  const sortedOrganizations = useMemo(() => {
    const sort = sortConfig.organizations;
    if (!sort.column || organizations.length === 0) {
      return organizations;
    }

    const result = [...organizations].sort((a, b) => {
      let aValue, bValue;
      
      switch (sort.column) {
        case 'created_at': 
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
        case 'is_active': 
          aValue = a.is_active ? 'Active' : 'Inactive';
          bValue = b.is_active ? 'Active' : 'Inactive';
          break;
        case 'subscription_tier': 
          aValue = a.subscription_tier ? (subscriptionLabelMap.get(a.subscription_tier) || a.subscription_tier) : '-';
          bValue = b.subscription_tier ? (subscriptionLabelMap.get(b.subscription_tier) || b.subscription_tier) : '-';
          break;
        default: 
          aValue = a[sort.column] ?? '';
          bValue = b[sort.column] ?? '';
      }

      // Gérer les valeurs null/undefined
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convertir en nombres si possible
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

      if (isNumeric) {
        return sort.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Tri alphabétique
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      return sort.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
    return result;
  }, [organizations, sortConfig.organizations, subscriptionLabelMap]);

  // Données triées pour les abonnements
  const sortedSubscriptions = useMemo(() => {
    const sort = sortConfig.subscriptions;
    if (!sort.column || subscriptions.length === 0) {
      return subscriptions;
    }

    const result = [...subscriptions].sort((a, b) => {
      let aValue, bValue;
      
      switch (sort.column) {
        case 'price': 
          aValue = parseFloat(a.price) || 0;
          bValue = parseFloat(b.price) || 0;
          return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
        case 'is_active': 
          aValue = a.is_active ? 'Actif' : 'Inactif';
          bValue = b.is_active ? 'Actif' : 'Inactif';
          break;
        default: 
          aValue = a[sort.column] ?? '';
          bValue = b[sort.column] ?? '';
      }

      // Gérer les valeurs null/undefined
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convertir en nombres si possible
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

      if (isNumeric) {
        return sort.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Tri alphabétique
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      return sort.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
    return result;
  }, [subscriptions, sortConfig.subscriptions]);

  // Fonction helper pour rendre un en-tête de colonne triable
  const renderSortableHeader = (tableName, columnKey, label) => {
    const sort = sortConfig[tableName];
    const isSorted = sort.column === columnKey;
    const direction = isSorted ? sort.direction : null;

    return (
      <th
        onClick={() => handleSort(tableName, columnKey)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: isSorted ? 'rgb(240, 240, 240)' : undefined,
          position: 'relative',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!isSorted) {
            e.currentTarget.style.backgroundColor = 'rgb(226, 226, 228)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSorted) {
            e.currentTarget.style.backgroundColor = '';
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span>{label}</span>
          {isSorted && (
            <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {direction === 'asc' ? (
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="rgb(81, 81, 84)">
                  <path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="rgb(81, 81, 84)">
                  <path d="M480-528 296-344l-56-56 240-240 240 240-56 56-184-184Z"/>
                </svg>
              )}
            </span>
          )}
        </div>
      </th>
    );
  };

  // Handlers pour les abonnements
  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await subscriptionsAPI.create(subscriptionFormData);
      const newSub = response.data;
      
      // Fermer la modal IMMÉDIATEMENT
      setShowSubscriptionForm(false);
      setSubscriptionFormData({
        code: '',
        name: '',
        description: '',
        price: 0,
        currency: 'EUR',
        is_active: true
      });
      
      // Mise à jour optimiste : ajouter le nouvel abonnement immédiatement
      if (newSub) {
        setSubscriptions(prev => [...prev, newSub]);
      }
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      subsLoadedRef.current = false;
        loadSubscriptions(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('L\'abonnement a été créé avec succès.', 'success', 3000, 'Création d\'abonnement', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la création';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    if (!editingSubscription) return;
    setLoading(true);
    try {
      const response = await subscriptionsAPI.update(editingSubscription.id, subscriptionFormData);
      const updatedSub = response.data;
      
      // Fermer la modal IMMÉDIATEMENT
      setShowSubscriptionForm(false);
      setEditingSubscription(null);
      setSubscriptionFormData({
        code: '',
        name: '',
        description: '',
        price: 0,
        currency: 'EUR',
        is_active: true
      });
      
      // Mise à jour optimiste : mettre à jour l'abonnement immédiatement
      if (updatedSub) {
        setSubscriptions(prev => prev.map(sub => sub.id === updatedSub.id ? updatedSub : sub));
      }
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      subsLoadedRef.current = false;
        loadSubscriptions(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('L\'abonnement a été mis à jour avec succès.', 'success', 3000, 'Mise à jour d\'abonnement', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la mise à jour';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscription = useCallback((sub) => {
    setEditingSubscription(sub);
    setSubscriptionFormData({
      code: sub.code,
      name: sub.name,
      description: sub.description || '',
      price: parseFloat(sub.price),
      currency: sub.currency || 'EUR',
      is_active: sub.is_active
    });
    setShowSubscriptionForm(true);
  }, []);

  const handleToggleSubscriptionStatus = useCallback((sub) => {
    setTogglingSubscription(sub);
  }, []);

  const confirmToggleSubscriptionStatus = async () => {
    if (!togglingSubscription) return;
    
    const subId = togglingSubscription.id;
    const newStatus = !togglingSubscription.is_active;
    
    setLoading(true);
    try {
      await subscriptionsAPI.update(subId, { ...togglingSubscription, is_active: newStatus });
      
      // Fermer la modal IMMÉDIATEMENT
      setTogglingSubscription(null);
      
      // Mise à jour optimiste : mettre à jour le statut immédiatement
      setSubscriptions(prev => prev.map(sub => 
        sub.id === subId ? { ...sub, is_active: newStatus } : sub
      ));
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      subsLoadedRef.current = false;
        loadSubscriptions(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast(`L'abonnement a été ${newStatus ? 'activé' : 'désactivé'} avec succès.`, 'success', 3000, 'Modification d\'abonnement', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la mise à jour';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubscription = useCallback((sub) => {
    setDeletingSubscription(sub);
  }, []);

  const confirmDeleteSubscription = async () => {
    if (!deletingSubscription) return;
    
    const subIdToDelete = deletingSubscription.id;
    
    setLoading(true);
    try {
      await subscriptionsAPI.delete(subIdToDelete);
      
      // Fermer la modal IMMÉDIATEMENT
      setDeletingSubscription(null);
      
      // Mise à jour optimiste : retirer l'abonnement immédiatement
      setSubscriptions(prev => prev.filter(sub => sub.id !== subIdToDelete));
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      setTimeout(() => {
      subsLoadedRef.current = false;
        loadSubscriptions(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('L\'abonnement a été supprimé avec succès.', 'success', 3000, 'Suppression d\'abonnement', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la suppression';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoading(false);
    }
  };

  // Fonctions pour les catégories - STABLE avec sessionStorage
  const isLoadingCatsRef = useRef(false);
  const loadCategories = useCallback(async (silent = false) => {
    // Ne pas bloquer si déjà en cours de chargement (utiliser ref pour éviter les boucles)
    if (isLoadingCatsRef.current) return;
    
    isLoadingCatsRef.current = true;
    setLoadingCategories(true);
    try {
      const response = await categoriesAPI.list();
      const cats = Array.isArray(response.data) ? response.data : [];
      setCategories(cats);
      categoriesRef.current = cats;
      catsLoadedRef.current = true;
      // Persister dans sessionStorage pour éviter la perte lors des remontages
      try {
        sessionStorage.setItem('parametres_categories', JSON.stringify(cats));
        sessionStorage.setItem('parametres_categories_timestamp', Date.now().toString());
      } catch (e) {
        // Ignorer les erreurs de sessionStorage
      }
    } catch (err) {
      // Ne pas vider les données existantes en cas d'erreur (garder les données en cache)
      setCategories(prev => {
        // Si on a déjà des données, les garder
        if (prev && prev.length > 0) {
          return prev;
        }
        // Sinon, retourner un tableau vide seulement si on n'avait rien
        return [];
      });
      // Ne pas afficher d'erreur si c'est un rechargement silencieux en arrière-plan
      if (!silent) {
        let errorMsg = 'Erreur lors du chargement des catégories';
        if (err.response) {
          if (err.response.status === 404) {
            errorMsg = 'Route API non trouvée. Vérifiez que le backend est démarré et que la route /api/categories existe.';
          } else {
            errorMsg = err.response?.data?.detail || `Erreur HTTP ${err.response.status}`;
          }
        } else if (err.request) {
          errorMsg = 'Le serveur ne répond pas. Vérifiez que le backend est démarré.';
        }
        console.error('❌ [Parametres] Erreur lors du chargement des catégories:', err);
        showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      }
      // Ne pas mettre catsLoadedRef.current à true en cas d'erreur pour permettre un nouvel essai
      catsLoadedRef.current = false; // Réinitialiser pour permettre un nouvel essai
    } finally {
      isLoadingCatsRef.current = false;
      setLoadingCategories(false);
    }
  }, [showToast]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setCategoryFormErrors({});
    
    const errors = {};
    if (!categoryFormData.name || !categoryFormData.name.trim()) {
      errors.name = 'Veuillez renseigner le nom de la catégorie.';
    }
    
    if (Object.keys(errors).length > 0) {
      setCategoryFormErrors(errors);
      return;
    }
    
    setLoadingCategories(true);
    try {
      const response = await categoriesAPI.create({
        name: categoryFormData.name.trim(),
        is_fragile: categoryFormData.is_fragile,
        target_percentage: categoryFormData.target_percentage && categoryFormData.target_percentage !== '' ? parseFloat(categoryFormData.target_percentage) : null,
        color: categoryFormData.color && categoryFormData.color !== '' ? categoryFormData.color : null
      });
      const newCategory = response.data;
      
      setShowCategoryForm(false);
      setCategoryFormData({ name: '', is_fragile: false, target_percentage: null, color: '#000000' });
      setCategoryFormErrors({});
      
      if (newCategory) {
        setCategories(prev => [...prev, newCategory]);
      }
      
      setTimeout(() => {
        catsLoadedRef.current = false;
        loadCategories(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
      
      showToast('La catégorie a été créée avec succès.', 'success', 3000, 'Création de catégorie', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      console.error('Erreur lors de la création de catégorie:', err);
      let errorMsg = 'Erreur lors de la création';
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMsg = 'Route API non trouvée. Vérifiez que le backend est démarré et que la route /api/categories existe.';
        } else if (err.response.status === 500) {
          errorMsg = 'Erreur serveur. Vérifiez les logs du backend.';
        } else {
          errorMsg = err.response?.data?.detail || err.response?.data?.message || `Erreur HTTP ${err.response.status}`;
        }
      } else if (err.request) {
        errorMsg = 'Le serveur ne répond pas. Vérifiez que le backend est démarré.';
      } else {
        errorMsg = err.message || 'Erreur lors de la création';
      }
      
      setCategoryFormErrors({ general: errorMsg });
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory) return;
    setCategoryFormErrors({});
    
    const errors = {};
    if (!categoryFormData.name || !categoryFormData.name.trim()) {
      errors.name = 'Veuillez renseigner le nom de la catégorie.';
    }
    
    if (Object.keys(errors).length > 0) {
      setCategoryFormErrors(errors);
      return;
    }
    
    setLoadingCategories(true);
    try {
      // Préparer les données à envoyer
      const updateData = {
        name: categoryFormData.name.trim(),
        is_fragile: categoryFormData.is_fragile
      };
      
      // Ajouter target_percentage seulement s'il est défini
      if (categoryFormData.target_percentage !== null && categoryFormData.target_percentage !== undefined && categoryFormData.target_percentage !== '') {
        updateData.target_percentage = typeof categoryFormData.target_percentage === 'string' 
          ? parseFloat(categoryFormData.target_percentage) 
          : categoryFormData.target_percentage;
      } else {
        updateData.target_percentage = null;
      }
      
      // Ajouter color seulement s'il est défini
      if (categoryFormData.color && categoryFormData.color !== '') {
        updateData.color = categoryFormData.color;
      } else {
        updateData.color = null;
      }
      
      const response = await categoriesAPI.update(editingCategory.id, updateData);
      const updatedCategory = response.data;
      // Fermer la modal IMMÉDIATEMENT
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryFormData({ name: '', is_fragile: false, target_percentage: null, color: '#000000' });
      setCategoryFormErrors({});
      
      // Mise à jour optimiste : mettre à jour la catégorie immédiatement
      if (updatedCategory) {
        setCategories(prev => {
          const updated = prev.map(cat => 
            cat.id === updatedCategory.id ? { ...updatedCategory } : cat
          );
          // Mettre à jour aussi le ref et sessionStorage
          categoriesRef.current = updated;
          try {
            sessionStorage.setItem('parametres_categories', JSON.stringify(updated));
            sessionStorage.setItem('parametres_categories_timestamp', Date.now().toString());
          } catch (e) {
            // Ignorer les erreurs de sessionStorage
          }
          return updated;
        });
      }
      
      // Recharger en arrière-plan pour garantir la cohérence (sans bloquer l'UI)
      // Mais seulement après un court délai pour laisser le temps à React de mettre à jour l'UI
      setTimeout(() => {
        catsLoadedRef.current = false;
        loadCategories(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 1000);
      
      showToast('La catégorie a été mise à jour avec succès.', 'success', 3000, 'Mise à jour de catégorie', 'Paramètres', getPageIcon('Paramètres'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur lors de la mise à jour';
      setCategoryFormErrors({ general: errorMsg });
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleDeleteCategory = useCallback((category) => {
    setDeletingCategory(category);
  }, []);

  const confirmDeleteCategory = async () => {
    if (!deletingCategory) return;
    
    const categoryIdToDelete = deletingCategory.id;
    
    setLoadingCategories(true);
    try {
      await categoriesAPI.delete(categoryIdToDelete);
      showToast('La catégorie a été supprimée avec succès.', 'success', 3000, 'Suppression de catégorie', 'Paramètres', getPageIcon('Paramètres'));
      
      setDeletingCategory(null);
      
      setCategories(prev => prev.filter(cat => cat.id !== categoryIdToDelete));
      
      setTimeout(() => {
        catsLoadedRef.current = false;
        loadCategories(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la suppression';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      setDeletingCategory(null);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleToggleCategoryStatus = useCallback((category) => {
    setTogglingCategory(category);
  }, []);

  const confirmToggleCategoryStatus = async () => {
    if (!togglingCategory) return;
    
    const categoryId = togglingCategory.id;
    const newStatus = !togglingCategory.is_active;
    
    setLoadingCategories(true);
    try {
      await categoriesAPI.toggleStatus(categoryId);
      
      setTogglingCategory(null);
      
      setCategories(prev => prev.map(cat => 
        cat.id === categoryId ? { ...cat, is_active: newStatus } : cat
      ));
      
      showToast(`La catégorie a été ${newStatus ? 'activée' : 'désactivée'} avec succès.`, 'success', 3000, 'Modification de catégorie', 'Paramètres', getPageIcon('Paramètres'));
      
      setTimeout(() => {
        catsLoadedRef.current = false;
        loadCategories(true).catch(() => {
          // Erreur silencieuse lors du rechargement - les données restent affichées
        });
      }, 500);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de la modification';
      showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
      setTogglingCategory(null);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      is_fragile: category.is_fragile,
      target_percentage: category.target_percentage || null,
      color: category.color || '#000000'
    });
    setCategoryFormErrors({});
    setShowCategoryForm(true);
  };

  const handleOpenCategoryForm = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', is_fragile: false, target_percentage: null, color: '#000000' });
    setCategoryFormErrors({});
    setShowCategoryForm(true);
  };

  const handleCloseCategoryForm = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
    setCategoryFormData({ name: '', is_fragile: false, target_percentage: null, color: '#000000' });
    setCategoryFormErrors({});
  };


  // Fonction pour charger les paramètres système
  const loadSettings = useCallback(async () => {
    const t0 = performance.now();
    try {
      setLoadingSettings(true);
      // Utiliser organization_id depuis le profil pour les codes API
      const organizationId = profile?.organization_id || 1;
      const userId = profile?.id || profile?.user_id;
      const response = await settingsAPI.list(organizationId, userId);
      const params = response.data || [];
      
      // Récupérer les valeurs des paramètres
      params.forEach(param => {
        if (param.param_name === 'fiscal_year_end_date') {
          // Format: MM-DD (ex: "06-30" pour 30 juin)
          if (param.param_value) {
            const [month, day] = param.param_value.split('-');
            if (month && day) {
              setFiscalYearEndMonth(String(parseInt(month) || 12));
              setFiscalYearEndDay(String(parseInt(day) || 31));
            }
          }
        } else if (param.param_name === 'taux_imposition') {
          const rate = parseFloat(param.param_value) || 25.0;
          setTaxRate(String(rate));
        } else if (param.param_name === 'reference_currency') {
          setReferenceCurrency(param.param_value || 'EUR');
        } else if (param.param_name === 'session_timeout_minutes') {
          // Charger depuis la base de données et synchroniser avec localStorage et AuthContext
          const minutes = parseInt(param.param_value, 10);
          if (isNaN(minutes) || minutes <= 0) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('⏱️ [Session Timeout] Valeur invalide depuis la DB:', param.param_value, '- utilisation de 30 minutes par défaut');
            }
            const defaultMinutes = 30;
            setSessionTimeoutInput(String(defaultMinutes));
            setSessionTimeoutMinutes(defaultMinutes);
          } else {
            setSessionTimeoutInput(String(minutes));
            setSessionTimeoutMinutes(minutes);
          }
        } else if (param.param_name === 'claude_api_key') {
          setClaudeApiKey(param.param_value || '');
        } else if (param.param_name === 'grok_api_key') {
          setGrokApiKey(param.param_value || '');
        } else if (param.param_name === 'chatgpt_api_key') {
          setChatgptApiKey(param.param_value || '');
        } else if (param.param_name === 'gemini_api_key') {
          setGeminiApiKey(param.param_value || '');
        } else if (param.param_name === 'yahoo_finance_api_key') {
          setYahooFinanceApiKey(param.param_value || '');
        } else if (param.param_name === 'financial_modeling_prep_api_key') {
          setFinancialModelingPrepApiKey(param.param_value || '');
        } else if (param.param_name === 'alpha_vantage_api_key') {
          setAlphaVantageApiKey(param.param_value || '');
        } else if (param.param_name === 'ibkr_api_key') {
          setIbkrApiKey(param.param_value || '');
        } else if (param.param_name === 'x_api_key') {
          setXApiKey(param.param_value || '');
        } else if (param.param_name === 'user_avatar') {
          setUserAvatar(param.param_value || null);
        } else if (param.param_name === 'sidebar_default_open') {
          const value = param.param_value === 'true' || param.param_value === true;
          setSidebarDefaultOpen(value);
          try {
            localStorage.setItem('sidebarDefaultOpen', String(value));
          } catch (e) {
            // Ignorer les erreurs de localStorage
          }
        } else if (param.param_name === 'dark_theme') {
          const value = param.param_value === 'true' || param.param_value === true;
          setDarkTheme(value);
          try {
            localStorage.setItem('darkTheme', String(value));
            // Appliquer le thème immédiatement
            if (value) {
              document.documentElement.classList.add('theme-dark');
            } else {
              document.documentElement.classList.remove('theme-dark');
            }
            const metaTheme = document.querySelector('meta[name="theme-color"]');
            if (metaTheme) metaTheme.setAttribute('content', value ? '#1a1a1a' : '#ffffff');
            window.dispatchEvent(new CustomEvent('themeUpdated'));
          } catch (e) {
            // Ignorer les erreurs de localStorage
          }
        } else if (param.param_name === 'accent_color') {
          const value = (param.param_value && /^#[0-9A-Fa-f]{6}$/.test(param.param_value)) ? param.param_value : '#1A4B8F';
          setAccentColor(value);
          try {
            localStorage.setItem('accentColor', value);
            document.documentElement.style.setProperty('--accent-color', value);
          } catch (e) {
            // Ignorer les erreurs de localStorage
          }
        } else if (param.param_name === 'notification_min_level') {
          const val = parseInt(param.param_value, 10);
          const level = [1, 2, 3].includes(val) ? val : 3;
          setNotificationMinLevel(level);
          try {
            localStorage.setItem('notification_min_level', String(level));
          } catch (e) {
            // Ignorer
          }
        } else if (param.param_name === 'sidebar_logo') {
          const value = param.param_value || null;
          setSidebarLogo(value);
          try {
            if (value) {
              localStorage.setItem('sidebarLogo', value);
            } else {
              localStorage.removeItem('sidebarLogo');
            }
            window.dispatchEvent(new CustomEvent('logoUpdated', { detail: value }));
          } catch (e) {
            // Ignorer les erreurs de localStorage
          }
        } else if (param.param_name === 'oliver_base_prompt') {
          setOliverBasePrompt(param.param_value || '');
        } else if (param.param_name === 'oliver_default_provider') {
          const v = (param.param_value || 'claude').toLowerCase();
          setOliverDefaultProvider(['claude', 'openai', 'gemini', 'grok'].includes(v) ? v : 'claude');
        } else if (param.param_name === 'oliver_display_name') {
          const v = (param.param_value || 'Oliver').trim() || 'Oliver';
          setOliverDisplayName(v);
          setOliverName(v);
        } else if (param.param_name === 'sidebar_mobile_items') {
          try {
            const arr = param.param_value ? JSON.parse(param.param_value) : null;
            const ids = Array.isArray(arr) ? arr : null;
            setSidebarMobileItemIds(ids);
            if (ids !== null) {
              try { localStorage.setItem('sidebar_mobile_items', JSON.stringify(ids)); } catch (e) { /* ignore */ }
            } else {
              try { localStorage.removeItem('sidebar_mobile_items'); } catch (e) { /* ignore */ }
            }
            window.dispatchEvent(new CustomEvent('sidebarMobileItemsUpdated', { detail: ids }));
          } catch (e) {
            setSidebarMobileItemIds(null);
          }
        } else if (param.param_name === 'sidebar_desktop_items') {
          try {
            const arr = param.param_value ? JSON.parse(param.param_value) : null;
            const ids = Array.isArray(arr) ? arr : null;
            setSidebarDesktopItemIds(ids);
            if (ids !== null) {
              try { localStorage.setItem('sidebar_desktop_items', JSON.stringify(ids)); } catch (e) { /* ignore */ }
            } else {
              try { localStorage.removeItem('sidebar_desktop_items'); } catch (e) { /* ignore */ }
            }
            window.dispatchEvent(new CustomEvent('sidebarDesktopItemsUpdated', { detail: ids }));
          } catch (e) {
            setSidebarDesktopItemIds(null);
          }
        }
      });
    } catch (error) {
      // Ne logger que les erreurs importantes (pas les timeouts répétés)
      const now = Date.now();
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('temps')) {
        // Logger seulement une fois toutes les 10 secondes pour éviter le spam
        if (!loadSettingsLastErrorTime.current || (now - loadSettingsLastErrorTime.current) > 10000) {
          console.warn('⚠️ [Parametres] Timeout lors du chargement des paramètres. Le backend peut être lent ou indisponible.');
          loadSettingsLastErrorTime.current = now;
        }
      } else if (error.response) {
        // Ne pas logger les erreurs 404 (paramètres optionnels)
        if (error.response.status !== 404) {
          console.warn(`⚠️ [Parametres] Erreur HTTP ${error.response.status} lors du chargement des paramètres:`, error.response.data);
        }
      } else if (error.request) {
        if (!loadSettingsLastErrorTime.current || (now - loadSettingsLastErrorTime.current) > 10000) {
          console.warn('⚠️ [Parametres] Pas de réponse du serveur. Vérifiez que le backend est démarré.');
          loadSettingsLastErrorTime.current = now;
        }
      }
    } finally {
      setLoadingSettings(false);
    }
  }, [setSessionTimeoutMinutes, profile?.id, profile?.organization_id]);

  // Charger les paramètres quand l'onglet Configuration, API, Apparence ou Compte est actif
  useEffect(() => {
    const configurationTabIndex = 0; // Configuration est le premier onglet
    const apiTabIndex = 3; // API est le quatrième onglet (index 3)
    const apparenceTabIndex = 1; // Apparence est le deuxième onglet (index 1)
    const compteTabIndex = 5; // Compte est le sixième onglet (index 5)
    if (activeTab === configurationTabIndex || activeTab === apiTabIndex || activeTab === apparenceTabIndex || activeTab === compteTabIndex) {
      loadSettings();
    }
  }, [activeTab, loadSettings]);

  // PHASE 1: Polling optimisé - vérifier seulement quand l'onglet "Statuts" est actif
  // Note: Le calcul de statutsTabIndex se fait après le calcul de tabs (voir plus bas)

  // Charger la configuration IB Gateway UNIQUEMENT quand l'onglet API devient actif (éviter les chargements multiples)
  useEffect(() => {
    if (activeTab === 3 && !ibGatewayConfigLoadedRef.current && !loadingIbGatewayConfig) {
      // Marquer comme en cours de chargement
      ibGatewayConfigLoadedRef.current = true;
      
      const loadIbGatewayConfig = async () => {
        try {
          setLoadingIbGatewayConfig(true);
          const response = await ibGatewayConfigAPI.get();
          
          if (response.data) {
            // Vérifier que le username ne ressemble pas à un texte chiffré (erreur de décryptage)
            let username = response.data.username || '';
            if (username && username.startsWith('gAAAAA')) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ [Parametres] Le username semble être du texte chiffré, réinitialisation');
              }
              username = '';
            }
            
            const newConfig = {
              host: response.data.host || '127.0.0.1',
              port: response.data.port || 4002,
              username: username,
              password: response.data.has_password ? '********' : '', // Afficher des astérisques si un mot de passe existe
              client_id: response.data.client_id || 1,
              is_active: response.data.is_active !== undefined ? response.data.is_active : true,
              has_password: response.data.has_password || false  // Conserver l'info si un mot de passe existe
            };
            
            setIbGatewayConfig(newConfig);
          }
        } catch (error) {
          if (error.response?.status !== 404 && process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [Parametres] Erreur lors du chargement de la configuration IB Gateway:', error);
          }
          // 404 est normal si la configuration n'existe pas encore
        } finally {
          setLoadingIbGatewayConfig(false);
        }
      };
      
      loadIbGatewayConfig();
    } else if (activeTab !== 3) {
      // Réinitialiser le flag quand on quitte l'onglet API pour permettre un nouveau chargement
      ibGatewayConfigLoadedRef.current = false;
    }
  }, [activeTab, loadingIbGatewayConfig]);

  // Fonction pour obtenir le nombre maximum de jours pour un mois donné
  const getMaxDayForMonth = useCallback((month) => {
    const monthNum = typeof month === 'string' ? parseInt(month) : month;
    const monthInt = monthNum || 1;
    if (monthInt === 2) return 29; // Février (29 pour gérer les années bissextiles)
    if ([4, 6, 9, 11].includes(monthInt)) return 30; // Avril, juin, septembre, novembre
    return 31; // Janvier, mars, mai, juillet, août, octobre, décembre
  }, []);

  // Fonction pour sauvegarder un paramètre et vérifier l'enregistrement
  const saveParameter = useCallback(async (paramName, paramValue, showSuccessToast = true) => {
    // Utiliser organization_id pour les codes API et paramètres de configuration, user_id pour les autres paramètres
    const organizationId = profile?.organization_id || 1;
    const userId = profile?.id || profile?.user_id;
    
    // Déterminer si c'est un code API ou un paramètre de configuration organisationnel
    const organizationParams = [
      // Codes API
      'claude_api_key', 'grok_api_key', 'chatgpt_api_key', 'gemini_api_key',
      'yahoo_finance_api_key', 'financial_modeling_prep_api_key', 
      'alpha_vantage_api_key', 'ibkr_api_key', 'x_api_key',
      // Paramètres de configuration organisationnels (les autres sont liés à user_id)
      'sidebar_logo', 'oliver_base_prompt', 'oliver_default_provider', 'oliver_display_name'
    ];
    const isOrganizationParam = organizationParams.includes(paramName);
    
    try {
      // Vérifier que organization_id est défini pour les paramètres organisationnels
      if (isOrganizationParam && !organizationId) {
        const errorMsg = `Impossible de sauvegarder ${paramName} : organisation non identifiée. Veuillez vous reconnecter.`;
        console.error('🔴 [saveParameter]', errorMsg, { paramName, isOrganizationParam, organizationId, userId, profile: profile?.organization_id });
        showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
        return;
      }
      
      // Vérifier que user_id est défini pour les paramètres non-organisationnels
      if (!isOrganizationParam && !userId) {
        const errorMsg = `Impossible de sauvegarder ${paramName} : utilisateur non identifié. Veuillez vous reconnecter.`;
        console.error('🔴 [saveParameter]', errorMsg, { paramName, isOrganizationParam, organizationId, userId, profile: { id: profile?.id, user_id: profile?.user_id, organization_id: profile?.organization_id } });
        showToast(errorMsg, 'error', 3000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
        return;
      }
      
      // Sauvegarder le paramètre et récupérer la réponse directement
      const response = await settingsAPI.update(
        paramName, 
        paramValue, 
        isOrganizationParam ? organizationId : null,  // organization_id pour les codes API et configuration
        isOrganizationParam ? null : userId  // user_id pour les autres paramètres
      );
      const savedValue = response.data?.param_value;
      
      // Ne pas comparer pour : user_avatar (base64 long), clés API (chiffrées côté backend, déchiffrement peut différer)
      const isAvatar = paramName === 'user_avatar';
      const isApiKeyParam = [
        'claude_api_key', 'grok_api_key', 'chatgpt_api_key', 'gemini_api_key',
        'yahoo_finance_api_key', 'financial_modeling_prep_api_key',
        'alpha_vantage_api_key', 'ibkr_api_key', 'x_api_key'
      ].includes(paramName);
      const isDeletion = !paramValue || paramValue === '';
      if (isAvatar || isApiKeyParam || isDeletion || savedValue === paramValue || String(savedValue) === String(paramValue)) {
        if (showSuccessToast) {
          // Messages personnalisés selon le paramètre
          let message = 'Paramètre sauvegardé';
          if (paramName === 'fiscal_year_end_date') {
            const [month, day] = paramValue.split('-');
            message = `Date de clôture d'exercice sauvegardée : ${day}/${month}`;
          } else if (paramName === 'taux_imposition') {
            message = `Taux d'imposition sauvegardé : ${paramValue}%`;
          } else if (paramName === 'reference_currency') {
            const currencyLabel = currencies.find(c => c.value === paramValue)?.label || paramValue;
            message = `Devise de référence sauvegardée : ${currencyLabel}`;
          } else if (paramName === 'session_timeout_minutes') {
            message = `Durée d'inactivité sauvegardée : ${paramValue} minutes`;
          } else if (paramName === 'claude_api_key') {
            message = `Clé API Claude sauvegardée`;
          } else if (paramName === 'grok_api_key') {
            message = `Clé API Grok sauvegardée`;
          } else if (paramName === 'chatgpt_api_key') {
            message = `Clé API ChatGPT sauvegardée`;
          } else if (paramName === 'gemini_api_key') {
            message = `Clé API Gemini sauvegardée`;
          } else if (paramName === 'yahoo_finance_api_key') {
            message = `Clé API Yahoo Finance sauvegardée`;
          } else if (paramName === 'financial_modeling_prep_api_key') {
            message = `Clé API Financial Modeling Prep sauvegardée`;
          } else if (paramName === 'alpha_vantage_api_key') {
            message = `Clé API Alpha Vantage sauvegardée`;
          } else if (paramName === 'ibkr_api_key') {
            message = `Clé API IBKR sauvegardée`;
          } else if (paramName === 'x_api_key') {
            message = `Clé API X (Twitter) sauvegardée`;
          } else if (paramName === 'user_avatar') {
            message = paramValue ? `Avatar sauvegardé` : `Avatar supprimé`;
          } else if (paramName === 'sidebar_logo') {
            message = paramValue ? `Logo sauvegardé` : `Logo supprimé`;
          } else if (paramName === 'notification_min_level') {
            message = `Niveau de notifications mis à jour`;
          } else if (paramName === 'sidebar_default_open') {
            message = paramValue === 'true' || paramValue === true ? `Sidebar ouverte par défaut` : `Sidebar fermée par défaut`;
          } else if (paramName === 'oliver_default_provider') {
            const labels = { claude: 'Claude', openai: 'GPT', gemini: 'Gemini', grok: 'Grok' };
            message = `IA par défaut : ${labels[paramValue] || paramValue}`;
          } else if (paramName === 'dark_theme') {
            message = paramValue === 'true' || paramValue === true ? `Thème sombre activé` : `Thème clair activé`;
          }
          // Format standard : showToast(message, type, duration, title, pageName, pageIcon)
          showToast(message, 'success', 3000, 'Paramètre sauvegardé', 'Paramètres', getPageIcon('Paramètres'));
        }
      } else {
        // La valeur sauvegardée ne correspond pas (sauf pour user_avatar où on ne compare pas)
        if (!isAvatar) {
          console.warn(`Valeur sauvegardée (${String(savedValue)?.substring(0, 50)}...) ne correspond pas à la valeur envoyée (${String(paramValue)?.substring(0, 50)}...)`);
          showToast('Paramètre sauvegardé mais valeur différente détectée. Vérifiez la valeur enregistrée.', 'warning', 3000, 'Avertissement', 'Paramètres', getPageIcon('Paramètres'));
        }
      }
    } catch (error) {
      console.error(`❌ [saveParameter] Erreur lors de la sauvegarde de ${paramName}:`, error);
      // Pour user_avatar, logger plus de détails pour le débogage
      if (paramName === 'user_avatar') {
        console.error('🔴 [saveParameter] Détails de l\'erreur pour user_avatar:', {
          userId: userId,
          hasValue: !!paramValue,
          valueLength: paramValue?.length || 0,
          valuePreview: paramValue?.substring(0, 50) || 'null',
          errorMessage: error.message,
          errorResponse: error.response?.data,
          errorStatus: error.response?.status
        });
      }
      console.error('🔴 [saveParameter] Détails de l\'erreur:', {
        paramName,
        paramValue,
        isOrganizationParam,
        organizationId,
        userId,
        profile: {
          id: profile?.id,
          user_id: profile?.user_id,
          email: profile?.email
        },
        error: {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          code: error.code
        }
      });
      
      // Message d'erreur plus détaillé
      let errorMessage = `Erreur lors de la sauvegarde de ${paramName}`;
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('temps')) {
        errorMessage = `Timeout : Le serveur ne répond pas. Vérifiez que le backend est démarré.`;
      } else if (error.response) {
        // Erreur HTTP avec réponse
        const status = error.response.status;
        const detail = error.response.data?.detail || error.response.data?.message || 'Erreur inconnue';
        
        // Messages spécifiques selon le code d'erreur
        if (status === 400 && detail.includes('user_id est requis')) {
          errorMessage = `Impossible de sauvegarder ${paramName} : utilisateur non identifié. Veuillez vous reconnecter.`;
        } else if ((status === 503 || status === 500) && (detail || '').includes('ENCRYPTION_KEY')) {
          errorMessage = `Configuration serveur : ENCRYPTION_KEY manquante. Exécutez fix_encryption_key_production.ps1 puis réessayez. Si l'erreur revient après un déploiement, ré-exécutez le script.`;
        } else {
          errorMessage = `Erreur ${status} : ${detail}`;
        }
      } else if (error.request) {
        // Requête envoyée mais pas de réponse
        errorMessage = `Pas de réponse du serveur. Vérifiez que le backend est démarré.`;
      }
      
      showToast(errorMessage, 'error', 3000, 'Erreur de sauvegarde', 'Paramètres', getPageIcon('Paramètres'));
    }
  }, [showToast, currencies]);

  // Handlers mémorisés pour les champs de configuration (pour éviter les problèmes de closure)
  const handleSessionTimeoutChange = useCallback((val) => {
    const stringValue = val === null || val === undefined ? '' : String(val);
    setSessionTimeoutInput(stringValue);
  }, []);

  const handleFiscalYearEndDayChange = useCallback((val) => {
    const stringValue = val === null || val === undefined ? '' : String(val);
    setFiscalYearEndDay(stringValue);
  }, []);

  const handleFiscalYearEndMonthChange = useCallback((val) => {
    const stringValue = val === null || val === undefined ? '' : String(val);
    setFiscalYearEndMonth(stringValue);
  }, []);

  const handleTaxRateChange = useCallback((val) => {
    const stringValue = val === null || val === undefined ? '' : String(val);
    setTaxRate(stringValue);
  }, []);

  const canAccessParametresTab = useCallback((tabId) => {
    if (!profile) return true;
    if (tabId === 'logs') {
      return profile.is_superadmin || profile?.email === 'remicabrit@gmail.com';
    }
    if (profile.is_superadmin || profile.is_owner) return true;
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return true;
    return allowed.includes(tabId);
  }, [profile?.allowed_tabs, profile?.is_superadmin, profile?.is_owner]);

  // Mémoriser allTabs pour éviter les problèmes de closure
  const allTabs = useMemo(() => [
    {
      tabId: 'compte-settings',
      label: 'Profil',
      content: (
        <div className="parametres-tab-content">
          {profile && (
            <>
              <Title level={2}>Profil</Title>
              <div className="admin-section" style={{ marginTop: '1.5rem' }}>
                <div className="account-profile-header" style={{ marginBottom: 0 }}>
                  <div
                    className="account-profile-avatar-wrapper"
                    style={{ position: 'relative', flexShrink: 0 }}
                  >
                    <div className="account-profile-avatar">
                    {(() => {
                      const avatarUrl = userAvatar || profile.picture || null;
                      const name = profile.name || 'User';
                      const initials = name.split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                      
                      if (avatarUrl) {
                        return (
                          <>
                            <img 
                              src={avatarUrl.startsWith('data:') ? avatarUrl : `${avatarUrl}?t=${Date.now()}`} 
                              alt={name}
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                // Fallback si l'image ne charge pas
                                e.target.style.display = 'none';
                                const initialsDiv = e.target.nextSibling;
                                if (initialsDiv) {
                                  initialsDiv.style.display = 'flex';
                                }
                              }}
                            />
                            <div className="account-profile-avatar-initials" style={{ display: 'none' }}>
                              {initials}
                            </div>
                          </>
                        );
                      }
                      return (
                        <div className="account-profile-avatar-initials">
                          {initials}
                        </div>
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => setShowAvatarModal(true)}
                    className="account-profile-avatar-edit"
                    aria-label="Modifier l'avatar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#1f1f1f">
                      <path d="M200-200h40l439-439-40-40-439 439v40Zm-28 28v-80l527-528q4.39-3.83 9.7-5.91Q714-788 719.68-788q5.67 0 11 1.5Q736-785 741-780l39 39q5 5 6.5 10.41t1.5 10.82q0 5.77-1.91 11.07-1.92 5.31-6.09 9.7L252-172h-80Zm589-548-41-41 41 41Zm-102.35 61.35L639-679l40 40-20.35-19.65Z"/>
                    </svg>
                  </button>
                </div>
                <div className="account-profile-info">
                  <div className="account-profile-name-full">{profile.name || 'Utilisateur'}</div>
                  <div className="account-profile-email">{profile.email || ''}</div>
                </div>
              </div>
              <div className="parametres-set-password" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '0.5rem' }}>Définir un mot de passe (connexion email)</label>
                <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.75rem' }}>
                  Permet de se connecter avec votre email et ce mot de passe en plus de Google.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="password"
                    placeholder="Nouveau mot de passe (min. 8 caractères)"
                    value={myPassword}
                    onChange={(e) => setMyPassword(e.target.value)}
                    minLength={8}
                    style={{ flex: '1', minWidth: '200px', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #ccc' }}
                  />
                  <Button
                    variant="primary"
                    disabled={!myPassword || myPassword.length < 8 || savingPassword}
                    onClick={async () => {
                      setSavingPassword(true);
                      try {
                        await authAPI.setPassword(myPassword);
                        setMyPassword('');
                        showToast('Mot de passe enregistré. Vous pouvez maintenant vous connecter avec votre email et ce mot de passe.', 'success', 5000, 'Mot de passe défini', 'Paramètres', getPageIcon('Paramètres'));
                      } catch (err) {
                        const msg = err.response?.data?.detail || err.message || 'Erreur lors de l\'enregistrement';
                        showToast(msg, 'error', 5000, 'Erreur', 'Paramètres', getPageIcon('Paramètres'));
                      } finally {
                        setSavingPassword(false);
                      }
                    }}
                  >
                    {savingPassword ? 'Enregistrement...' : 'Définir le mot de passe'}
                  </Button>
                </div>
              </div>
              <Button onClick={logout} variant="secondary" className="account-logout-button" style={{ marginTop: '2rem' }}>
                Se déconnecter
              </Button>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      tabId: 'configuration',
      label: 'Configuration',
      content: (
        <div className="parametres-tab-content">
          <Title level={2}>Préférences utilisateur</Title>
          <div className="admin-section" style={{ marginTop: '1.5rem' }}>
            <div className="parametres-prefs-grid" style={{ marginBottom: '2rem' }}>
              <div className="form-group" style={{ marginBottom: '0' }}>
                <Selectbox
                  label="Niveau minimum de notifications affichées dans la cloche"
                  value={String(notificationMinLevel)}
                  onChange={async (value) => {
                    const level = parseInt(value, 10);
                    if ([1, 2, 3].includes(level)) {
                      setNotificationMinLevel(level);
                      try {
                        localStorage.setItem('notification_min_level', String(level));
                        window.dispatchEvent(new CustomEvent('notificationLevelUpdated'));
                      } catch (e) {
                        // Ignorer
                      }
                      await saveParameter('notification_min_level', String(level), true);
                    }
                  }}
                  options={[
                    { value: '1', label: 'Priorité haute uniquement (erreurs)' },
                    { value: '2', label: 'Priorité haute et moyenne (erreurs + succès)' },
                    { value: '3', label: 'Toutes les notifications' }
                  ]}
                  placeholder={null}
                />
                <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                  Filtre les notifications affichées dans la cloche selon leur niveau de priorité
                </small>
              </div>
              <div className="form-group" style={{ marginBottom: '0' }}>
                <NumberInput
                  label="Durée d'inactivité avant déconnexion automatique (en minutes)"
                  min={1}
                  max={480}
                  step={1}
                  value={sessionTimeoutInput}
                  onChange={handleSessionTimeoutChange}
                  onBlur={async (e) => {
                    let num = e.target.value === '' ? 30 : parseFloat(e.target.value);
                    if (isNaN(num) || num < 1) num = 1;
                    if (num > 480) num = 480;
                    const clamped = Math.min(Math.max(num, 1), 480);
                    setSessionTimeoutInput(String(clamped));
                    setSessionTimeoutMinutes(clamped);
                    await saveParameter('session_timeout_minutes', String(clamped));
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
              <Title level={2} style={{ marginBottom: '1.5rem' }}>Paramètres fiscaux et financiers</Title>

              <div className="parametres-settings-grid parametres-fiscal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div className="form-group parametres-fiscal-date" style={{ gridColumn: '1 / -1' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Date de clôture d'exercice
                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <NumberInput
                        label="Jour"
                        min={1}
                      max={getMaxDayForMonth(parseInt(fiscalYearEndMonth) || 12)}
                      step={1}
                      value={fiscalYearEndDay}
                      onChange={handleFiscalYearEndDayChange}
                      onBlur={async (e) => {
                        const num = parseInt(e.target.value) || 1;
                        const maxDay = getMaxDayForMonth(parseInt(fiscalYearEndMonth) || 12);
                        const clamped = Math.min(Math.max(num, 1), maxDay);
                        setFiscalYearEndDay(String(clamped));
                        // Sauvegarder au blur et afficher un toast
                        const month = String(parseInt(fiscalYearEndMonth) || 12).padStart(2, '0');
                        const day = String(clamped).padStart(2, '0');
                        await saveParameter('fiscal_year_end_date', `${month}-${day}`);
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <NumberInput
                      label="Mois"
                      min={1}
                      max={12}
                      step={1}
                      value={fiscalYearEndMonth}
                      onChange={handleFiscalYearEndMonthChange}
                      onBlur={async (e) => {
                        const num = parseInt(e.target.value) || 1;
                        const clamped = Math.min(Math.max(num, 1), 12);
                        setFiscalYearEndMonth(String(clamped));
                        // Ajuster le jour si nécessaire
                        const maxDay = getMaxDayForMonth(clamped);
                        const currentDay = parseInt(fiscalYearEndDay) || 1;
                        const finalDay = Math.min(currentDay, maxDay);
                        setFiscalYearEndDay(String(finalDay));
                        // Sauvegarder au blur et afficher un toast
                        const month = String(clamped).padStart(2, '0');
                        const day = String(finalDay).padStart(2, '0');
                        await saveParameter('fiscal_year_end_date', `${month}-${day}`);
                      }}
                    />
                  </div>
                </div>
              </div>
                <div className="form-group">
                  <NumberInput
                    label="Taux d'imposition (%)"
                    min={0}
                    max={100}
                    step={0.1}
                    value={taxRate}
                    onChange={handleTaxRateChange}
                    onBlur={async (e) => {
                      const num = parseFloat(e.target.value) || 0;
                      const clamped = Math.min(Math.max(num, 0), 100);
                      setTaxRate(String(clamped));
                      // Sauvegarder et afficher un toast
                      await saveParameter('taux_imposition', String(clamped));
                    }}
                  />
                </div>

                <div className="form-group">
                  <Selectbox
                    label="Devise de référence"
                    value={referenceCurrency}
                    onChange={async (val) => {
                      setReferenceCurrency(val);
                      // Sauvegarder et afficher un toast
                      await saveParameter('reference_currency', val);
                    }}
                    options={currencies}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'apparence',
      label: 'Apparence',
      content: (
        <div className="parametres-tab-content parametres-apparence-section">
          <Title level={2}>Préférences d'affichage</Title>
          <div className="admin-section parametres-apparence-block">
              <div className="parametres-settings-grid parametres-apparence-grid">
                <div className="form-group toggle-group parametres-apparence-row">
                  <label htmlFor="sidebar-default-open" className="parametres-apparence-label-wrap">
                    <span className="parametres-apparence-label">Sidebar ouverte par défaut</span>
                    <div className="parametres-apparence-desc">La sidebar sera ouverte ou fermée au chargement de l'application</div>
                  </label>
                <label className="toggle-switch parametres-apparence-toggle">
                  <input
                    type="checkbox"
                    id="sidebar-default-open"
                    name="sidebarDefaultOpen"
                  checked={sidebarDefaultOpen}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setSidebarDefaultOpen(newValue);
                    try {
                      localStorage.setItem('sidebarDefaultOpen', String(newValue));
                    } catch (e) {
                      // Ignorer les erreurs de localStorage
                    }
                    // Vérifier que le profil est chargé avant de sauvegarder
                    if (!profile?.id && !profile?.user_id) {
                      console.warn('⚠️ [sidebar_default_open] Profil non chargé, sauvegarde différée');
                      // La sauvegarde sera effectuée lors du prochain chargement des paramètres
                      return;
                    }
                    await saveParameter('sidebar_default_open', String(newValue), true);
                  }}
                />
<span className="toggle-slider"></span>
                </label>
                </div>

                <div className="form-group toggle-group parametres-apparence-row">
                  <label htmlFor="dark-theme" className="parametres-apparence-label-wrap">
                    <span className="parametres-apparence-label">Thème sombre</span>
                    <div className="parametres-apparence-desc">Activez le thème sombre pour une meilleure expérience en faible luminosité</div>
                  </label>
                  <label className="toggle-switch parametres-apparence-toggle">
                <input
                  type="checkbox"
                  id="dark-theme"
                  name="darkTheme"
                  checked={darkTheme}
                  disabled={darkThemeAuto}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setDarkTheme(newValue);
                    try {
                      localStorage.setItem('darkTheme', String(newValue));
                      const effective = darkThemeAuto ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) : newValue;
                      if (effective) {
                        document.documentElement.classList.add('theme-dark');
                      } else {
                        document.documentElement.classList.remove('theme-dark');
                      }
                      const metaTheme = document.querySelector('meta[name="theme-color"]');
                      if (metaTheme) metaTheme.setAttribute('content', effective ? '#1a1a1a' : '#ffffff');
                      window.dispatchEvent(new CustomEvent('themeUpdated'));
                    } catch (e) {
                      // Ignorer les erreurs de localStorage
                    }
                    if (!profile?.id && !profile?.user_id) {
                      console.warn('⚠️ [dark_theme] Profil non chargé, sauvegarde différée');
                      return;
                    }
                    await saveParameter('dark_theme', String(newValue), true);
                  }}
                />
                <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="form-group toggle-group parametres-apparence-row">
                  <label htmlFor="dark-theme-auto" className="parametres-apparence-label-wrap">
                    <span className="parametres-apparence-label">Thème sombre automatique</span>
                    <div className="parametres-apparence-desc">Le thème suit le mode sombre du téléphone ou du système</div>
                  </label>
                  <label className="toggle-switch parametres-apparence-toggle">
                    <input
                      type="checkbox"
                      id="dark-theme-auto"
                      name="darkThemeAuto"
                      checked={darkThemeAuto}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setDarkThemeAuto(newValue);
                        try {
                          localStorage.setItem('darkThemeAuto', String(newValue));
                          const effective = newValue
                            ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)
                            : darkTheme;
                          if (effective) {
                            document.documentElement.classList.add('theme-dark');
                          } else {
                            document.documentElement.classList.remove('theme-dark');
                          }
                          const metaTheme = document.querySelector('meta[name="theme-color"]');
                          if (metaTheme) metaTheme.setAttribute('content', effective ? '#1a1a1a' : '#ffffff');
                          window.dispatchEvent(new CustomEvent('themeUpdated'));
                          showToast(
                            newValue ? 'Le thème suit désormais le mode sombre de votre appareil' : 'Le thème sombre automatique a été désactivé',
                            'success', 3000, 'Thème sombre automatique', 'Paramètres', getPageIcon('Paramètres')
                          );
                        } catch (err) {
                          // Ignorer
                        }
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {/* Personnalisation de la barre mobile : sélection des icônes affichées */}
                <div className="form-group parametres-apparence-mobile-block">
                  <span className="parametres-apparence-label">Barre de navigation mobile</span>
                  <div className="parametres-apparence-desc parametres-apparence-mobile-desc">
                    Choisissez les icônes affichées dans la sidebar mobile. Si aucune icône n'est sélectionnée, toutes les entrées autorisées sont affichées.
                  </div>
                  <div className="parametres-mobile-bar-checkboxes">
                    {(appConfig.sidebar?.items || []).map((item) => {
                      const id = item.id;
                      const label = item.id === 'ai' ? (oliverName || 'Oliver') : item.label;
                      const isChecked = sidebarMobileItemIds === null || sidebarMobileItemIds.includes(id);
                      return (
                        <label key={id} className="parametres-mobile-bar-item">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={async () => {
                              const nextIds = sidebarMobileItemIds === null
                                ? (appConfig.sidebar?.items || []).map((i) => i.id).filter((x) => x !== id)
                                : isChecked
                                  ? sidebarMobileItemIds.filter((x) => x !== id)
                                  : [...sidebarMobileItemIds, id];
                              const toSave = nextIds.length === 0 ? null : nextIds;
                              setSidebarMobileItemIds(toSave);
                              try {
                                if (toSave === null) localStorage.removeItem('sidebar_mobile_items');
                                else localStorage.setItem('sidebar_mobile_items', JSON.stringify(toSave));
                                window.dispatchEvent(new CustomEvent('sidebarMobileItemsUpdated', { detail: toSave }));
                              } catch (e) { /* ignore */ }
                              if (profile?.id || profile?.user_id) {
                                await saveParameter('sidebar_mobile_items', toSave === null ? '' : JSON.stringify(toSave), true);
                              }
                              showToast(
                                toSave === null ? 'Toutes les icônes seront affichées sur mobile' : 'Préférence mobile enregistrée',
                                'success', 2500, 'Barre mobile', 'Paramètres', getPageIcon('Paramètres')
                              );
                            }}
                          />
                          <span className="parametres-mobile-bar-item-label">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Personnalisation de la sidebar desktop (ouverte et fermée) : sélection des icônes affichées */}
                <div className="form-group parametres-apparence-mobile-block">
                  <span className="parametres-apparence-label">Barre de navigation desktop</span>
                  <div className="parametres-apparence-desc parametres-apparence-mobile-desc">
                    Choisissez les icônes affichées dans la sidebar desktop (ouverte ou fermée). Si aucune icône n'est sélectionnée, toutes les entrées autorisées sont affichées.
                  </div>
                  <div className="parametres-mobile-bar-checkboxes">
                    {(appConfig.sidebar?.items || []).map((item) => {
                      const id = item.id;
                      const label = item.id === 'ai' ? (oliverName || 'Oliver') : item.label;
                      const isChecked = sidebarDesktopItemIds === null || sidebarDesktopItemIds.includes(id);
                      return (
                        <label key={`desktop-${id}`} className="parametres-mobile-bar-item">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={async () => {
                              const nextIds = sidebarDesktopItemIds === null
                                ? (appConfig.sidebar?.items || []).map((i) => i.id).filter((x) => x !== id)
                                : isChecked
                                  ? sidebarDesktopItemIds.filter((x) => x !== id)
                                  : [...sidebarDesktopItemIds, id];
                              const toSave = nextIds.length === 0 ? null : nextIds;
                              setSidebarDesktopItemIds(toSave);
                              try {
                                if (toSave === null) localStorage.removeItem('sidebar_desktop_items');
                                else localStorage.setItem('sidebar_desktop_items', JSON.stringify(toSave));
                                window.dispatchEvent(new CustomEvent('sidebarDesktopItemsUpdated', { detail: toSave }));
                              } catch (e) { /* ignore */ }
                              if (profile?.id || profile?.user_id) {
                                await saveParameter('sidebar_desktop_items', toSave === null ? '' : JSON.stringify(toSave), true);
                              }
                              showToast(
                                toSave === null ? 'Toutes les icônes seront affichées sur desktop' : 'Préférence desktop enregistrée',
                                'success', 2500, 'Sidebar desktop', 'Paramètres', getPageIcon('Paramètres')
                              );
                            }}
                          />
                          <span className="parametres-mobile-bar-item-label">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
        </div>
      )
    },
    {
      tabId: 'valeurs-par-defaut',
      label: 'Valeurs par défaut',
      content: (
        <div className="parametres-tab-content">
          <div className="admin-section">
            <div className="admin-header">
              <Title level={2}>Catégories</Title>
              <Button
                variant="secondary"
                onClick={handleOpenCategoryForm}
                style={{ minWidth: '100px' }}
              >
                Ajouter
              </Button>
            </div>

            <div className="organizations-list">
              {loadingCategories && categories.length === 0 ? (
                <Spinner text="Chargement des catégories..." size="medium" />
              ) : categories.length === 0 ? (
                <p>Aucune catégorie créée</p>
              ) : (
                <Table
                  defaultSortColumn="target_percentage"
                  defaultSortDirection="asc"
                  columns={[
                    { key: 'name', label: 'Nom', align: 'left' },
                    { 
                      key: 'is_fragile', 
                      label: 'Type', 
                      align: 'left',
                      render: (value) => (
                        <span style={{ 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px',
                          backgroundColor: value ? 'var(--surface-elevated, rgba(255,243,205,0.5))' : 'var(--surface-elevated, rgba(209,236,241,0.5))',
                          color: value ? 'var(--text-primary)' : 'var(--text-primary)'
                        }}>
                          {value ? 'Fragile' : 'Anti-Fragile'}
                        </span>
                      )
                    },
                    {
                      key: 'is_active',
                      label: 'Statut',
                      align: 'left',
                      render: (value) => (
                        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
                          {value ? 'Actif' : 'Inactif'}
                        </span>
                      )
                    },
                    {
                      key: 'target_percentage',
                      label: '% cible',
                      align: 'left',
                      render: (value) => value !== null && value !== undefined ? `${value}%` : '-'
                    },
                    {
                      key: 'color',
                      label: 'Couleur',
                      align: 'left',
                      render: (value) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div 
                            style={{
                              width: '24px',
                              height: '24px',
                              backgroundColor: value || '#000000',
                              border: '1px solid #d0d4da',
                              borderRadius: '4px',
                              flexShrink: 0
                            }}
                            title={value || '#000000'}
                          />
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>
                            {value || '#000000'}
                          </span>
                        </div>
                      )
                    },
                  ]}
                  data={categories}
                  striped={true}
                  hover={true}
                  onRowClick={(category) => handleEditCategory(category)}
                />
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'api',
      label: 'API',
      content: (
        <div className="parametres-tab-content">
          <Title level={2}>IBKR - Configuration IB Gateway</Title>

          {/* Configuration IB Gateway */}
          <form onSubmit={(e) => { e.preventDefault(); }} className="admin-section" style={{ marginTop: '1.5rem' }}>
              {/* Première ligne : Host, Port et Client ID */}
              <div className="parametres-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Host (Adresse IP)
                  </label>
                  <input
                    type="text"
                    value={ibGatewayConfig.host || ''}
                    onChange={(e) => setIbGatewayConfig({ ...ibGatewayConfig, host: e.target.value })}
                    placeholder="127.0.0.1"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid rgb(237, 237, 237)',
                      borderRadius: '8px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
                
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Port
                  </label>
                  <input
                    type="number"
                    value={ibGatewayConfig.port || 4002}
                    onChange={(e) => setIbGatewayConfig({ ...ibGatewayConfig, port: parseInt(e.target.value) || 4002 })}
                    placeholder="4002"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid rgb(237, 237, 237)',
                      borderRadius: '8px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <small style={{ color: 'rgb(142, 142, 147)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    4002 pour IB Gateway, 7497 pour TWS
                  </small>
                </div>
                
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Client ID
                  </label>
                  <input
                    type="number"
                    value={ibGatewayConfig.client_id || 20}
                    onChange={(e) => setIbGatewayConfig({ ...ibGatewayConfig, client_id: parseInt(e.target.value) || 20 })}
                    placeholder="20"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid rgb(237, 237, 237)',
                      borderRadius: '8px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <small style={{ color: 'rgb(142, 142, 147)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    ID client pour la connexion API (par défaut: 20)
                  </small>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'flex-end' }}>
                <Button 
                  type="button"
                  onClick={async () => {
                    setSavingIbGatewayConfig(true);
                    try {
                      const existingConfig = await ibGatewayConfigAPI.get();
                      // Ne pas envoyer le mot de passe s'il contient uniquement des astérisques (non modifié)
                      const configToSave = { ...ibGatewayConfig };
                      if (configToSave.password === '********') {
                        // Si le password est '********', ne pas l'envoyer (garder l'ancien)
                        delete configToSave.password;
                      }
                      if (existingConfig.data) {
                        await ibGatewayConfigAPI.update(configToSave);
                        showToast('La configuration IB Gateway a été mise à jour avec succès.', 'success', 3000, 'Configuration IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
                      } else {
                        await ibGatewayConfigAPI.create(configToSave);
                        showToast('La configuration IB Gateway a été créée avec succès.', 'success', 3000, 'Configuration IB Gateway', 'Paramètres', getPageIcon('Paramètres'));
                      }
                    } catch (error) {
                      console.error('Erreur lors de la sauvegarde de la configuration IB Gateway:', error);
                      showToast('Erreur lors de la sauvegarde: ' + (error.response?.data?.detail || error.message), 'error', 3000, 'Erreur de sauvegarde', 'Paramètres', getPageIcon('Paramètres'));
                    } finally {
                      setSavingIbGatewayConfig(false);
                    }
                  }}
                  disabled={savingIbGatewayConfig}
                  variant="secondary"
                  style={{ flex: 1 }}
                >
                  {savingIbGatewayConfig ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
                {!isConnected ? (
                  <Button onClick={handleConnect} disabled={loading} variant="secondary" style={{ flex: 1 }}>
                    {loading ? 'Connexion...' : 'Se connecter'}
                  </Button>
                ) : (
                  <Button onClick={handleDisconnect} disabled={loading} variant="secondary" style={{ flex: 1 }}>
                    Se déconnecter
                  </Button>
                )}
              </div>
            </form>

          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
            <Title level={2}>IA</Title>
            <div className="admin-section" style={{ marginTop: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <Selectbox
                  label="IA par défaut"
                  value={oliverDefaultProvider}
                  onChange={async (val) => {
                    setOliverDefaultProvider(val);
                    await saveParameter('oliver_default_provider', val, true);
                  }}
                  options={[
                    { value: 'claude', label: 'Claude' },
                    { value: 'openai', label: 'GPT (ChatGPT)' },
                    { value: 'gemini', label: 'Gemini' },
                    { value: 'grok', label: 'Grok' }
                  ]}
                  placeholder={null}
                />
              </div>
              <form onSubmit={(e) => e.preventDefault()} className="parametres-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Claude
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={claudeApiKey}
                      onChange={(e) => setClaudeApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? claudeApiKey;
                        await saveParameter('claude_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API Claude"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          await settingsAPI.testKey('claude', profile?.organization_id || 1);
                          showToast('Clé Claude valide', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Clé invalide';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Grok
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={grokApiKey}
                      onChange={(e) => setGrokApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? grokApiKey;
                        await saveParameter('grok_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API Grok"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          await settingsAPI.testKey('grok', profile?.organization_id || 1);
                          showToast('Clé Grok valide', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Clé invalide';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    ChatGPT
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={chatgptApiKey}
                      onChange={(e) => setChatgptApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? chatgptApiKey;
                        await saveParameter('chatgpt_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API ChatGPT"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          await settingsAPI.testKey('chatgpt', profile?.organization_id || 1);
                          showToast('Clé ChatGPT valide', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Clé invalide';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Gemini
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={geminiApiKey}
                      onChange={(e) => setGeminiApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? geminiApiKey;
                        await saveParameter('gemini_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API Gemini"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          await settingsAPI.testKey('gemini', profile?.organization_id || 1);
                          showToast('Clé Gemini valide', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Clé invalide';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgb(237, 237, 237)' }}>
            <Title level={2}>Informations financières</Title>
            <div className="admin-section" style={{ marginTop: '1.5rem' }}>
              <form onSubmit={(e) => e.preventDefault()} className="parametres-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Yahoo Finance
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={yahooFinanceApiKey}
                      onChange={(e) => setYahooFinanceApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? yahooFinanceApiKey;
                        await saveParameter('yahoo_finance_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API Yahoo Finance"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => showToast('Test non disponible pour cette API.', 'info', 3000, 'Yahoo Finance', 'Paramètres', getPageIcon('Paramètres'))}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Financial Modeling Prep
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={financialModelingPrepApiKey}
                      onChange={(e) => setFinancialModelingPrepApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? financialModelingPrepApiKey;
                        await saveParameter('financial_modeling_prep_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API Financial Modeling Prep"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          await settingsAPI.testKey('financial_modeling_prep', profile?.organization_id || 1);
                          showToast('Clé Financial Modeling Prep valide', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Clé invalide';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Alpha Vantage
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={alphaVantageApiKey}
                      onChange={(e) => setAlphaVantageApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? alphaVantageApiKey;
                        await saveParameter('alpha_vantage_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API Alpha Vantage"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          await settingsAPI.testKey('alpha_vantage', profile?.organization_id || 1);
                          showToast('Clé Alpha Vantage valide', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Clé invalide';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    IBKR
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={ibkrApiKey}
                      onChange={(e) => setIbkrApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? ibkrApiKey;
                        await saveParameter('ibkr_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API IBKR"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => showToast('Test non disponible pour cette API.', 'info', 3000, 'IBKR', 'Paramètres', getPageIcon('Paramètres'))}
                    >
                      Tester
                    </Button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: 'rgb(29, 29, 31)', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    X (Twitter)
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="password"
                      value={xApiKey}
                      onChange={(e) => setXApiKey(e.target.value)}
                      onBlur={async (e) => {
                        const val = e.target?.value ?? xApiKey;
                        await saveParameter('x_api_key', val ?? '', true);
                      }}
                      placeholder="Clé API X - Bearer Token"
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid rgb(237, 237, 237)',
                        borderRadius: '8px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={async () => {
                        try {
                          const diag = await xFeedAPI.getDiagnostic();
                          if (diag.data?.api_key_valid && diag.data?.api_connection_ok) {
                            showToast('Clé API X valide et connexion OK', 'success', 3000, 'Test', 'Paramètres', getPageIcon('Paramètres'));
                          } else if (diag.data?.api_key_valid && diag.data?.accounts_configured) {
                            const issues = diag.data?.issues || [];
                            const has401 = issues.some(i => /401|invalide ou expirée/i.test(i));
                            const msg = has401
                              ? 'Clé API X rejetée (401). Utilisez le Bearer Token du portail X (Keys and tokens).'
                              : (issues[0] || 'Connexion ou quota en erreur');
                            showToast(msg, 'warning', 5000, 'Test X (Twitter)', 'Paramètres', getPageIcon('Paramètres'));
                          } else {
                            const issues = diag.data?.issues || [];
                            const has401 = issues.some(i => /401|invalide ou expirée/i.test(i));
                            const hasEnClair = issues.some(i => /en clair|chiffrer/i.test(i));
                            let msg = has401
                              ? 'Clé API X invalide ou expirée (401). Utilisez le Bearer Token (portail X > Keys and tokens), pas l\'API Key/Secret.'
                              : (issues[0] || 'Clé invalide ou non configurée');
                            if (hasEnClair) {
                              msg += ' Re-sauvegardez la clé ici pour la chiffrer.';
                            }
                            showToast(msg, 'error', 6000, 'Test X (Twitter)', 'Paramètres', getPageIcon('Paramètres'));
                          }
                        } catch (e) {
                          const msg = e.response?.data?.detail || e.message || 'Erreur lors du test';
                          showToast(msg, 'error', 5000, 'Test échoué', 'Paramètres', getPageIcon('Paramètres'));
                        }
                      }}
                    >
                      Tester
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'oliver',
      label: oliverName,
      content: (
        <div className="parametres-tab-content">
          <Title level={2}>Paramètres de {oliverName}</Title>
          <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary, #666)' }}>
            Définissez un prompt de base pour pré-conditionner l'assistant {oliverName} (ex. rôle et contraintes sur les données).
          </p>
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="parametres-admin-config-label">
              Nom d'affichage
            </label>
            <small>
              Ce nom remplace « Oliver » partout dans l'application (menu, notifications, chat).
            </small>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              <input
                type="text"
                value={oliverDisplayName}
                onChange={(e) => setOliverDisplayName(e.target.value)}
                placeholder="Oliver"
                maxLength={80}
                style={{ maxWidth: '280px', flex: '1 1 auto', minWidth: '120px' }}
              />
              <Button
                type="button"
                onClick={async () => {
                  setSavingOliverDisplayName(true);
                  try {
                    const value = (oliverDisplayName || '').trim() || 'Oliver';
                    await saveParameter('oliver_display_name', value, true);
                    setOliverName(value);
                    showToast(`Nom enregistré : ${value}`, 'success', 3000, oliverName, 'Paramètres', getPageIcon('Paramètres'));
                  } catch (e) {
                    showToast('Erreur lors de la sauvegarde.', 'error', 3000, oliverName, 'Paramètres', getPageIcon('Paramètres'));
                  } finally {
                    setSavingOliverDisplayName(false);
                  }
                }}
                disabled={savingOliverDisplayName}
                variant="secondary"
                size="small"
              >
                {savingOliverDisplayName ? 'Sauvegarde...' : 'Enregistrer le nom'}
              </Button>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="parametres-admin-config-label">
              Prompt de base
            </label>
            <textarea
              value={oliverBasePrompt}
              onChange={(e) => setOliverBasePrompt(e.target.value)}
              rows={6}
              placeholder={DEFAULT_OLIVER_PROMPT}
            />
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              <Button
                type="button"
                onClick={async () => {
                  setSavingOliverPrompt(true);
                  try {
                    await saveParameter('oliver_base_prompt', oliverBasePrompt, true);
                    showToast('Prompt Oliver sauvegardé.', 'success', 3000, 'Oliver', 'Paramètres', getPageIcon('Paramètres'));
                  } catch (e) {
                    showToast('Erreur lors de la sauvegarde.', 'error', 3000, 'Oliver', 'Paramètres', getPageIcon('Paramètres'));
                  } finally {
                    setSavingOliverPrompt(false);
                  }
                }}
                disabled={savingOliverPrompt}
                variant="secondary"
                size="medium"
              >
                {savingOliverPrompt ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setOliverBasePrompt(DEFAULT_OLIVER_PROMPT);
                  showToast('Prompt par défaut restauré. Cliquez sur Sauvegarder pour l\'enregistrer.', 'info', 3000, 'Oliver', 'Paramètres', getPageIcon('Paramètres'));
                }}
                disabled={savingOliverPrompt}
                variant="secondary"
                size="medium"
              >
                Prompt par défaut
              </Button>
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'import-export',
      label: 'Import/Export',
      content: (
        <div className="parametres-tab-content">
          <p>Import et export de données</p>
        </div>
      )
    },
    {
      tabId: 'utilisateurs',
      label: 'Utilisateurs',
      content: (
        <div className="parametres-tab-content">
          {(() => {
            // Vérifier si l'utilisateur est admin ou propriétaire dans son organisation
            const isAdmin = profile?.role === 'admin' || profile?.role === 'owner' || profile?.is_owner || profile?.is_superadmin;
            if (!isAdmin) {
              return (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  <p>Vous n'avez pas accès à cette section. Seuls les administrateurs et propriétaires peuvent gérer les utilisateurs.</p>
                </div>
              );
            }
            
            // Utiliser l'organisation de l'utilisateur connecté
            const userOrganizationId = profile?.organization_id;
            
            return (
              <div className="admin-section">
                <div className="admin-header">
                  <Title level={2}>Utilisateurs</Title>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    {profile?.is_superadmin ? (
                      // Superadmin peut sélectionner une organisation
                    <div style={{ minWidth: '250px' }}>
                      <Selectbox
                        label=""
                        options={organizationOptions}
                        value={currentOrgId || ''}
                        onChange={handleOrganizationChange}
                        placeholder="Sélectionner une société"
                      />
                    </div>
                    ) : (
                      // Admin normal voit uniquement son organisation
                      <div style={{ padding: '0.5rem 1rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                        <strong>{organizations.find(org => org.id === userOrganizationId)?.name || 'Votre organisation'}</strong>
                      </div>
                    )}
                {(profile?.is_superadmin ? currentOrgId : userOrganizationId) && canEdit && (
                  <Button
                    onClick={(e) => {
                      if (e) { e.preventDefault(); e.stopPropagation(); }
                      setEditingMemberRow(null);
                      handleOpenMemberForm(e, allTabIds);
                    }}
                    variant="secondary"
                    type="button"
                    style={{ minWidth: '100px' }}
                  >
                    Ajouter
                  </Button>
                )}
              </div>
            </div>

            {(profile?.is_superadmin ? currentOrgId : userOrganizationId) && (
              <>
                <div className="organizations-list">
                  {(() => {
                    // Le composant Table gère le tri automatiquement, donc on utilise directement members
                    const dataToDisplay = members || [];
                    
                    const isArray = Array.isArray(dataToDisplay);
                    const hasLength = dataToDisplay.length > 0;
                    const shouldShowEmpty = !isArray || !hasLength;
                    
                    if (loadingMembers) {
                      return <Spinner text="Chargement..." size="medium" />;
                    }
                    
                    if (shouldShowEmpty) {
                      return <p>Aucun utilisateur</p>;
                    }
                    
                    // Transformer les données pour le composant Table
                    const tableData = dataToDisplay.map(member => ({
                      id: member.id,
                      email: member.user?.email || '',
                      name: `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim(),
                      role: member.is_owner ? 'owner' : (member.role || 'viewer'),
                      is_owner: member.is_owner,
                      is_active: member.user?.is_active || false,
                      picture: member.user?.picture || null,
                      member: member // Garder la référence complète pour les actions
                    }));

                    return (
                      <div className="parametres-members-table">
                      <Table
                        defaultSortColumn="name"
                        defaultSortDirection="asc"
                        columns={[
                          {
                            key: 'avatar',
                            label: '',
                            align: 'left',
                            render: (_, row) => {
                              const name = row.name || row.email || 'U';
                              const initials = name.split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                              const avatarUrl = row.picture;
                              
                              return (
                                <div className="parametres-table-avatar" style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  backgroundColor: 'rgb(226, 226, 228)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  {avatarUrl ? (
                                    <img 
                                      src={avatarUrl.startsWith('data:') ? avatarUrl : `${avatarUrl}?t=${Date.now()}`}
                                      alt={name}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        const initialsDiv = e.target.nextSibling;
                                        if (initialsDiv) {
                                          initialsDiv.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: avatarUrl ? 'none' : 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: 'rgb(29, 29, 31)'
                                  }}>
                                    {initials}
                                  </div>
                                </div>
                              );
                            }
                          },
                          { key: 'email', label: 'Email', align: 'left' },
                          { key: 'name', label: 'Nom', align: 'left' },
                          {
                            key: 'role',
                            label: 'Rôle',
                            align: 'left',
                            render: (value) => {
                              const labels = {
                                viewer: 'Viewer',
                                editor: 'Editor',
                                admin: 'Admin',
                                owner: 'Propriétaire'
                              };
                              const styles = {
                                admin: { background: '#e3f2fd', color: '#1976d2' },
                                editor: { background: '#f3e5f5', color: '#7b1fa2' },
                                owner: { background: '#fff3e0', color: '#e65100' },
                                viewer: { background: '#f1f8e9', color: '#558b2f' }
                              };
                              return (
                                <span className="status-badge" style={styles[value] || styles.viewer}>
                                  {labels[value] || value}
                                </span>
                              );
                            }
                          },
                          {
                            key: 'is_active',
                            label: 'Statut',
                            align: 'left',
                            render: (value) => (
                              <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
                                {value ? 'Actif' : 'Inactif'}
                              </span>
                            )
                          },
                        ]}
                        data={tableData}
                        striped={true}
                        hover={true}
                        onRowClick={canEdit ? (row) => {
                          setEditingMember(row.member);
                          setEditingMemberRow(row);
                          setMemberFormErrors({});
                          setMemberFormData(prev => ({
                            ...prev,
                            avatar: row.member.user?.picture || null
                          }));
                          setShowMemberForm(true);
                        } : undefined}
                      />
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
            {!userOrganizationId && !currentOrgId && (
              <p style={{ color: '#666', marginTop: '2rem' }}>
                {profile?.is_superadmin 
                  ? 'Sélectionnez une société pour gérer ses utilisateurs'
                  : 'Vous n\'êtes pas associé à une organisation'}
              </p>
            )}
              </div>
            );
          })()}
        </div>
      )
    },
    {
      tabId: 'configuration-admin',
      label: 'Paramètres généraux',
      content: (
        <div className="parametres-tab-content">
          <Title level={2}>Paramètres généraux</Title>
          <div className="admin-section" style={{ marginTop: '1.5rem' }}>
            <div className="parametres-prefs-grid">
              <div className="form-group parametres-admin-config-row parametres-admin-config-align" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', padding: 0 }}>
                <label style={{ margin: 0, lineHeight: '24px', flex: 1, minWidth: 200 }}>
                  <div className="parametres-admin-config-label">Logo</div>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    accept="image/*"
                    id="sidebar-logo-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = async (event) => {
                          const base64 = event.target?.result;
                          if (base64) {
                            setSidebarLogo(base64);
                            try {
                              localStorage.setItem('sidebarLogo', base64);
                              window.dispatchEvent(new CustomEvent('logoUpdated', { detail: base64 }));
                            } catch (err) {
                              // Ignorer
                            }
                            await saveParameter('sidebar_logo', base64, true);
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => document.getElementById('sidebar-logo-file-input')?.click()}
                  >
                    Choisir un fichier
                  </Button>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={async () => {
                      setSidebarLogo(null);
                      try {
                        localStorage.removeItem('sidebarLogo');
                        window.dispatchEvent(new CustomEvent('logoUpdated', { detail: null }));
                      } catch (err) {
                        // Ignorer
                      }
                      await saveParameter('sidebar_logo', '', true);
                    }}
                    disabled={!sidebarLogo}
                  >
                    Supprimer le logo
                  </Button>
                </div>
              </div>
              <div className="form-group parametres-admin-config-row parametres-admin-config-align toggle-group" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', padding: 0 }}>
              <label style={{ margin: 0, lineHeight: '24px', flex: 1, minWidth: 200 }}>
                <div className="parametres-admin-config-label">Couleur principale</div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }} className="parametres-accent-color-row">
                <ColorPicker
                  value={accentColor}
                  onChange={async (newValue) => {
                    const color = (newValue && /^#[0-9A-Fa-f]{6}$/.test(newValue)) ? newValue : (newValue && newValue.trim() ? newValue : accentColor);
                    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
                      setAccentColor(color);
                      try {
                        localStorage.setItem('accentColor', color);
                        document.documentElement.style.setProperty('--accent-color', color);
                      } catch (e) {
                        // Ignorer les erreurs de localStorage
                      }
                      if (profile?.id || profile?.user_id) {
                        await saveParameter('accent_color', color, true);
                      }
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={async () => {
                    const defaultColor = '#1A4B8F';
                    setAccentColor(defaultColor);
                    try {
                      localStorage.setItem('accentColor', defaultColor);
                      document.documentElement.style.setProperty('--accent-color', defaultColor);
                    } catch (e) {
                      // Ignorer
                    }
                    if (profile?.id || profile?.user_id) {
                      await saveParameter('accent_color', defaultColor, true);
                    }
                  }}
                >
                  Réinitialiser
                </Button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'administration',
      label: 'Administration',
      content: (
        <div className="parametres-tab-content">
          <div className="admin-section">
            <div className="admin-header">
              <Title level={2}>Sociétés</Title>
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Réinitialiser les erreurs de formulaire
                  setFormErrors({});
                  setEditingOrg(null);
                  setFormData({
                    organization: { 
                      name: '', 
                      slug: '', 
                      siren: '',
                      address_line1: '',
                      address_line2: '',
                      city: '',
                      postal_code: '',
                      country: '',
                      subscription_tier: 'free',
                      is_active: true
                    },
                    super_admin: { email: '', password: '', first_name: '', last_name: '' }
                  });
                  setEditingOrg(null);
                  // Ouvrir la modal directement (pas besoin de setTimeout)
                  setShowCreateForm(true);
                }}
                variant="secondary"
                type="button"
                style={{ minWidth: '100px' }}
              >
                Ajouter
              </Button>
            </div>

            <div className="organizations-list">
              {(() => {
                // Utiliser organizations directement si sortedOrganizations est vide mais organizations ne l'est pas
                const dataToDisplay = (sortedOrganizations && sortedOrganizations.length > 0) 
                  ? sortedOrganizations 
                  : (organizations && organizations.length > 0) 
                    ? organizations 
                    : [];
                
                const isArray = Array.isArray(dataToDisplay);
                const hasLength = dataToDisplay.length > 0;
                const shouldShowEmpty = !isArray || !hasLength;
                
                if (loadingOrgs) {
                  return <Spinner text="Chargement..." size="medium" />;
                }
                
                if (shouldShowEmpty) {
                  return <p>Aucune société créée</p>;
                }
                
                return (
                <>
                  <Table
                    defaultSortColumn="id"
                    defaultSortDirection="asc"
                    columns={[
                      { key: 'id', label: 'ID', align: 'left' },
                      { key: 'name', label: 'Nom', align: 'left' },
                      { key: 'slug', label: 'Slug', align: 'left' },
                      { 
                        key: 'subscription_tier', 
                        label: 'Abonnement', 
                        align: 'left',
                        render: (value) => getSubscriptionLabel(value)
                      },
                      {
                        key: 'is_active',
                        label: 'Statut',
                        align: 'left',
                        render: (value) => (
                          <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
                            {value ? 'Active' : 'Inactive'}
                            </span>
                        )
                      },
                      {
                        key: 'created_at',
                        label: 'Créée le',
                        align: 'left',
                        render: (value) => new Date(value).toLocaleDateString('fr-FR')
                      }
                    ]}
                    data={dataToDisplay}
                    striped={true}
                    hover={true}
                    onRowClick={(org) => handleEditOrganization(org)}
                  />
                  
                  {/* Modal de confirmation de suppression (inline, sans bpm/Modal) */}
                  {deletingOrg && (
                    <div 
                      className="bpm-modal-backdrop"
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                      }}
                    >
                      <div className="bpm-modal bpm-modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="bpm-modal-header">
                          <h3 className="bpm-modal-title">Confirmer la suppression</h3>
                          <button
                            type="button"
                            className="bpm-modal-close"
                            aria-label="Fermer"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeletingOrg(null);
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <div className="bpm-modal-content">
                          <p>Êtes-vous sûr de vouloir supprimer la société <strong>{deletingOrg.name}</strong> ?</p>
                          <p style={{ color: '#d32f2f', fontSize: '0.875rem' }}>Cette action est irréversible.</p>
                          <div className="form-actions">
                            <Button variant="secondary" type="button" onClick={confirmDeleteOrganization} disabled={loading}>
                              {loading ? 'Suppression...' : 'Supprimer'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modal de confirmation de désactivation/activation */}
                  {togglingOrg && (
                    <div 
                      className="bpm-modal-backdrop"
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                      }}
                    >
                      <div className="bpm-modal bpm-modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="bpm-modal-header">
                          <h3 className="bpm-modal-title">
                            {togglingOrg.is_active ? 'Confirmer la désactivation' : 'Confirmer l\'activation'}
                          </h3>
                          <button
                            type="button"
                            className="bpm-modal-close"
                            aria-label="Fermer"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setTogglingOrg(null);
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <div className="bpm-modal-content">
                          <p>
                            Êtes-vous sûr de vouloir {togglingOrg.is_active ? 'désactiver' : 'activer'} la société <strong>{togglingOrg.name}</strong> ?
                          </p>
                          {togglingOrg.is_active && (
                            <p style={{ color: '#d32f2f', fontSize: '0.875rem' }}>
                              La société ne sera plus accessible une fois désactivée.
                            </p>
                          )}
                          <div className="form-actions">
                            <Button variant="secondary" type="button" onClick={confirmToggleOrganizationStatus} disabled={loading}>
                              {loading ? (togglingOrg.is_active ? 'Désactivation...' : 'Activation...') : (togglingOrg.is_active ? 'Désactiver' : 'Activer')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
                );
              })()}
            </div>
          </div>

          {/* Section Gestion des abonnements */}
          <div className="admin-section" style={{ marginTop: '3rem' }}>
            <div className="admin-header">
              <Title level={2}>Abonnements</Title>
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingSubscription(null);
                  setSubscriptionFormData({
                    code: '',
                    name: '',
                    description: '',
                    price: 0,
                    currency: 'EUR',
                    is_active: true
                  });
                  setShowSubscriptionForm(true);
                }}
                variant="secondary"
                type="button"
                style={{ minWidth: '100px' }}
              >
                Ajouter
              </Button>
            </div>

            <div className="organizations-list">
              {(() => {
                // Utiliser subscriptions directement si sortedSubscriptions est vide mais subscriptions ne l'est pas
                const dataToDisplay = (sortedSubscriptions && sortedSubscriptions.length > 0) 
                  ? sortedSubscriptions 
                  : (subscriptions && subscriptions.length > 0) 
                    ? subscriptions 
                    : [];
                
                const isArray = Array.isArray(dataToDisplay);
                const hasLength = dataToDisplay.length > 0;
                const shouldShowEmpty = !isArray || !hasLength;
                
                if (loadingSubscriptions) {
                  return <Spinner text="Chargement..." size="medium" />;
                }
                
                if (shouldShowEmpty) {
                  return <p>Aucun abonnement créé</p>;
                }
                
                return (
                  <Table
                    defaultSortColumn="price"
                    defaultSortDirection="asc"
                    columns={[
                      { key: 'code', label: 'Code', align: 'left' },
                      { key: 'name', label: 'Nom', align: 'left' },
                      { 
                        key: 'description', 
                        label: 'Description', 
                        align: 'left',
                        render: (value) => value || '-'
                      },
                      {
                        key: 'price',
                        label: 'Tarif',
                        align: 'left',
                        render: (value, sub) => `${parseFloat(value).toFixed(2)} ${sub.currency}`
                      },
                      {
                        key: 'is_active',
                        label: 'Statut',
                        align: 'left',
                        render: (value) => (
                          <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
                            {value ? 'Actif' : 'Inactif'}
                          </span>
                        )
                      }
                    ]}
                    data={dataToDisplay}
                    striped={true}
                    hover={true}
                    onRowClick={(sub) => handleEditSubscription(sub)}
                  />
                );
              })()}

              {/* Modal de confirmation de suppression d'abonnement */}
              {deletingSubscription && (
                <div 
                  className="bpm-modal-backdrop"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 99999,
                  }}
                >
                  <div className="bpm-modal bpm-modal-small" onClick={(e) => e.stopPropagation()}>
                    <div className="bpm-modal-header">
                      <h3 className="bpm-modal-title">Confirmer la suppression</h3>
                      <button
                        type="button"
                        className="bpm-modal-close"
                        aria-label="Fermer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeletingSubscription(null);
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="bpm-modal-content">
                      <p>Êtes-vous sûr de vouloir supprimer l'abonnement <strong>{deletingSubscription.name}</strong> ?</p>
                      <p style={{ color: 'var(--accent-color)', fontSize: '0.875rem' }}>Cette action est irréversible.</p>
                      <div className="form-actions">
                        <Button variant="secondary" type="button" onClick={confirmDeleteSubscription} disabled={loading}>
                          {loading ? 'Suppression...' : 'Supprimer'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de confirmation de désactivation/activation d'abonnement */}
              {togglingSubscription && (
                <div 
                  className="bpm-modal-backdrop"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 99999,
                  }}
                >
                  <div className="bpm-modal bpm-modal-small" onClick={(e) => e.stopPropagation()}>
                    <div className="bpm-modal-header">
                      <h3 className="bpm-modal-title">
                        {togglingSubscription.is_active ? 'Confirmer la désactivation' : 'Confirmer l\'activation'}
                      </h3>
                      <button
                        type="button"
                        className="bpm-modal-close"
                        aria-label="Fermer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTogglingSubscription(null);
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="bpm-modal-content">
                      <p>
                        Êtes-vous sûr de vouloir {togglingSubscription.is_active ? 'désactiver' : 'activer'} l'abonnement <strong>{togglingSubscription.name}</strong> ?
                      </p>
                      {togglingSubscription.is_active && (
                        <p style={{ color: '#d32f2f', fontSize: '0.875rem' }}>
                          L'abonnement ne sera plus disponible une fois désactivé.
                        </p>
                      )}
                      <div className="form-actions">
                        <Button variant="secondary" type="button" onClick={confirmToggleSubscriptionStatus} disabled={loading}>
                          {loading ? (togglingSubscription.is_active ? 'Désactivation...' : 'Activation...') : (togglingSubscription.is_active ? 'Désactiver' : 'Activer')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'statuts',
      label: 'Statuts',
      content: (
        <div className="parametres-tab-content parametres-statuts-tab">
          <Title level={2}>Statuts des services</Title>
          <div className="admin-section parametres-statuts-section" style={{ marginTop: '1.5rem' }}>
            <div className="status-boxes-grid">
              {/* Backend Status Box */}
              <Box
                title="Backend"
                headerExtra={
                  <Tooltip text="API FastAPI associée à PostgreSQL. Gère toutes les requêtes HTTP de l'application, l'authentification et l'accès aux données." position="bottom-end">
                    <span className="bpm-tooltip-trigger"><InfoIcon size={16} /></span>
                  </Tooltip>
                }
                isConnected={backendStatus.connected}
                error={backendStatus.error}
                details={
                  backendStatus.health ? (
                    <>
                      <div>Statut: {backendStatus.health.status === 'healthy' ? 'Opérationnel' : backendStatus.health.status === 'unhealthy' ? 'Défaillant' : backendStatus.health.status === 'degraded' ? 'Dégradé' : (backendStatus.health.status || 'N/A')}</div>
                      {backendStatus.health.database && (
                        <div>Base de données: {backendStatus.health.database}</div>
                      )}
                      {backendStatus.health.cache && (
                        <div>Cache: {backendStatus.health.cache}</div>
                      )}
                      {backendStatus.health.outbound !== undefined && (
                        <div>Connectivité externe: {backendStatus.health.outbound === 'ok' ? 'OK' : backendStatus.health.outbound}</div>
                      )}
                    </>
                  ) : null
                }
                lastCheck={backendStatus.lastCheck}
                actions={
                  <>
                    <Button
                      variant="secondary"
                      onClick={checkBackendStatus}
                      disabled={backendStatus.loading || restartingBackend}
                      style={{ minWidth: '120px' }}
                    >
                      {backendStatus.loading ? 'Vérification...' : 'Vérifier'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={restartBackend}
                      disabled={restartingBackend || backendStatus.loading}
                      style={{ minWidth: '120px' }}
                    >
                      {restartingBackend ? 'Redémarrage...' : 'Relancer'}
                    </Button>
                  </>
                }
              />

              {/* IB Gateway Status Box */}
              <Box
                title="IB Gateway"
                headerExtra={
                  <Tooltip text="Connexion au logiciel IB Gateway d'Interactive Brokers. Permet de récupérer les positions, le solde et les ordres du portefeuille en temps réel." position="bottom-end">
                    <span className="bpm-tooltip-trigger"><InfoIcon size={16} /></span>
                  </Tooltip>
                }
                isConnected={ibGatewayStatus.connected}
                error={ibGatewayStatus.error}
                details={
                  (ibGatewayStatus.status || ibGatewayStatus.lastError) ? (
                    <>
                      {ibGatewayStatus.status?.host && (
                        <div>Host: {ibGatewayStatus.status.host}:{ibGatewayStatus.status.port}</div>
                      )}
                      {ibGatewayStatus.status?.client_id !== undefined && (
                        <div>Client ID: {ibGatewayStatus.status.client_id}</div>
                      )}
                      {!ibGatewayStatus.connected && ibGatewayStatus.lastError && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.85em', color: 'var(--color-text-secondary, #666)' }}>
                          Dernière erreur : {(typeof ibGatewayStatus.lastError === 'string' ? ibGatewayStatus.lastError : '').split('\n')[0].trim()}
                        </div>
                      )}
                    </>
                  ) : null
                }
                lastCheck={ibGatewayStatus.lastCheck}
                actions={
                  <>
                    <Button
                      variant="secondary"
                      onClick={checkIbGatewayStatus}
                      disabled={ibGatewayStatus.loading}
                      style={{ minWidth: '120px' }}
                    >
                      {ibGatewayStatus.loading ? 'Vérification...' : 'Vérifier'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={fetchTwsDiagnostic}
                      style={{ minWidth: '100px' }}
                    >
                      Diagnostic
                    </Button>
                  </>
                }
              />

              {/* Daily Valuation Service Status Box */}
              <Box
                title="Sauvegarde Valorisation"
                headerExtra={
                  <Tooltip text="Service système planifié à 22h (heure française). Enregistre la valorisation du portefeuille en base pour l'historique et les calculs de performance." position="bottom-end">
                    <span className="bpm-tooltip-trigger"><InfoIcon size={16} /></span>
                  </Tooltip>
                }
                isConnected={dailyValuationStatus.connected}
                error={dailyValuationStatus.error}
                details={
                  dailyValuationStatus.lastValuation ? (
                    <>
                      <div>Dernière sauvegarde: {new Date(dailyValuationStatus.lastValuation.date).toLocaleDateString('fr-FR')}</div>
                      <div>Il y a {dailyValuationStatus.lastValuation.daysAgo} jour{dailyValuationStatus.lastValuation.daysAgo > 1 ? 's' : ''}</div>
                    </>
                  ) : null
                }
                lastCheck={dailyValuationStatus.lastCheck}
                actions={
                  <Button
                    variant="secondary"
                    onClick={checkDailyValuationStatus}
                    disabled={dailyValuationStatus.loading}
                    style={{ minWidth: '120px' }}
                  >
                    {dailyValuationStatus.loading ? 'Vérification...' : 'Vérifier'}
                  </Button>
                }
              />

              {/* Keep-Alive IBKR Status Box */}
              <Box
                title="Keep-Alive IBKR"
                headerExtra={
                  <Tooltip text="Envoie un ping toutes les 60 secondes à IB Gateway pour maintenir la session active. Évite la déconnexion automatique par inactivité." position="bottom-end">
                    <span className="bpm-tooltip-trigger"><InfoIcon size={16} /></span>
                  </Tooltip>
                }
                isConnected={keepAliveStatus.running}
                error={keepAliveStatus.error}
                details={
                  keepAliveStatus.stats ? (
                    <>
                      <div>Statut: {keepAliveStatus.stats.is_running ? 'Actif' : 'Arrêté'}</div>
                      <div>Intervalle: {keepAliveStatus.stats.interval}s</div>
                      <div>Pings: {keepAliveStatus.stats.ping_count} envoyés, {keepAliveStatus.stats.success_count} réussis</div>
                      {keepAliveStatus.stats.success_rate !== undefined && (
                        <div>Taux de succès: {(keepAliveStatus.stats.success_rate * 100).toFixed(1)}%</div>
                      )}
                      {keepAliveStatus.stats.last_ping && (
                        <div>Dernier ping: {new Date(keepAliveStatus.stats.last_ping).toLocaleTimeString('fr-FR')}</div>
                      )}
                    </>
                  ) : ibGatewayStatus.connected ? (
                    <div>Statistiques en attente (premier ping)</div>
                  ) : (
                    <div>Keep-alive IBKR inactif</div>
                  )
                }
                lastCheck={keepAliveStatus.lastCheck}
                actions={
                  <Button
                    variant="secondary"
                    onClick={checkIbGatewayStatus}
                    disabled={keepAliveStatus.loading}
                    style={{ minWidth: '120px' }}
                  >
                    {keepAliveStatus.loading ? 'Vérification...' : 'Vérifier'}
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      )
    },
    {
      tabId: 'logs',
      label: 'Logs',
      content: (
        <div className="parametres-tab-content parametres-logs-tab">
          <Title level={2}>Logs des services</Title>

          <div className="admin-section" style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
                <Selectbox
                  label="Service"
                  value={logsState.service}
                  onChange={(value) => setLogsState(prev => ({ ...prev, service: value }))}
                  options={[
                    { value: 'backend', label: 'Backend' },
                    { value: 'daily-valuation', label: 'Sauvegarde Valorisation' },
                    { value: 'nginx', label: 'Nginx' },
                    { value: 'performance-api', label: 'Performance' }
                  ]}
                />
              </div>
              {logsState.service !== 'performance-api' && (
              <div className="form-group" style={{ marginBottom: 0, minWidth: 120 }}>
                <Selectbox
                  label="Lignes"
                  value={String(logsState.lines)}
                  onChange={(value) => setLogsState(prev => ({ ...prev, lines: parseInt(value, 10) }))}
                  options={[
                    { value: '100', label: '100' },
                    { value: '200', label: '200' },
                    { value: '500', label: '500' },
                    { value: '1000', label: '1000' }
                  ]}
                />
              </div>
              )}
              <Button variant="secondary" onClick={loadLogs} disabled={logsState.loading} style={{ minWidth: '100px' }}>
                {logsState.loading ? 'Chargement...' : logsState.service === 'performance-api' ? 'Rafraîchir' : 'Charger'}
              </Button>
              {logsState.service === 'performance-api' && (
                <Button variant="secondary" size="small" onClick={() => { clearPerformanceLog(); loadLogs(); }} style={{ minWidth: '100px' }}>
                  Vider
                </Button>
              )}
            </div>
            {logsState.error && (
              <div style={{ color: 'var(--color-error, #c0392b)', marginBottom: '0.5rem' }}>
                {logsState.error}
              </div>
            )}
            <div className="logs-display-container">
              <div className="logs-copy-btn-wrapper">
                <button
                  type="button"
                  className="logs-copy-btn"
                  onClick={copyLogsToClipboard}
                  disabled={!logsState.content || logsState.loading}
                  aria-label="Copier les logs"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              <pre
                style={{
                  background: 'var(--color-bg-secondary, #1e1e1e)',
                  color: 'var(--color-text-secondary, #d4d4d4)',
                  padding: '1rem',
                  borderRadius: 4,
                  overflow: 'auto',
                  maxHeight: '60vh',
                  fontSize: '12px',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {logsState.content || (logsState.loading ? 'Chargement...' : 'Sélectionnez un service et cliquez sur Charger.')}
              </pre>
            </div>
          </div>
        </div>
      )
    }
  ], [
    sessionTimeoutInput,
    notificationMinLevel,
    fiscalYearEndDay,
    fiscalYearEndMonth,
    taxRate,
    referenceCurrency,
    currencies,
    handleSessionTimeoutChange,
    handleFiscalYearEndDayChange,
    handleFiscalYearEndMonthChange,
    handleTaxRateChange,
    getMaxDayForMonth,
    saveParameter,
    isConnected,
    loading,
    handleConnect,
    handleDisconnect,
    claudeApiKey,
    grokApiKey,
    chatgptApiKey,
    geminiApiKey,
    yahooFinanceApiKey,
    financialModelingPrepApiKey,
    alphaVantageApiKey,
    ibkrApiKey,
    profile,
    logout,
    userAvatar,
    savingAvatar,
    showAvatarModal,
    sidebarDefaultOpen,
    darkTheme,
    accentColor,
    sidebarLogo,
    organizations,
    subscriptions,
    categories,
    loadingOrgs,
    loadingSubscriptions,
    loadingCategories,
    showCreateForm,
    editingOrg,
    deletingOrg,
    togglingOrg,
    showSubscriptionForm,
    editingSubscription,
    deletingSubscription,
    togglingSubscription,
    showCategoryForm,
    editingCategory,
    deletingCategory,
    togglingCategory,
    categoryFormData,
    categoryFormErrors,
    handleOpenCategoryForm,
    handleEditCategory,
    handleDeleteCategory,
    handleToggleCategoryStatus,
    confirmDeleteCategory,
    confirmToggleCategoryStatus,
    handleCloseCategoryForm,
    handleCreateCategory,
    handleUpdateCategory,
    currentOrgId,
    organizationOptions,
    handleOrganizationChange,
    members,
    loadingMembers,
    showMemberForm,
    editingMember,
    memberFormData,
    memberFormErrors,
    handleOpenMemberForm,
    handleCreateMember,
    handleUpdateMember,
    handleDeleteMember,
    handleCancelMember,
    handleCloseMemberModal,
    handleMemberFormChange,
    handleEditingMemberChange,
    handleMemberTabToggle,
    availableTabLabels,
    sortedMembers,
    sortedOrganizations,
    sortedSubscriptions,
    handleSort,
    renderSortableHeader,
    getSubscriptionLabel,
    formData,
    formErrors,
    handleFormChange,
    handleNameChange,
    handleCloseModal,
    handleCancel,
    handleCreateOrganization,
    handleUpdateOrganization,
    handleEditOrganization,
    handleDeleteOrganization,
    confirmDeleteOrganization,
    handleToggleOrganizationStatus,
    confirmToggleOrganizationStatus,
    handleCloseSubscriptionModal,
    handleCancelSubscription,
    handleCreateSubscription,
    handleUpdateSubscription,
    handleEditSubscription,
    handleDeleteSubscription,
    confirmDeleteSubscription,
    handleToggleSubscriptionStatus,
    confirmToggleSubscriptionStatus,
    backendStatus,
    checkBackendStatus,
    restartingBackend,
    restartBackend,
    ibGatewayStatus,
    checkIbGatewayStatus,
    keepAliveStatus,
    dailyValuationStatus,
    checkDailyValuationStatus,
    logsState,
    setLogsState,
    loadLogs,
    perfLogRefresh,
    copyLogsToClipboard
  ]);

  // Filtrer les onglets selon les permissions (allowed_tabs + rôle)
  const tabs = useMemo(() => {
    // 1) Filtrer par allowed_tabs (BPM tabs - niveau onglet)
    let filtered = allTabs.filter(tab => canAccessParametresTab(tab.tabId));
    // Toujours inclure Profil (compte-settings) pour permettre la déconnexion
    const hasProfil = filtered.some(t => t.tabId === 'compte-settings');
    if (!hasProfil) {
      const profilTab = allTabs.find(t => t.tabId === 'compte-settings');
      if (profilTab) filtered = [profilTab, ...filtered];
    }
    
    // 2) Filtrer par rôle (admin vs utilisateur normal)
    const isOwner = !!profile?.is_owner;
    const isSuperAdmin = !!profile?.is_superadmin;
    const isRoleAdmin = profile?.role === 'admin' || profile?.role === 'owner';
    const isAdmin = isRoleAdmin || isOwner || isSuperAdmin;
    const finalIsSuperAdmin = isSuperAdmin || (profile?.email === 'remicabrit@gmail.com' && profile?.is_superadmin !== false);
    
    if (finalIsSuperAdmin) return filtered;
    
    if (isAdmin) {
      return filtered.filter(tab => tab.label !== 'Administration' && tab.label !== 'Statuts' && tab.label !== 'Logs');
    }
    
    return filtered.filter(tab => tab.label !== 'Administration' && tab.label !== 'Paramètres généraux' && tab.label !== 'Utilisateurs' && tab.label !== 'Statuts' && tab.label !== 'Logs');
  }, [profile?.role, profile?.is_owner, profile?.is_superadmin, profile?.email, allTabs, canAccessParametresTab]);
  
  // Réajuster activeTab si l'onglet actuel n'est plus accessible (filtré par allowed_tabs)
  useEffect(() => {
    if (activeTab >= tabs.length && tabs.length > 0) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  // Charger l'avatar à la demande quand l'onglet Profil est actif (exclu de la liste pour éviter ralentissement ~10s)
  useEffect(() => {
    const currentTab = tabs[activeTab];
    if (currentTab?.tabId !== 'compte-settings' || !profile?.id || userAvatar) return; // Ne pas recharger si déjà présent
    const userId = profile.id || profile.user_id;
    if (!userId) return;
    let cancelled = false;
    settingsAPI.get('user_avatar', null, userId)
      .then((res) => {
        if (!cancelled && res.data?.param_value) setUserAvatar(res.data.param_value);
      })
      .catch(() => { /* 404 normal si pas d'avatar */ });
    return () => { cancelled = true; };
  }, [activeTab, tabs, profile?.id, profile?.user_id, userAvatar]);

  // PHASE 1: Trouver l'index de l'onglet "Statuts" dans la liste filtrée pour le polling
  const statutsTabIndex = useMemo(() => {
    return tabs.findIndex(tab => tab.label === 'Statuts');
  }, [tabs]);
  
  // Trouver l'index de l'onglet "Valeurs par défaut" dans la liste filtrée (comme pour Statuts)
  const valeursParDefautTabIndex = useMemo(() => {
    return tabs.findIndex(tab => tab.label === 'Valeurs par défaut');
  }, [tabs]);

  // Charger les catégories quand l'onglet "Valeurs par défaut" est actif (comme pour sociétés/abonnements)
  useEffect(() => {
    const isAdmin = profile?.email?.toLowerCase() === 'remicabrit@gmail.com';
    if (!isAdmin) {
      return;
    }
    
    // Vérifier seulement si l'onglet "Valeurs par défaut" existe et est actif
    if (valeursParDefautTabIndex === -1 || activeTab !== valeursParDefautTabIndex) {
      return;
    }
    
    // Charger les catégories quand l'onglet devient actif
    // Ne pas vérifier catsLoadedRef pour forcer le rechargement à chaque fois qu'on ouvre l'onglet
    if (!loadingCategories) {
      setTimeout(async () => {
        await loadCategories();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, valeursParDefautTabIndex, profile?.email]);
  
  // PHASE 1: Polling optimisé - vérifier seulement quand l'onglet "Statuts" est actif
  useEffect(() => {
    // Vérifier seulement si l'onglet "Statuts" existe et est actif
    if (statutsTabIndex === -1 || activeTab !== statutsTabIndex) {
      return;
    }
    
    // Vérifier immédiatement quand l'onglet devient actif
    checkBackendStatus();
    checkIbGatewayStatus();
    checkDailyValuationStatus();
    
    // Vérifier toutes les 10 secondes seulement si l'onglet reste actif
    const interval = setInterval(() => {
      if (activeTab === statutsTabIndex) {
        checkBackendStatus();
        checkIbGatewayStatus();
        checkDailyValuationStatus();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [activeTab, statutsTabIndex, checkBackendStatus, checkIbGatewayStatus, checkDailyValuationStatus]);

  return (
    <div className="parametres-page">
      <Tabs 
        tabs={tabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />
      
      {/* Modals dédiées à l'administration (rendues en dehors des onglets pour rester visibles) */}
      {showCreateForm && (
          <div
            className="bpm-modal-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
            }}
            onMouseDown={(e) => {
              // Utiliser onMouseDown au lieu de onClick pour éviter les conflits
              if (e.target === e.currentTarget) {
                e.preventDefault();
              }
            }}
            onClick={(e) => {
              // Vérifier que le clic est bien sur le backdrop et pas sur un élément enfant
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
                handleCloseModal();
              }
            }}
          >
            <div
              className="bpm-modal bpm-modal-medium"
              style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                maxHeight: '90vh',
                maxWidth: '600px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="bpm-modal-header">
                <h3 className="bpm-modal-title">
                  {editingOrg ? 'Modifier la société' : 'Ajouter une société'}
                </h3>
                <button
                  type="button"
                  className="bpm-modal-close"
                  aria-label="Fermer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCloseModal();
                  }}
                >
                  ×
                </button>
              </div>
              <div className="bpm-modal-content">
                <form onSubmit={editingOrg ? handleUpdateOrganization : handleCreateOrganization} className="create-org-form" noValidate>
                  {formErrors.general && (
                    <Message variant="error" style={{ marginBottom: '1rem' }}>
                      {formErrors.general}
                    </Message>
                  )}
                  <div className="form-section">
                    <h4>Informations de la société</h4>
                    <div className="form-group">
                      <label>Nom de la société *</label>
                      <input
                        type="text"
                        value={formData.organization.name}
                        onChange={(e) => {
                          handleNameChange(e.target.value);
                          if (formErrors['organization.name']) {
                            setFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors['organization.name'];
                              return newErrors;
                            });
                          }
                        }}
                        required
                        placeholder="Ex: Acme Corporation"
                      />
                      {formErrors['organization.name'] ? (
                        <div style={{ marginTop: '0.5rem' }}>
                          <Message type="error">{formErrors['organization.name']}</Message>
                        </div>
                      ) : null}
                    </div>
                    <div className="form-group">
                      <label>Slug *</label>
                      <input
                        type="text"
                        value={formData.organization.slug}
                        onChange={(e) => handleFormChange('organization', 'slug', e.target.value)}
                        required
                        placeholder="acme-corporation"
                        pattern="[a-z0-9\-]+"
                        title="Uniquement des lettres minuscules, chiffres et tirets"
                      />
                      <small>Identifiant unique pour la société (URL-friendly)</small>
                    </div>
                    <div className="form-group">
                      <label>SIREN *</label>
                      <input
                        type="text"
                        value={formData.organization.siren || ''}
                        onChange={(e) => {
                          handleFormChange('organization', 'siren', e.target.value);
                          // Effacer l'erreur quand l'utilisateur tape
                          if (formErrors['organization.siren']) {
                            setFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors['organization.siren'];
                              return newErrors;
                            });
                          }
                        }}
                        placeholder="123456789"
                        pattern="[0-9]{9}"
                        title="9 chiffres"
                        maxLength={9}
                        required
                      />
                      <small>Numéro SIREN (9 chiffres)</small>
                      {formErrors['organization.siren'] ? (
                        <div style={{ marginTop: '0.5rem' }}>
                          <Message type="error">{formErrors['organization.siren']}</Message>
                        </div>
                      ) : null}
                    </div>
                    <div className="form-group">
                      <label>Adresse (ligne 1) *</label>
                      <input
                        type="text"
                        value={formData.organization.address_line1 || ''}
                        onChange={(e) => handleFormChange('organization', 'address_line1', e.target.value)}
                        placeholder="123 rue de la République"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Adresse (ligne 2)</label>
                      <input
                        type="text"
                        value={formData.organization.address_line2 || ''}
                        onChange={(e) => handleFormChange('organization', 'address_line2', e.target.value)}
                        placeholder="Bâtiment A, étage 2"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Code postal *</label>
                        <input
                          type="text"
                          value={formData.organization.postal_code || ''}
                          onChange={(e) => handleFormChange('organization', 'postal_code', e.target.value)}
                          placeholder="75001"
                          maxLength={10}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Ville *</label>
                        <input
                          type="text"
                          value={formData.organization.city || ''}
                          onChange={(e) => handleFormChange('organization', 'city', e.target.value)}
                          placeholder="Paris"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Pays *</label>
                      <input
                        type="text"
                        value={formData.organization.country || ''}
                        onChange={(e) => handleFormChange('organization', 'country', e.target.value)}
                        placeholder="France"
                        required
                      />
                    </div>
                  </div>

                  {!editingOrg && (
                    <div className="form-section">
                      <h4>Super administrateur</h4>
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          value={formData.super_admin.email}
                          onChange={(e) => handleFormChange('super_admin', 'email', e.target.value)}
                          required
                          placeholder="admin@example.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>Mot de passe *</label>
                        <input
                          type="password"
                          value={formData.super_admin.password}
                          onChange={(e) => handleFormChange('super_admin', 'password', e.target.value)}
                          required
                          minLength={8}
                          maxLength={72}
                          placeholder="Entre 8 et 72 caractères"
                        />
                        <small style={{ color: '#666', fontSize: '0.875rem' }}>Le mot de passe doit contenir entre 8 et 72 caractères</small>
                        {formErrors['super_admin.password'] && (
                          <Message variant="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                            {formErrors['super_admin.password']}
                          </Message>
                        )}
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Prénom *</label>
                          <input
                            type="text"
                            value={formData.super_admin.first_name}
                            onChange={(e) => handleFormChange('super_admin', 'first_name', e.target.value)}
                            placeholder="Jean"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Nom *</label>
                          <input
                            type="text"
                            value={formData.super_admin.last_name}
                            onChange={(e) => handleFormChange('super_admin', 'last_name', e.target.value)}
                            placeholder="Dupont"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-section">
                    <h4>Abonnement</h4>
                    <div className="form-group">
                      <Selectbox
                        label="Niveau d'abonnement *"
                        options={subscriptions
                          .filter(sub => sub.is_active)
                          .map(sub => ({
                            value: sub.code,
                            label: `${sub.name} (${parseFloat(sub.price).toFixed(2)} ${sub.currency})`
                          }))}
                        value={formData.organization.subscription_tier}
                        onChange={(value) => handleFormChange('organization', 'subscription_tier', value)}
                        required
                        placeholder={null}
                      />
                    </div>
                    {editingOrg && (
                      <div className="form-group toggle-group">
                        <label htmlFor="org-active" style={{ margin: 0, lineHeight: '24px' }}>
                          Société active
                        </label>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            id="org-active"
                            name="isActive"
                            checked={!!formData.organization.is_active}
                            onChange={(e) => handleFormChange('organization', 'is_active', e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    {editingOrg && (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowCreateForm(false);
                            setEditingOrg(null);
                            handleDeleteOrganization(editingOrg);
                          }}
                          disabled={loading}
                        >
                          Supprimer
                        </Button>
                      </>
                    )}
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? (editingOrg ? 'Mise à jour...' : 'Création...') : (editingOrg ? 'Mettre à jour' : 'Ajouter')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
      )}

      {/* Modal pour créer/éditer un abonnement (rendue en dehors des onglets pour rester visible) */}
      {showSubscriptionForm && (
        <div
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
            }
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              handleCloseSubscriptionModal();
            }
          }}
        >
          <div
            className="bpm-modal bpm-modal-medium"
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxHeight: '90vh',
              maxWidth: '600px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">
                {editingSubscription ? 'Modifier l\'abonnement' : 'Ajouter un abonnement'}
              </h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSubscriptionForm(false);
                  setEditingSubscription(null);
                  setSubscriptionFormData({
                    code: '',
                    name: '',
                    description: '',
                    price: 0,
                    currency: 'EUR',
                    is_active: true
                  });
                }}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <form onSubmit={editingSubscription ? handleUpdateSubscription : handleCreateSubscription} className="create-org-form" noValidate>
                <div className="form-section">
                  <h4>Informations de l'abonnement</h4>
                  <div className="form-group">
                    <label>Code *</label>
                    <input
                      type="text"
                      value={subscriptionFormData.code}
                      onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, code: e.target.value })}
                      required
                      placeholder="free, pro, enterprise"
                      disabled={!!editingSubscription}
                      pattern="[a-z0-9\-]+"
                      title="Uniquement des lettres minuscules, chiffres et tirets"
                    />
                    <small>Identifiant unique (ne peut pas être modifié)</small>
                  </div>
                  <div className="form-group">
                    <label>Nom *</label>
                    <input
                      type="text"
                      value={subscriptionFormData.name}
                      onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, name: e.target.value })}
                      required
                      placeholder="Gratuit, Professionnel, Entreprise"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={subscriptionFormData.description}
                      onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, description: e.target.value })}
                      placeholder="Description de l'abonnement"
                      rows={3}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid rgb(237, 237, 237)', fontFamily: 'inherit', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Prix *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={subscriptionFormData.price}
                        onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, price: parseFloat(e.target.value) || 0 })}
                        required
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <label>Devise *</label>
                      <input
                        type="text"
                        value={subscriptionFormData.currency}
                        onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, currency: e.target.value })}
                        required
                        placeholder="EUR"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  {editingSubscription && (
                    <div className="form-group toggle-group">
                      <label htmlFor="sub-active" style={{ margin: 0, lineHeight: '24px' }}>
                        Abonnement actif
                      </label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          id="sub-active"
                          name="isActive"
                          checked={!!subscriptionFormData.is_active}
                          onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, is_active: e.target.checked })}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  {editingSubscription && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowSubscriptionForm(false);
                          setEditingSubscription(null);
                          handleDeleteSubscription(editingSubscription);
                        }}
                        disabled={loading}
                      >
                        Supprimer
                      </Button>
                    </>
                  )}
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? (editingSubscription ? 'Mise à jour...' : 'Création...') : (editingSubscription ? 'Mettre à jour' : 'Ajouter')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal dédiée aux utilisateurs (rendue en dehors des onglets pour rester visible) */}
      {showMemberForm && (
        <div
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            overflow: 'visible', // Permettre aux dropdowns de dépasser
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
            }
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              handleCloseMemberModal();
            }
          }}
        >
          <div
            className="bpm-modal bpm-modal-medium"
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxHeight: '90vh',
              maxWidth: '600px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible', // Permettre aux dropdowns de dépasser
              position: 'relative',
              zIndex: 100001,
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">
                {editingMember ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}
              </h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCloseMemberModal();
                }}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <form onSubmit={editingMember ? handleUpdateMember : handleCreateMember} className="create-org-form" noValidate>
                {memberFormErrors.general && (
                  <Message variant="error" style={{ marginBottom: '1rem' }}>
                    {memberFormErrors.general}
                  </Message>
                )}
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={editingMember ? editingMember.user.email : memberFormData.email}
                    onChange={(e) => {
                      if (editingMember) {
                        handleEditingMemberChange('email', e.target.value, true);
                      } else {
                        handleMemberFormChange('email', e.target.value);
                      }
                    }}
                    required={!editingMember}
                    disabled={!!editingMember}
                    placeholder="user@example.com"
                  />
                  {memberFormErrors.email && (
                    <Message variant="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                      {memberFormErrors.email}
                    </Message>
                  )}
                </div>
                {!editingMember && (
                  <div className="form-group">
                    <label>Mot de passe *</label>
                    <input
                      type="password"
                      value={memberFormData.password || ''}
                      onChange={(e) => handleMemberFormChange('password', e.target.value)}
                      required
                      minLength={8}
                      maxLength={72}
                      placeholder="Entre 8 et 72 caractères"
                    />
                    <small style={{ color: '#666', fontSize: '0.875rem' }}>Le mot de passe doit contenir entre 8 et 72 caractères</small>
                    {memberFormErrors.password && (
                      <Message variant="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                        {memberFormErrors.password}
                      </Message>
                    )}
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom *</label>
                    <input
                      type="text"
                      value={editingMember ? editingMember.user.first_name || '' : memberFormData.first_name}
                      onChange={(e) => {
                        if (editingMember) {
                          handleEditingMemberChange('first_name', e.target.value, true);
                        } else {
                          handleMemberFormChange('first_name', e.target.value);
                        }
                      }}
                      required
                      placeholder="Jean"
                    />
                    {memberFormErrors.first_name && (
                      <Message variant="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                        {memberFormErrors.first_name}
                      </Message>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Nom *</label>
                    <input
                      type="text"
                      value={editingMember ? editingMember.user.last_name || '' : memberFormData.last_name}
                      onChange={(e) => {
                        if (editingMember) {
                          handleEditingMemberChange('last_name', e.target.value, true);
                        } else {
                          handleMemberFormChange('last_name', e.target.value);
                        }
                      }}
                      required
                      placeholder="Dupont"
                    />
                    {memberFormErrors.last_name && (
                      <Message variant="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                        {memberFormErrors.last_name}
                      </Message>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={editingMember ? (editingMember.user.function || '') : memberFormData.function}
                    onChange={(e) => {
                      if (editingMember) {
                        handleEditingMemberChange('function', e.target.value, true);
                      } else {
                        handleMemberFormChange('function', e.target.value);
                      }
                    }}
                    placeholder="Ex: Directeur financier, Analyste, etc."
                  />
                </div>
                {!editingMember && (
                <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                  <label>Avatar</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      overflow: 'hidden',
                      backgroundColor: 'rgb(226, 226, 228)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {(() => {
                        const avatarUrl = memberFormData.avatar || null;
                        const name = `${memberFormData.first_name || ''} ${memberFormData.last_name || ''}`.trim();
                        const initials = name 
                          ? name.split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)
                          : 'U';
                        
                        if (avatarUrl) {
                          return (
                            <>
                              <img 
                                src={avatarUrl.startsWith('data:') ? avatarUrl : `${avatarUrl}?t=${Date.now()}`}
                                alt={name || 'User'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const initialsDiv = e.target.nextSibling;
                                  if (initialsDiv) {
                                    initialsDiv.style.display = 'flex';
                                  }
                                }}
                              />
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '24px',
                                fontWeight: '600',
                                color: 'rgb(29, 29, 31)'
                              }}>
                                {initials}
                              </div>
                            </>
                          );
                        }
                        return (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            fontWeight: '600',
                            color: 'rgb(29, 29, 31)'
                          }}>
                            {initials}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              const base64 = event.target?.result;
                              if (base64) {
                                handleMemberFormChange('avatar', base64);
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.875rem',
                          border: '1px solid rgb(237, 237, 237)',
                          borderRadius: '8px',
                          fontFamily: 'inherit'
                        }}
                      />
                      <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                        Sélectionnez une image pour l'avatar (JPG, PNG, etc.)
                      </small>
                    </div>
                  </div>
                </div>
                )}
                <div className="form-group">
                  <Selectbox
                    label="Type d'accès *"
                    options={[
                      { value: 'user', label: 'Utilisateur' },
                      { value: 'admin', label: 'Administrateur' }
                    ]}
                    value={editingMember ? (editingMember.access_type || 'user') : memberFormData.access_type}
                    onChange={(value) => {
                      if (editingMember) {
                        handleEditingMemberChange('access_type', value);
                      } else {
                        handleMemberFormChange('access_type', value);
                      }
                    }}
                    required
                    placeholder={null}
                  />
                </div>
                <div className="form-group">
                  <Selectbox
                    label="Rôle *"
                    options={[
                      { value: 'viewer', label: 'Viewer (Lecture seule)' },
                      { value: 'editor', label: 'Editor (Édition)' },
                      { value: 'admin', label: 'Admin (Administration)' },
                      { value: 'owner', label: 'Propriétaire (un seul par société)' }
                    ]}
                    value={editingMember 
                      ? (editingMember.is_owner ? 'owner' : (editingMember.role || 'viewer'))
                      : memberFormData.role}
                    onChange={(value) => {
                      const isOwner = value === 'owner';
                      if (editingMember) {
                        handleEditingMemberChange('role', isOwner ? 'owner' : value);
                        handleEditingMemberChange('is_owner', isOwner);
                      } else {
                        handleMemberFormChange('role', isOwner ? 'owner' : value);
                        handleMemberFormChange('is_owner', isOwner);
                      }
                    }}
                    required
                    placeholder={null}
                  />
                </div>
                <div className="form-group">
                  <label>Accès autorisés *</label>
                  <small style={{ color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                    Menus sidebar et onglets à l&apos;intérieur de chaque page
                  </small>
                  <div style={{ 
                    marginTop: '0.25rem', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '0.25rem', 
                    maxHeight: '240px', 
                    width: '100%',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    padding: '0.5rem', 
                    border: '1px solid rgb(237, 237, 237)', 
                    borderRadius: '8px',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}>
                    {(() => {
                      const selectedTabs = getSelectedTabs();
                      // Grouper les onglets par parent (niveau 1)
                      const parents = tabsHierarchy.filter(tab => tab.level === 1);
                      
                      return parents.map((parent) => {
                        // Récupérer les enfants : filtrer par tab.parent (source fiable) puis trier selon sidebarOrder
                        const childrenByParent = tabsHierarchy.filter(tab => tab.parent === parent.id);
                        const childOrder = sidebarOrder[parent.id];
                        const children = childOrder && childOrder.length > 0
                          ? childOrder.map(id => childrenByParent.find(c => c.id === id)).filter(Boolean)
                              .concat(childrenByParent.filter(c => !childOrder.includes(c.id)))
                          : childrenByParent.sort((a, b) => a.label.localeCompare(b.label));
                        
                        const hasChildren = children.length > 0;
                        const parentIsChecked = hasChildren
                          ? isParentChecked(parent.id)
                          : selectedTabs.includes(parent.id);
                        const parentIsIndeterminate = hasChildren
                          ? isParentIndeterminate(parent.id)
                          : false;
                        
                        return (
                          <React.Fragment key={parent.id}>
                            {/* Parent (niveau 1) */}
                            <TabCheckbox
                              tab={parent}
                              isChecked={parentIsChecked}
                              isIndeterminate={parentIsIndeterminate}
                              isParent={hasChildren}
                              isChild={false}
                              isParentChecked={parentIsChecked}
                              onParentChange={handleParentChange}
                              onChildChange={handleChildChange}
                              onSimpleToggle={handleMemberTabToggle}
                            />
                            {/* Enfants (niveau 2) */}
                            {hasChildren && children.map((child) => {
                              if (!child) return null; // Sécurité
                              const isChildChecked = selectedTabs.includes(child.id);
                              return (
                                <TabCheckbox
                                  key={child.id}
                                  tab={child}
                                  isChecked={isChildChecked}
                                  isIndeterminate={false}
                                  isParent={false}
                                  isChild={true}
                                  isParentChecked={false}
                                  onParentChange={handleParentChange}
                                  onChildChange={handleChildChange}
                                  onSimpleToggle={handleMemberTabToggle}
                                />
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>
                  <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                    Sélectionnez les menus et sous-onglets accessibles (coché = visible dans la sidebar et dans la page)
                  </small>
                  {memberFormErrors.allowed_tabs && (
                    <Message variant="error" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                      {memberFormErrors.allowed_tabs}
                    </Message>
                  )}
                </div>
                {editingMember && (
                  <div className="form-group toggle-group">
                    <label htmlFor="utilisateur-actif" style={{ margin: 0, lineHeight: '24px' }}>
                      Utilisateur actif
                    </label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        id="utilisateur-actif"
                        name="isActive"
                        checked={editingMember.user.is_active}
                        onChange={(e) => {
                          handleEditingMemberChange('is_active', e.target.checked, true);
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                )}
                <div className="form-actions">
                  {editingMemberRow && !editingMemberRow.is_owner && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeleteMemberFromModal(editingMember);
                        }}
                        disabled={loading}
                      >
                        Supprimer
                      </Button>
                    </>
                  )}
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? (editingMember ? 'Mise à jour...' : 'Création...') : (editingMember ? 'Mettre à jour' : 'Ajouter')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation suppression utilisateur */}
      {deletingMember && (
        <div
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              setDeletingMember(null);
            }
          }}
        >
          <div className="bpm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">Confirmer la suppression</h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={() => setDeletingMember(null)}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <p>
                Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{deletingMember.user?.email}</strong> ?
              </p>
              <div className="form-actions" style={{ marginTop: '1rem' }}>
                <Button variant="secondary" type="button" onClick={confirmDeleteMember} disabled={loading}>
                  {loading ? 'Suppression...' : 'Supprimer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation désactivation/activation utilisateur */}
      {togglingMember && (
        <div
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              setTogglingMember(null);
            }
          }}
        >
          <div className="bpm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">
                {togglingMember?.user?.is_active ? 'Confirmer la désactivation' : "Confirmer l'activation"}
              </h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={() => setTogglingMember(null)}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <p>
                Êtes-vous sûr de vouloir {togglingMember?.user?.is_active ? 'désactiver' : 'activer'} l'utilisateur <strong>{togglingMember?.user?.email}</strong> ?
              </p>
              <div className="form-actions" style={{ marginTop: '1rem' }}>
                <Button variant="secondary" type="button" onClick={confirmToggleMemberStatus} disabled={loading}>
                  {loading ? (togglingMember?.user?.is_active ? 'Désactivation...' : 'Activation...') : (togglingMember?.user?.is_active ? 'Désactiver' : 'Activer')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals pour les catégories */}
      {showCategoryForm && (
        <div 
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              handleCloseCategoryForm();
            }
          }}
        >
          <div className="bpm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">
                {editingCategory ? 'Modifier la catégorie' : 'Ajouter une catégorie'}
              </h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCloseCategoryForm();
                }}
              >
                ×
              </button>
    </div>
            <div className="bpm-modal-content">
              {categoryFormErrors.general && (
                <Message type="error" style={{ marginBottom: '1rem' }}>
                  {categoryFormErrors.general}
                </Message>
              )}
              <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory} className="create-org-form" noValidate>
                <div className="form-group">
                  <label htmlFor="category-name">Nom de la catégorie *</label>
                  <input
                    id="category-name"
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    required
                    placeholder="Ex: Asie"
                  />
                  {categoryFormErrors.name && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <Message type="error">{categoryFormErrors.name}</Message>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="category-type">Type *</label>
                  <Selectbox
                    id="category-type"
                    value={categoryFormData.is_fragile ? 'fragile' : 'anti-fragile'}
                    onChange={(value) => setCategoryFormData({ ...categoryFormData, is_fragile: value === 'fragile' })}
                    options={[
                      { value: 'fragile', label: 'Fragile' },
                      { value: 'anti-fragile', label: 'Anti-Fragile' }
                    ]}
                  />
                </div>
                
                <div className="form-group">
                  <NumberInput
                    label="Pourcentage cible (%)"
                    min={0}
                    max={100}
                    step={0.01}
                    value={categoryFormData.target_percentage || ''}
                    onChange={(val) => setCategoryFormData({ ...categoryFormData, target_percentage: val || null })}
                    placeholder="Ex: 25.5"
                  />
                </div>
                
                <div className="form-group">
                  <ColorPicker
                    label="Couleur"
                    value={categoryFormData.color || '#000000'}
                    onChange={(color) => setCategoryFormData({ ...categoryFormData, color })}
                  />
                </div>
                
                <div className="form-actions">
                  {editingCategory && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          handleCloseCategoryForm();
                          setTogglingCategory(editingCategory);
                        }}
                        disabled={loadingCategories}
                      >
                        {loadingCategories ? '...' : (editingCategory.is_active ? 'Désactiver' : 'Activer')}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          handleCloseCategoryForm();
                          setDeletingCategory(editingCategory);
                        }}
                        disabled={loadingCategories}
                      >
                        {loadingCategories ? '...' : 'Supprimer'}
                      </Button>
                    </>
                  )}
                  <Button
                    type="submit"
                    variant="secondary"
                    disabled={loadingCategories}
                  >
                    {loadingCategories ? 'Enregistrement...' : (editingCategory ? 'Modifier' : 'Ajouter')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmation de suppression de catégorie */}
      {deletingCategory && (
        <div 
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
        >
          <div className="bpm-modal bpm-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">Confirmer la suppression</h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeletingCategory(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <p>Êtes-vous sûr de vouloir supprimer la catégorie <strong>{deletingCategory.name}</strong> ?</p>
              <p style={{ color: 'var(--accent-color)', fontSize: '0.875rem' }}>Cette action est irréversible.</p>
              <div className="form-actions">
                <Button variant="secondary" type="button" onClick={confirmDeleteCategory} disabled={loadingCategories}>
                  {loadingCategories ? 'Suppression...' : 'Supprimer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmation de désactivation/activation de catégorie */}
      {togglingCategory && (
        <div 
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
        >
          <div className="bpm-modal bpm-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">
                {togglingCategory.is_active ? 'Confirmer la désactivation' : 'Confirmer l\'activation'}
              </h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTogglingCategory(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <p>
                Êtes-vous sûr de vouloir {togglingCategory.is_active ? 'désactiver' : 'activer'} la catégorie <strong>{togglingCategory.name}</strong> ?
              </p>
              {togglingCategory.is_active && (
                <p style={{ color: '#d32f2f', fontSize: '0.875rem' }}>
                  La catégorie ne sera plus disponible une fois désactivée.
                </p>
              )}
              <div className="form-actions">
                <Button variant="secondary" type="button" onClick={confirmToggleCategoryStatus} disabled={loadingCategories}>
                  {loadingCategories ? (togglingCategory.is_active ? 'Désactivation...' : 'Activation...') : (togglingCategory.is_active ? 'Désactiver' : 'Activer')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour la sélection de l'avatar */}
      {showAvatarModal && (
        <div
          className="bpm-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAvatarModal(false);
            }
          }}
        >
          <div
            className="bpm-modal bpm-modal-medium"
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxHeight: '90vh',
              maxWidth: '500px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="bpm-modal-header">
              <h3 className="bpm-modal-title">Modifier l'avatar</h3>
              <button
                type="button"
                className="bpm-modal-close"
                aria-label="Fermer"
                onClick={() => setShowAvatarModal(false)}
              >
                ×
              </button>
            </div>
            <div className="bpm-modal-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
                {/* Aperçu de l'avatar */}
                <div style={{ 
                  width: '150px', 
                  height: '150px', 
                  borderRadius: '50%', 
                  overflow: 'hidden',
                  backgroundColor: 'rgb(226, 226, 228)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid rgb(237, 237, 237)'
                }}>
                  {(() => {
                    const avatarUrl = userAvatar || profile?.picture || null;
                    const name = profile?.name || 'User';
                    const initials = name.split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                    
                    if (avatarUrl) {
                      return (
                        <>
                          <img 
                            src={avatarUrl.startsWith('data:') ? avatarUrl : `${avatarUrl}?t=${Date.now()}`}
                            alt={name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const initialsDiv = e.target.nextSibling;
                              if (initialsDiv) {
                                initialsDiv.style.display = 'flex';
                              }
                            }}
                          />
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '48px',
                            fontWeight: '600',
                            color: 'rgb(29, 29, 31)'
                          }}>
                            {initials}
                          </div>
                        </>
                      );
                    }
                    return (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '48px',
                        fontWeight: '600',
                        color: 'rgb(29, 29, 31)'
                      }}>
                        {initials}
                      </div>
                    );
                  })()}
                </div>

                {/* Input file */}
                <div style={{ width: '100%' }}>
                  <input
                    type="file"
                    accept="image/*"
                    id="avatar-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Vérifier la taille du fichier (limite à 2MB avant conversion base64)
                        const maxFileSize = 2 * 1024 * 1024; // 2MB
                        if (file.size > maxFileSize) {
                          showToast(`L'image est trop volumineuse. Taille maximale: ${maxFileSize / 1024 / 1024} MB`, 'error', 3000, 'Erreur d\'upload', 'Paramètres', getPageIcon('Paramètres'));
                          // Réinitialiser l'input
                          e.target.value = '';
                          return;
                        }
                        
                        // Vérifier le type de fichier
                        if (!file.type.startsWith('image/')) {
                          showToast('Veuillez sélectionner un fichier image valide.', 'error', 3000, 'Erreur d\'upload', 'Paramètres', getPageIcon('Paramètres'));
                          e.target.value = '';
                          return;
                        }
                        
                        const reader = new FileReader();
                        reader.onload = async (event) => {
                          const base64 = event.target?.result;
                          if (base64 && typeof base64 === 'string') {
                            // Vérifier la taille après conversion base64 (base64 est ~33% plus grand que l'original)
                            const maxBase64Size = 3 * 1024 * 1024; // 3MB pour base64
                            if (base64.length > maxBase64Size) {
                              showToast('L\'image convertie est trop volumineuse. Veuillez choisir une image plus petite.', 'error', 3000, 'Erreur d\'upload', 'Paramètres', getPageIcon('Paramètres'));
                              e.target.value = '';
                              return;
                            }
                            
                            // Mettre à jour l'aperçu immédiatement
                            setUserAvatar(base64);
                            
                            // Sauvegarder seulement si l'utilisateur est identifié
                            const userId = profile?.id || profile?.user_id;
                            if (userId) {
                              setSavingAvatar(true);
                              try {
                                await saveParameter('user_avatar', base64, true);
                                if (refreshAvatar) {
                                  await refreshAvatar(true);
                                }
                                // Fermer la modal seulement après sauvegarde réussie
                                setShowAvatarModal(false);
                              } catch (error) {
                                console.error('❌ [Avatar] Erreur lors de la sauvegarde de l\'avatar:', error);
                                const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erreur inconnue';
                                const errorStatus = error.response?.status;
                                console.error('❌ [Avatar] Détails de l\'erreur:', {
                                  message: errorMessage,
                                  status: errorStatus,
                                  statusText: error.response?.statusText,
                                  data: error.response?.data,
                                  code: error.code,
                                  base64Length: base64?.length || 0
                                });
                                
                                // Message d'erreur plus spécifique selon le type d'erreur
                                let userMessage = `❌ Erreur lors de la sauvegarde de l'avatar`;
                                if (errorStatus === 400) {
                                  userMessage = `❌ ${errorMessage}`;
                                } else if (errorStatus === 413) {
                                  userMessage = '❌ L\'image est trop volumineuse. Veuillez choisir une image plus petite.';
                                } else if (errorStatus === 500) {
                                  userMessage = `❌ Erreur serveur: ${errorMessage}`;
                                } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                                  userMessage = '❌ Le serveur met trop de temps à répondre. L\'image est peut-être trop volumineuse.';
                                } else if (errorMessage) {
                                  userMessage = `❌ ${errorMessage}`;
                                }
                                
                                showToast(userMessage, 'error');
                                // Ne pas fermer la modal en cas d'erreur pour permettre de réessayer
                              } finally {
                                setSavingAvatar(false);
                              }
                            } else {
                              // Si pas d'utilisateur identifié, afficher un message
                              console.error('❌ [Avatar] Utilisateur non identifié. profile:', profile);
                              showToast('Utilisateur non identifié. Veuillez vous reconnecter.', 'error', 3000, 'Erreur d\'authentification', 'Paramètres', getPageIcon('Paramètres'));
                            }
                          }
                        };
                        reader.onerror = () => {
                          console.error('❌ [Avatar] Erreur lors de la lecture du fichier');
                          showToast('Erreur lors de la lecture du fichier. Veuillez réessayer avec un autre fichier.', 'error', 3000, 'Erreur d\'upload', 'Paramètres', getPageIcon('Paramètres'));
                          e.target.value = '';
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    disabled={savingAvatar}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const fileInput = document.getElementById('avatar-file-input');
                      if (fileInput) {
                        fileInput.click();
                      }
                    }}
                    disabled={savingAvatar}
                    style={{ width: '100%' }}
                  >
                    {savingAvatar ? 'Sauvegarde en cours...' : 'Choisir un fichier'}
                  </Button>
                  <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.5rem', textAlign: 'center' }}>
                    Sélectionnez une image pour votre avatar (JPG, PNG, etc.)
                  </small>
                </div>

                {/* Bouton supprimer */}
                {(userAvatar || profile?.picture) && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      const userId = profile?.id || profile?.user_id;
                      if (userId) {
                        // Mise à jour optimiste : mettre à jour l'état immédiatement pour un feedback instantané
                        const previousAvatar = userAvatar;
                        const previousProfilePicture = profile?.picture;
                        setUserAvatar(null);
                        setSavingAvatar(true);
                        
                        // Mettre à jour immédiatement le profil dans AuthContext pour un feedback instantané
                        if (setAvatarToNull) {
                          setAvatarToNull();
                        }
                        try {
                          await saveParameter('user_avatar', '', true);
                          if (refreshAvatar) {
                            await refreshAvatar();
                          }
                          setShowAvatarModal(false);
                        } catch (error) {
                          // En cas d'erreur, restaurer l'état précédent
                          setUserAvatar(previousAvatar);
                          try {
                            const sessionData = JSON.parse(localStorage.getItem('session') || '{}');
                            if (sessionData.profile && previousProfilePicture) {
                              sessionData.profile = { ...sessionData.profile, picture: previousProfilePicture };
                              localStorage.setItem('session', JSON.stringify(sessionData));
                            }
                          } catch (e) {
                            console.warn('⚠️ [Avatar] Erreur lors de la restauration de la session:', e);
                          }
                          console.error('❌ [Avatar] Erreur lors de la suppression de l\'avatar:', error);
                          const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erreur inconnue';
                          showToast(`Erreur lors de la suppression de l'avatar: ${errorMessage}`, 'error', 3000, 'Erreur de suppression', 'Paramètres', getPageIcon('Paramètres'));
                        } finally {
                          setSavingAvatar(false);
                        }
                      } else {
                        console.error('❌ [Avatar] Utilisateur non identifié pour la suppression');
                        showToast('Utilisateur non identifié. Veuillez vous reconnecter.', 'error', 3000, 'Erreur d\'authentification', 'Paramètres', getPageIcon('Paramètres'));
                      }
                    }}
                    disabled={savingAvatar}
                    style={{ width: '100%' }}
                  >
                    Supprimer l'avatar
                  </Button>
                )}

                {/* Bouton fermer */}
                <Button
                  variant="secondary"
                  onClick={() => setShowAvatarModal(false)}
                  disabled={savingAvatar}
                  style={{ width: '100%' }}
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Parametres;

